import { useState, useMemo, useRef, useEffect } from "react";
import React from "react";
import { useAuth } from "./AuthContext";
import AuthScreen from "./AuthScreen";
import { db, messagingPromise } from "./firebase";
import { collection, doc, getDoc, getDocs, setDoc, addDoc, deleteDoc, onSnapshot, query, where } from "firebase/firestore";
import { getToken } from "firebase/messaging";
import { deleteUser, sendEmailVerification } from "firebase/auth";
import { auth } from "./firebase";
import { isNative, registerNativePush, addDeepLinkListener, setupStatusBar, hideSplash } from "./capacitorNative";

/* ── Calendar helpers ──────────────────────────────────────────────────────── */
async function fetchCalendarEvents(token, days=90) {
  const now = new Date();
  const future = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  const url = `https://www.googleapis.com/calendar/v3/calendars/primary/events?`
    + `timeMin=${now.toISOString()}&timeMax=${future.toISOString()}`
    + `&singleEvents=true&orderBy=startTime&maxResults=2500`;
  const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) throw new Error("Calendar fetch failed");
  const data = await resp.json();
  return (data.items || [])
    .filter(e => e.status !== "cancelled" && e.start?.dateTime)
    .map(e => ({ start: e.start.dateTime, end: e.end.dateTime }));
}

function findFreeWindows(myBusy, othersBusy, days = 14) {
  // Returns human-readable free windows shared by everyone.
  // Progressively relaxes the blocking threshold if the calendar is packed,
  // and auto-extends the look-ahead so dense schedules still produce results.
  const allBusy = [...(myBusy || []), ...(othersBusy || [])];
  const now = new Date();
  const windows = [
    { label: "morning",   sh: "9am–12pm",  start: 9,  end: 12 },
    { label: "afternoon", sh: "12pm–5pm",  start: 12, end: 17 },
    { label: "evening",   sh: "5pm–8pm",   start: 17, end: 20 },
  ];

  function scanDays(maxDays, overlapThresholdMs) {
    const slots = [];
    for (let d = 0; d < maxDays; d++) {
      const date = new Date(now);
      date.setDate(date.getDate() + d);
      const dayStr = date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
      for (const w of windows) {
        const wStart = new Date(date); wStart.setHours(w.start, 0, 0, 0);
        const wEnd   = new Date(date); wEnd.setHours(w.end,   0, 0, 0);
        if (wStart <= now) continue;
        const totalOverlapMs = allBusy.reduce((sum, b) => {
          const bStart = new Date(b.start).getTime();
          const bEnd   = new Date(b.end).getTime();
          const overlap = Math.min(bEnd, wEnd.getTime()) - Math.max(bStart, wStart.getTime());
          return sum + Math.max(0, overlap);
        }, 0);
        if (totalOverlapMs < overlapThresholdMs) slots.push(`${dayStr} ${w.label} (${w.sh})`);
      }
    }
    return slots;
  }

  // Pass 1 — strict: block if >45 min overlap, look ahead requested days
  let slots = scanDays(days, 45 * 60 * 1000);
  if (slots.length >= 3) return slots.slice(0, 10);

  // Pass 2 — extend look-ahead to 28 days, same threshold
  slots = scanDays(28, 45 * 60 * 1000);
  if (slots.length >= 3) return slots.slice(0, 10);

  // Pass 3 — busy schedules: relax threshold to 90 min, still 28 days
  slots = scanDays(28, 90 * 60 * 1000);
  if (slots.length >= 3) return slots.slice(0, 10);

  // Pass 4 — very busy: relax to 2 hours, extend to 42 days
  slots = scanDays(42, 120 * 60 * 1000);
  return slots.slice(0, 10);
}

/* ── Bootstrap ─────────────────────────────────────────────────────────────── */
(() => {
  const l = document.createElement("link");
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Serif+Display:ital@0;1&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap";
  document.head.appendChild(l);
  const s = document.createElement("style");
  s.textContent = `
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { height: 100%; height: -webkit-fill-available; }
    body {
      background: #f0f2f8;
      -webkit-font-smoothing: antialiased;
      min-height: 100vh;
      min-height: -webkit-fill-available;
      overscroll-behavior: none;
      -webkit-overflow-scrolling: touch;
    }
    #root { min-height: 100vh; min-height: -webkit-fill-available; }
    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: rgba(37,99,235,.18); border-radius: 4px; }
    input, textarea, select, button { font-family: inherit; }
    input[type=date]::-webkit-calendar-picker-indicator { opacity:.4; cursor:pointer; }
    input[type=number] { -moz-appearance:textfield; }
    input[type=number]::-webkit-inner-spin-button { -webkit-appearance:none; }
    ::placeholder { color: rgba(30,41,59,.3); }

    @keyframes up     { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    @keyframes in     { from{opacity:0} to{opacity:1} }
    @keyframes pop    { from{opacity:0;transform:scale(.93)} to{opacity:1;transform:scale(1)} }
    @keyframes spin   { to{transform:rotate(360deg)} }
    @keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-5px)} }

    .u  { animation: up  .46s cubic-bezier(.16,1,.3,1) both; }
    .i  { animation: in  .3s ease both; }
    .p  { animation: pop .38s cubic-bezier(.16,1,.3,1) both; }
    .d1{animation-delay:.05s} .d2{animation-delay:.10s} .d3{animation-delay:.15s}
    .d4{animation-delay:.20s} .d5{animation-delay:.25s}

    .tap { transition:transform .14s cubic-bezier(.34,1.4,.64,1),opacity .1s ease; cursor:pointer; }
    .tap:hover  { transform:translateY(-1px); }
    .tap:active { transform:scale(.96); opacity:.65; }

    .tog { transition:all .16s ease; cursor:pointer; user-select:none; }
    .tog:active { transform:scale(.95); }

    .lift { transition:box-shadow .2s ease,transform .2s ease; }
    .lift:hover { transform:translateY(-2px); box-shadow:0 10px 36px rgba(37,99,235,.11)!important; }

    input:focus, textarea:focus {
      outline:none!important;
      border-color:#2563eb!important;
      box-shadow:0 0 0 3px rgba(37,99,235,.12)!important;
    }
    .nav-tab { transition:color .16s,border-color .16s; }
    .sp {
      width:16px; height:16px;
      border:2.5px solid rgba(37,99,235,.2);
      border-top-color:#2563eb;
      border-radius:50%;
      animation:spin .7s linear infinite;
      display:inline-block; flex-shrink:0;
    }
    .fr { backdrop-filter:blur(20px) saturate(1.5); -webkit-backdrop-filter:blur(20px) saturate(1.5); }
  `;
  document.head.appendChild(s);
})();

/* ── Tokens ────────────────────────────────────────────────────────────────── */
const C = {
  // Canvas
  bg:      "#f0f2f8",
  white:   "#ffffff",
  surface: "#ffffff",
  tint:    "#f5f7ff",
  tint2:   "#eef0f8",

  // Borders
  line:    "rgba(37,99,235,.10)",
  lineM:   "rgba(37,99,235,.18)",

  // Type
  ink:     "#0f172a",
  ink2:    "#1e293b",
  ink3:    "#475569",
  ink4:    "#64748b",

  // Cobalt — the hero
  blue:    "#2563eb",
  blueDk:  "#1d4ed8",
  blueSoft:"rgba(37,99,235,.08)",
  blueBdr: "rgba(37,99,235,.20)",
  blueMid: "#3b82f6",

  // Semantic — all calibrated to cobalt world
  green:  "#059669",  greenSoft:"rgba(5,150,105,.09)",
  amber:  "#d97706",  amberSoft:"rgba(217,119,6,.09)",
  red:    "#dc2626",  redSoft:  "rgba(220,38,38,.08)",
  violet: "#7c3aed",  violetSoft:"rgba(124,58,237,.09)",
  rose:   "#e11d48",  roseSoft: "rgba(225,29,72,.08)",
  teal:   "#0891b2",  tealSoft: "rgba(8,145,178,.09)",

  // Fonts
  display:"'Syne', system-ui, sans-serif",
  body:   "'DM Sans', system-ui, sans-serif",
};

/* ── Data ──────────────────────────────────────────────────────────────────── */
const TYPES = {
  friend:      { label:"Friend",       dot:"●", color:C.amber,  soft:C.amberSoft  },
  family:      { label:"Family",       dot:"♥", color:C.rose,   soft:C.roseSoft   },
  acquaintance:{ label:"Acquaintance", dot:"◆", color:C.teal,   soft:C.tealSoft   },
  lead:        { label:"Network",      dot:"▲", color:C.violet, soft:C.violetSoft },
};
const NURTURE = {
  new:    { label:"New",     color:C.teal,  soft:C.tealSoft  },
  warming:{ label:"Growing", color:C.amber, soft:C.amberSoft },
  close:  { label:"Close",   color:C.green, soft:C.greenSoft },
};
const HOBBIES = {
  coffee:"☕",cooking:"👩‍🍳",hiking:"🥾",reading:"📚",music:"🎵",
  movies:"🎬",art:"🎨",yoga:"🧘",pilates:"🏋️",running:"🏃",
  cycling:"🚲",tennis:"🎾",dancing:"💃",fitness:"💪",gaming:"🎮",
  poker:"🃏",mahjong:"🀄",gamenight:"🎲",boardgames:"♟️",photography:"📷",
  gardening:"🌱",crafts:"✂️",history:"📜",fashion:"👗",brunch:"🥞",
  concerts:"🎤",volunteering:"🤝",travel:"✈️",
};
// Display labels for keys that differ from the key string
const HOBBY_LABELS = { gamenight:"game night", boardgames:"board games" };
function hobbyLabel(h) { return HOBBY_LABELS[h] || h; }
function hobbyEmoji(h) { return HOBBIES[h] || "✨"; }
const LIFE_EVENTS_TYPES = {
  baby:       { label:"New baby",     emoji:"👶" },
  newjob:     { label:"New job",      emoji:"💼" },
  promoted:   { label:"Promoted",     emoji:"🚀" },
  moved:      { label:"Moved",        emoji:"🏠" },
  married:    { label:"Got married",  emoji:"💍" },
  engaged:    { label:"Got engaged",  emoji:"💍" },
  graduated:  { label:"Graduated",    emoji:"🎓" },
  health:     { label:"Health journey",emoji:"💙" },
  loss:       { label:"Lost someone", emoji:"🕊️" },
  other:      { label:"Life event",   emoji:"⭐" },
};
const KID_INT = {
  lego:"🧱",art:"🎨",sports:"⚽",reading:"📚",music:"🎵",dinosaurs:"🦕",
  animals:"🐾",cooking:"👩‍🍳",dance:"💃",gaming:"🎮",outdoors:"🌳",building:"🔨",
  science:"🔬",dress_up:"👑",swimming:"🏊",bikes:"🚲",trains:"🚂",dolls:"🪆",superheroes:"🦸",puzzles:"🧩",
};
const ENERGY = { low:"Calm", medium:"Active", high:"High energy" };
const PLACES = { our_house:"Our place",their_house:"Their place",park:"Park",class:"Class",cafe:"Café",other:"Other" };

/* ── Seeds ─────────────────────────────────────────────────────────────────── */
const SEED_C = [
  {id:1,name:"Sarah M.",type:"friend",hobbies:["wine","brunch","art"],stage:"close",lastSeen:"2026-02-10",birthday:"1985-03-15",metAt:"yoga studio",notes:"Obsessed with Burgundy wines, has two kids",followUpDays:21,followUpSet:"2026-02-10",snoozedUntil:null,log:[],phone:"",email:"",calType:"none",calUrl:"",manualSlots:[]},
  {id:2,name:"Priya K.",type:"friend",hobbies:["fashion","shopping","coffee"],stage:"close",lastSeen:"2025-11-20",birthday:"1987-07-22",metAt:"college",notes:"Works in fashion PR, always impeccably dressed",followUpDays:21,followUpSet:"2025-11-20",snoozedUntil:null,log:[],phone:"",email:"",calType:"none",calUrl:"",manualSlots:[]},
  {id:3,name:"Dana R.",type:"friend",hobbies:["hiking","cooking","yoga"],stage:"close",lastSeen:"2026-04-01",birthday:"1983-11-08",metAt:"neighborhood",notes:"Neighbor, always down for a walk or dinner",followUpDays:14,followUpSet:"2026-04-01",snoozedUntil:null,log:[{date:"2026-04-01",note:"Walked to the farmers market — she's thinking about redecorating"}],phone:"",email:"",calType:"none",calUrl:"",manualSlots:[]},
  {id:4,name:"Lila T.",type:"acquaintance",hobbies:["jewelry","antiques","history"],stage:"warming",lastSeen:"2026-04-20",birthday:"",metAt:"school event",notes:"Fascinating taste in antiques, mentioned a trip to Vienna",followUpDays:14,followUpSet:"2026-04-20",snoozedUntil:null,log:[],phone:"",email:"",calType:"none",calUrl:"",manualSlots:[]},
  {id:5,name:"Marcus O.",type:"lead",hobbies:["coffee","travel","reading"],stage:"new",lastSeen:"2026-04-28",birthday:"",metAt:"networking dinner",notes:"Creative director at Ogilvy, interested in vintage branding",followUpDays:7,followUpSet:"2026-04-28",snoozedUntil:null,log:[],phone:"",email:"",calType:"none",calUrl:"",manualSlots:[]},
  {id:6,name:"Mom",type:"family",hobbies:["cooking","reading","travel"],stage:"close",lastSeen:"2026-03-20",birthday:"1952-05-12",metAt:"family",notes:"In Cape Town, loves hearing about the kids. Call more.",followUpDays:14,followUpSet:"2026-03-20",snoozedUntil:null,log:[],phone:"",email:"",calType:"none",calUrl:"",manualSlots:[]},
];
const SEED_G = [
  {id:101,name:"Wine circle",emoji:"🍷",contactIds:[1,2],notes:"Monthly wine nights"},
  {id:102,name:"School parents",emoji:"📚",contactIds:[4],notes:""},
];
const SEED_KIDS = [
  {id:201,name:"Lily",age:6,interests:["art","lego","dress_up"],energy:"medium",allergies:"",notes:"Best in mornings"},
  {id:202,name:"Noah",age:3,interests:["trains","animals","outdoors"],energy:"high",allergies:"peanuts",notes:"Nap 1–3pm"},
];
const SEED_PDC = [
  {id:301,parentName:"Maya's mom (Rachel)",kids:[{id:401,name:"Maya",age:6,interests:["art","dance","dress_up"],energy:"medium",allergies:"",notes:""}]},
  {id:302,parentName:"Jake's dad (Tom)",kids:[{id:402,name:"Jake",age:4,interests:["trains","lego","superheroes"],energy:"high",allergies:"dairy",notes:"Pickup 4pm"}]},
];

/* ── Helpers ───────────────────────────────────────────────────────────────── */
const todayISO = () => new Date().toISOString().split("T")[0];
const daysAgo  = d => d ? Math.round((Date.now()-new Date(d))/864e5) : 999;

function fupStatus(c) {
  if (c.snoozedUntil && new Date(c.snoozedUntil) > new Date())
    return { label:"Snoozed", color:C.ink4, soft:C.tint2, urgent:false };
  // Brand new — never connected yet, no real interaction history
  if (!c.lastSeen && (c.touchCount||0)===0)
    return { label:"New", color:C.blue, soft:C.blueSoft, urgent:false };
  const r = daysAgo(c.lastSeen||c.followUpSet) / (c.followUpDays||14);
  if (r>=1.2) return { label:"Good time to reconnect", color:C.red,   soft:C.redSoft,   urgent:true  };
  if (r>=0.8) return { label:"Good time to say hi",   color:C.amber, soft:C.amberSoft, urgent:true  };
  if (r>=0.5) return { label:"In touch",         color:C.blue,  soft:C.blueSoft,  urgent:false };
  return             { label:"Close",            color:C.green, soft:C.greenSoft, urgent:false };
}

function bdayStatus(bday) {
  if (!bday) return null;
  const now=new Date(), b=new Date(bday);
  const next=new Date(now.getFullYear(),b.getMonth(),b.getDate());
  if (next<now) next.setFullYear(now.getFullYear()+1);
  const d=Math.round((next-now)/864e5);
  if (d===0) return {label:"Today! 🎂",color:C.rose,  soft:C.roseSoft,  days:0};
  if (d<=3)  return {label:`${d}d 🎂`,  color:C.red,   soft:C.redSoft,   days:d};
  if (d<=14) return {label:`${d}d`,     color:C.amber, soft:C.amberSoft, days:d};
  if (d<=30) return {label:`${d}d`,     color:C.blue,  soft:C.blueSoft,  days:d};
  return null;
}

function getAge(bday) {
  if (!bday) return null;
  const b=new Date(bday),n=new Date();
  let a=n.getFullYear()-b.getFullYear();
  if(n.getMonth()<b.getMonth()||(n.getMonth()===b.getMonth()&&n.getDate()<b.getDate())) a--;
  return a;
}

function season() {
  const m=new Date().getMonth();
  if(m===11||m===0) return "Winter is a great time to reconnect — long dinners, a good bottle, no rush.";
  if(m>=2&&m<=4)   return "Spring energy is real. Suggest a walk, a market, an outdoor lunch — people say yes.";
  if(m>=5&&m<=7)   return "Summer opens calendars. Rooftop drinks, picnics, long evenings — now is the time.";
  return                  "Autumn is for catching up properly — cozy dinners, gallery visits, that bottle you've been saving.";
}

function dedupeContacts(list){
  const richness=c=>(c.gatherId?100:0)+(c.email?10:0)+(c.phone?5:0)+(c.notes?5:0)+(c.log?.length||0)*2;
  const seen=new Map();
  for(const c of list){
    const key=c.name?.toLowerCase().trim()||c.id;
    const existing=seen.get(key);
    if(!existing||richness(c)>richness(existing)) seen.set(key,c);
  }
  return [...seen.values()];
}

function urgentList(contacts) {
  return contacts.filter(c=>fupStatus(c).urgent).sort((a,b)=>daysAgo(a.lastSeen)-daysAgo(b.lastSeen)).reverse();
}
function bdaySoon(contacts) {
  return contacts.map(c=>({c,bs:bdayStatus(c.birthday)})).filter(x=>x.bs&&x.bs.days<=30).sort((a,b)=>a.bs.days-b.bs.days);
}

function initials(n) { return n.trim().split(/\s+/).map(w=>w[0]).join("").slice(0,2).toUpperCase(); }

function buildNudge(profile, contact, days, intent) {
  const fn = contact.name.split(" ")[0];
  const isFamily = contact.type === "family";
  const isMale = profile.gender === "man";
  const isOlder = profile.ageRange === "50s60s" || profile.ageRange === "70plus";

  // Intent-specific instant messages (no AI, just smart templates)
  if (intent && intent !== "checkin") {
    const t = {
      coffee: {
        mo: `Hey ${fn}! We should grab a coffee and catch up — how are you doing?`,
        m:  `Hey ${fn}! We should grab a coffee soon — how's everything going?`,
        o:  `Hi ${fn}! I was thinking we should get together for a coffee — it would be lovely to catch up 😊`,
        d:  `Hey ${fn}! We should grab a coffee and catch up properly — how's everything? ☕`,
      },
      dinner: {
        mo: `Hey ${fn}! We should get dinner sometime — it's been too long.`,
        m:  `Hey ${fn}! We should get dinner soon — it's been ages!`,
        o:  `Hi ${fn}! I was thinking we should get together for dinner — how does that sound? 😊`,
        d:  `Hey ${fn}! We should get dinner sometime — it's been way too long! 🍽️`,
      },
      drinks: {
        mo: `Hey ${fn}! Overdue for a drink — you free soon?`,
        m:  `Hey ${fn}! We're overdue for drinks — you around soon?`,
        o:  `Hi ${fn}! We should get together for a drink and a proper catch-up 😊`,
        d:  `Hey ${fn}! We're so overdue for drinks — you free soon? 🍺`,
      },
      lunch: {
        mo: `Hey ${fn}! We should grab lunch sometime — how are you?`,
        m:  `Hey ${fn}! We should grab lunch soon — it's been a while!`,
        o:  `Hi ${fn}! I thought we might get together for lunch — it would be so nice to catch up 😊`,
        d:  `Hey ${fn}! We should grab lunch soon and catch up properly! 🥗`,
      },
      walk: {
        mo: `Hey ${fn}! We should go for a walk and catch up sometime.`,
        m:  `Hey ${fn}! We should go for a walk soon — it's been a while!`,
        o:  `Hi ${fn}! I thought it might be nice to go for a walk together — how does that sound? 😊`,
        d:  `Hey ${fn}! We should go for a walk and catch up — it's been too long! 🚶`,
      },
      tennis: {
        mo: `Hey ${fn}! We should get a game of tennis in — how does your schedule look?`,
        m:  `Hey ${fn}! Up for some tennis soon? It's been ages!`,
        o:  `Hi ${fn}! I was thinking we should have a game of tennis — what do you think? 😊`,
        d:  `Hey ${fn}! We should play tennis soon — it's been way too long! 🎾`,
      },
      golf: {
        mo: `Hey ${fn}! We should get a round in — how are you doing?`,
        m:  `Hey ${fn}! We should play a round of golf soon — it's been too long!`,
        o:  `Hi ${fn}! I was thinking we should get a round of golf in — what do you think? 😊`,
        d:  `Hey ${fn}! We should get a round of golf in — it's been too long! ⛳`,
      },
      call: {
        mo: `Hey ${fn}! Would love to catch up — got time for a call soon?`,
        m:  `Hey ${fn}! We should have a proper catch-up call soon — how are you doing?`,
        o:  `Hi ${fn}! I'd love to have a proper chat — would you be free for a call sometime? 😊`,
        d:  `Hey ${fn}! Would love to catch up properly — free for a call soon? 📞`,
      },
    };
    const v = t[intent];
    if (v) {
      if (isMale && isOlder) return v.mo;
      if (isMale) return v.m;
      if (isOlder) return v.o;
      return v.d;
    }
  }

  // Default check-in (tone by gender/age/days)
  const activityMap = {
    coffee:"grab a coffee", beer:"grab a drink", dinner:"get dinner",
    tennis:"play tennis", walk:"go for a walk", lunch:"grab lunch",
    golf:"play a round of golf", gym:"hit the gym",
  };
  const acts = (profile.catchupActivity||[]).map(a=>activityMap[a]).filter(Boolean);
  const actPhrase = acts.length > 0 ? acts[Math.floor(Math.random()*acts.length)] : "catch up";

  if (isMale && isOlder) {
    if (isFamily)  return days > 30 ? `Hey ${fn}, just checking in. Hope all is well.` : `Hey ${fn}, how's everything going?`;
    if (days > 60) return `Hey ${fn}! Been a while. How are you doing these days? We should ${actPhrase} soon.`;
    if (days > 21) return `Hey ${fn}! Just thinking of you. We should ${actPhrase} sometime.`;
    return `Hey ${fn}! How's everything going?`;
  }
  if (isMale) {
    if (isFamily)  return days > 30 ? `Hey ${fn}! Been a while — hope you're doing well 👋` : `Hey ${fn}, how's it going?`;
    if (days > 60) return `Hey ${fn}! It's been ages. We should ${actPhrase} soon — how are you doing?`;
    if (days > 21) return `Hey ${fn}! Just thought I'd check in. We should ${actPhrase} sometime — how's everything?`;
    return `Hey ${fn}! How's it going?`;
  }
  if (isOlder) {
    if (isFamily)  return days > 30 ? `Hello ${fn} darling, just thinking of you and wanted to say hello 💕` : `Hello ${fn}! Just checking in — how are you?`;
    if (days > 60) return `Hi ${fn}! It's been such a long time — I've been thinking about you. We should ${actPhrase} and catch up properly!`;
    if (days > 21) return `Hi ${fn}! Just wanted to say hello. Hope all is well — we should ${actPhrase} soon 😊`;
    return `Hi ${fn}! Just thinking of you — hope you're doing well 😊`;
  }
  let result;
  if (isFamily)  result = days > 30 ? `Hey ${fn}! It's been a while — just wanted to check in and say I'm thinking of you 💕` : `Hey ${fn}! Just thinking of you 💕 How's everything?`;
  else if (days > 60) result = `Hey ${fn}! It's been way too long — miss you! We should ${actPhrase} soon and catch up properly 😊`;
  else if (days > 21) result = `Hey ${fn}! Was just thinking about you — we should ${actPhrase} soon! How's everything going? 😊`;
  else result = `Hey ${fn}! Just wanted to say hi — how's everything going? 😊`;
  return applyEmojiStyle(result, profile.emojiStyle);
}
// Build a style context string for AI prompts from the user's texting personality
function buildStyleCtx(profile) {
  const emoji = profile.emojiStyle==="none" ? "Use NO emojis whatsoever — the person never uses them."
    : profile.emojiStyle==="some" ? "Use at most one emoji, only if it feels completely natural."
    : "Emojis are fine if they feel natural.";
  const length = profile.msgLength==="short" ? "Keep it to 1–2 short sentences max."
    : profile.msgLength==="long"  ? "3–4 sentences is fine — they write proper messages."
    : "2–3 sentences.";
  const tone = profile.toneVibe==="direct" ? "Tone: direct and to the point — no filler, no fluff."
    : profile.toneVibe==="casual" ? "Tone: casual and relaxed, like texting a mate."
    : profile.toneVibe==="warm"   ? "Tone: warm and friendly."
    : "";
  return [emoji,length,tone].filter(Boolean).join(" ");
}

// Strip emojis from a string based on emojiStyle preference
function applyEmojiStyle(str, emojiStyle) {
  if (!str) return str;
  if (emojiStyle==="none") {
    return str.replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27FF}\u{FE00}-\u{FEFF}\u{1F900}-\u{1F9FF}]/gu,"").replace(/\s{2,}/g," ").trim();
  }
  if (emojiStyle==="some") {
    let count=0;
    return str.replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27FF}\u{FE00}-\u{FEFF}\u{1F900}-\u{1F9FF}]/gu, m=>(++count<=1?m:"")).replace(/\s{2,}/g," ").trim();
  }
  return str;
}

const AV_BG = ["#1d4ed8","#059669","#7c3aed","#d97706","#0891b2","#dc2626","#475569"];
function avBg(n) { let h=0; for(const c of n) h=(h*31+c.charCodeAt(0))&0xffff; return AV_BG[h%AV_BG.length]; }

function parseVCF(text) {
  return text.split(/BEGIN:VCARD/i).slice(1).map(card => {
    const get=key=>{const m=card.match(new RegExp(`${key}[^:]*:([^\\r\\n]+)`,'i'));return m?m[1].trim():"";};
    const name=get("FN")||get("N").split(";").slice(0,2).reverse().join(" ").trim();
    return name ? {name,phone:get("TEL").replace(/[^\d+\-() ]/g,"").trim(),email:get("EMAIL"),birthday:get("BDAY").replace(/(\d{4})(\d{2})(\d{2})/,"$1-$2-$3")||""} : null;
  }).filter(Boolean);
}

/* ── VCF Export ────────────────────────────────────────────────────────────── */
function exportVCF(contact) {
  const lines = ["BEGIN:VCARD","VERSION:3.0"];
  lines.push(`FN:${contact.name||""}`);
  const parts = (contact.name||"").trim().split(/\s+/);
  if(parts.length>=2) lines.push(`N:${parts.slice(1).join(" ")};${parts[0]};;;`);
  if(contact.phone) lines.push(`TEL;TYPE=CELL:${contact.phone}`);
  if(contact.email) lines.push(`EMAIL:${contact.email}`);
  if(contact.birthday) lines.push(`BDAY:${contact.birthday.replace(/-/g,"")}`);
  if(contact.notes) lines.push(`NOTE:${contact.notes.replace(/\r?\n/g,"\\n")}`);
  lines.push("END:VCARD");
  const blob = new Blob([lines.join("\r\n")], {type:"text/vcard;charset=utf-8"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(contact.name||"contact").replace(/\s+/g,"_")}.vcf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(()=>URL.revokeObjectURL(url), 1000);
}

/* ── Analytics helper ──────────────────────────────────────────────────────── */
function track(event, props={}) {
  try { window.posthog?.capture(event, props); } catch(e) {}
}

async function callAI(prompt, sys) {
  try {
    const r = await fetch("/api/ai",{
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body:JSON.stringify({prompt,sys:sys||"You are a warm, thoughtful assistant."}),
    });
    if(!r.ok){ console.error("AI error",r.status,await r.text()); return ""; }
    return (await r.json()).text||"";
  } catch(e) { console.error("AI call failed",e); return ""; }
}
async function callAIJSON(prompt,sys) {
  const t=await callAI(prompt,sys);
  if(!t) return null;
  // Try direct parse first
  try{return JSON.parse(t.replace(/```json\n?|```/g,"").trim());}catch{}
  // Extract first JSON array or object found in the text
  const arrMatch=t.match(/\[[\s\S]*\]/);
  if(arrMatch){try{return JSON.parse(arrMatch[0]);}catch{}}
  const objMatch=t.match(/\{[\s\S]*\}/);
  if(objMatch){try{return JSON.parse(objMatch[0]);}catch{}}
  return null;
}

/* ── Toast ─────────────────────────────────────────────────────────────────── */
let _toastSetFn=null;
function showToast(msg,type="success"){_toastSetFn?.({msg,type,id:Date.now()});}
function Toast(){
  const [toast,setToast]=useState(null);
  useEffect(()=>{_toastSetFn=setToast;return()=>{_toastSetFn=null;};},[]);
  useEffect(()=>{if(!toast)return;const t=setTimeout(()=>setToast(null),2800);return()=>clearTimeout(t);},[toast]);
  if(!toast)return null;
  const cols={success:C.green,error:C.red,info:C.blue};
  return(
    <div className="p" style={{position:"fixed",bottom:"calc(76px + env(safe-area-inset-bottom))",left:"50%",transform:"translateX(-50%)",
      background:C.ink,color:"#fff",borderRadius:10,padding:"10px 18px",
      fontSize:13,fontFamily:C.body,fontWeight:500,zIndex:999,
      display:"flex",alignItems:"center",gap:8,
      boxShadow:"0 8px 28px rgba(0,0,0,.22)",whiteSpace:"nowrap",maxWidth:"90vw",
      borderLeft:`3px solid ${cols[toast.type]||C.green}`}}>
      {toast.type==="success"?"✓":toast.type==="error"?"✕":"ℹ"} {toast.msg}
    </div>
  );
}

/* ── Add-to-Home-Screen prompt (iOS only) ──────────────────────────────────── */
function AddToHomeScreen(){
  const [show,setShow]=useState(false);
  const isIOS=/iPad|iPhone|iPod/.test(navigator.userAgent)&&!window.MSStream;
  const isStandalone=window.matchMedia?.("(display-mode: standalone)").matches||window.navigator.standalone;
  useEffect(()=>{
    if(!isIOS||isStandalone||localStorage.getItem("gather_a2hs_dismissed")) return;
    const t=setTimeout(()=>setShow(true),4000);
    return()=>clearTimeout(t);
  },[]);
  if(!show) return null;
  return(
    <div className="p" style={{position:"fixed",bottom:"calc(76px + env(safe-area-inset-bottom) + 10px)",left:16,right:16,
      background:C.white,border:`1.5px solid ${C.blueBdr}`,borderRadius:14,padding:"14px 16px",zIndex:997,
      boxShadow:"0 8px 32px rgba(37,99,235,.14)"}}>
      <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
        <div style={{width:42,height:42,borderRadius:10,background:C.blue,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <div style={{width:12,height:12,borderRadius:"50%",background:"rgba(255,255,255,.9)"}}/>
        </div>
        <div style={{flex:1}}>
          <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:3}}>Add gather to your home screen</p>
          <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,lineHeight:1.5}}>
            Tap <strong>Share</strong> ⎙ then <strong>"Add to Home Screen"</strong> for the best experience.
          </p>
        </div>
        <button onClick={()=>{setShow(false);localStorage.setItem("gather_a2hs_dismissed","1");}}
          style={{background:"none",border:"none",cursor:"pointer",color:C.ink3,fontSize:20,lineHeight:1,padding:"0 2px",flexShrink:0}}>×</button>
      </div>
    </div>
  );
}

/* ── Nav tab icons ─────────────────────────────────────────────────────────── */
function NavTabIcon({id,active}){
  const col=active?C.blue:C.ink4;
  const icons={
    overview:<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" stroke={col} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 21V12h6v9" stroke={col} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    reach:   <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="9" cy="7" r="3" stroke={col} strokeWidth="1.7"/><circle cx="16" cy="7" r="3" stroke={col} strokeWidth="1.7"/><path d="M3 20v-1a5 5 0 015-5h3" stroke={col} strokeWidth="1.7" strokeLinecap="round"/><path d="M14 17l2 2 4-4" stroke={col} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    contacts:<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="9" cy="7" r="3.5" stroke={col} strokeWidth="1.7"/><path d="M2 21v-1a6 6 0 016-6h2a6 6 0 016 6v1" stroke={col} strokeWidth="1.7" strokeLinecap="round"/><path d="M16 11l2 2 4-4" stroke={col} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    plan:    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" stroke={col} strokeWidth="1.7"/><path d="M3 9h18M8 2v4M16 2v4" stroke={col} strokeWidth="1.7" strokeLinecap="round"/><path d="M8 14h2M12 14h2M8 17h2" stroke={col} strokeWidth="1.7" strokeLinecap="round"/></svg>,
    playdates:<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="8" cy="8" r="2.5" stroke={col} strokeWidth="1.7"/><circle cx="16" cy="8" r="2.5" stroke={col} strokeWidth="1.7"/><path d="M4 20v-1a4 4 0 014-4h8a4 4 0 014 4v1" stroke={col} strokeWidth="1.7" strokeLinecap="round"/></svg>,
    profile: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="3.5" stroke={col} strokeWidth="1.7"/><path d="M5 21v-1a7 7 0 0114 0v1" stroke={col} strokeWidth="1.7" strokeLinecap="round"/></svg>,
  };
  return icons[id]||null;
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* ── Atoms ──────────────────────────────────────────────────────────────── */

function Av({name,size=40}) {
  return (
    <div style={{width:size,height:size,borderRadius:"50%",flexShrink:0,background:avBg(name),display:"flex",alignItems:"center",justifyContent:"center",color:"rgba(255,255,255,.92)",fontFamily:C.display,fontWeight:700,fontSize:size*.32,letterSpacing:".02em"}}>
      {initials(name)}
    </div>
  );
}

function Pill({color,soft,children}) {
  return (
    <span style={{background:soft,color,border:`1px solid ${color}22`,borderRadius:4,fontSize:10,fontWeight:600,fontFamily:C.body,padding:"2px 7px",whiteSpace:"nowrap",letterSpacing:".04em",textTransform:"uppercase"}}>
      {children}
    </span>
  );
}

function Chip({color=C.blue,soft,children,onClick,active,small}) {
  return (
    <span className="tog" onClick={onClick} style={{display:"inline-flex",alignItems:"center",gap:4,background:active?(soft||C.blueSoft):C.white,color:active?color:C.ink2,border:`1.5px solid ${active?color+"30":C.lineM}`,borderRadius:6,fontSize:small?12:13,fontWeight:active?600:500,padding:small?"4px 11px":"6px 14px",whiteSpace:"nowrap",fontFamily:C.body}}>
      {children}
    </span>
  );
}

function Btn({onClick,children,variant="primary",small,disabled,loading,full,icon}) {
  const V={
    primary: {bg:C.blue,    fg:"#fff",    border:"none",                         shadow:`0 2px 12px rgba(37,99,235,.30)`},
    soft:    {bg:C.blueSoft,fg:C.blue,    border:`1.5px solid ${C.blueBdr}`,     shadow:"none"},
    ghost:   {bg:"transparent",fg:C.ink2, border:`1.5px solid ${C.lineM}`,       shadow:"none"},
    green:   {bg:C.greenSoft,fg:C.green,  border:`1.5px solid ${C.green}28`,     shadow:"none"},
    amber:   {bg:C.amberSoft,fg:C.amber,  border:`1.5px solid ${C.amber}28`,     shadow:"none"},
    red:     {bg:C.redSoft,  fg:C.red,    border:`1.5px solid ${C.red}22`,       shadow:"none"},
    rose:    {bg:C.roseSoft, fg:C.rose,   border:`1.5px solid ${C.rose}28`,      shadow:"none"},
    violet:  {bg:C.violetSoft,fg:C.violet,border:`1.5px solid ${C.violet}28`,   shadow:"none"},
    teal:    {bg:C.tealSoft, fg:C.teal,   border:`1.5px solid ${C.teal}28`,      shadow:"none"},
    dark:    {bg:C.ink,      fg:"#fff",   border:"none",                         shadow:"0 2px 8px rgba(0,0,0,.20)"},
  };
  const v=V[variant]||V.primary;
  return (
    <button className="tap" onClick={disabled||loading?undefined:onClick} style={{background:v.bg,color:v.fg,border:v.border,boxShadow:v.shadow,borderRadius:8,cursor:(disabled||loading)?"not-allowed":"pointer",fontFamily:C.body,fontWeight:600,padding:small?"6px 14px":"9px 22px",fontSize:small?12:13,opacity:(disabled||loading)?.38:1,display:"inline-flex",alignItems:"center",gap:7,width:full?"100%":undefined,justifyContent:full?"center":undefined,transition:"opacity .12s,box-shadow .15s",letterSpacing:".01em"}}>
      {loading?<span className="sp"/>:icon?<span style={{fontSize:small?13:16}}>{icon}</span>:null}
      {!loading&&children}
    </button>
  );
}

function Field({value,onChange,placeholder,multi,type,style,min,max}) {
  const base={background:C.white,border:`1.5px solid ${C.line}`,borderRadius:8,color:C.ink,fontFamily:C.body,fontSize:13,padding:"9px 13px",width:"100%",transition:"border-color .15s,box-shadow .15s",lineHeight:1.6,...style};
  return multi
    ?<textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={3} style={{...base,resize:"vertical"}}/>
    :<input type={type||"text"} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} min={min} max={max} style={base}/>;
}

function Label({children,note}) {
  return (
    <div style={{marginBottom:8}}>
      <p style={{fontSize:13,fontWeight:600,color:C.ink2,fontFamily:C.body}}>{children}</p>
      {note&&<p style={{fontSize:12,color:C.ink3,marginTop:3,lineHeight:1.6,fontFamily:C.body}}>{note}</p>}
    </div>
  );
}

function Hr({style}) { return <div style={{height:1,background:C.line,margin:"16px 0",...style}}/>; }

function Card({children,style,className,onClick}) {
  return (
    <div className={className} onClick={onClick} style={{background:C.white,border:`1.5px solid ${C.line}`,borderRadius:14,padding:20,boxShadow:"0 1px 4px rgba(37,99,235,.06)",...style}}>
      {children}
    </div>
  );
}

function EmptyState({icon,title,note}) {
  return (
    <div className="i" style={{textAlign:"center",padding:"60px 24px"}}>
      <div style={{fontSize:36,marginBottom:14,opacity:.25}}>{icon}</div>
      <p style={{fontFamily:C.display,fontSize:22,fontWeight:700,color:C.ink,marginBottom:8}}>{title}</p>
      <p style={{fontSize:13,color:C.ink3,lineHeight:1.7,maxWidth:260,margin:"0 auto",fontFamily:C.body}}>{note}</p>
    </div>
  );
}

function SearchBar({value,onChange,placeholder}) {
  return (
    <div style={{position:"relative",marginBottom:16}}>
      <span style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",color:C.ink4,fontSize:14,pointerEvents:"none"}}>⌕</span>
      <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder||"Search…"} style={{width:"100%",background:C.white,border:`1.5px solid ${C.line}`,borderRadius:8,color:C.ink,fontFamily:C.body,fontSize:13,padding:"9px 13px 9px 36px",outline:"none",transition:"border-color .15s,box-shadow .15s"}}/>
      {value&&<span onClick={()=>onChange("")} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",cursor:"pointer",color:C.ink3,fontSize:12}}>✕</span>}
    </div>
  );
}

/* ── VCF Importer ──────────────────────────────────────────────────────────── */
function VCFImporter({onImport}) {
  const [list,setList]=useState([]);
  const [q,setQ]=useState("");
  const [open,setOpen]=useState(false);
  const ref=useRef();

  function handleFile(e) {
    const f=e.target.files[0]; if(!f) return;
    const r=new FileReader();
    r.onload=ev=>{setList(parseVCF(ev.target.result));setOpen(true);setQ("");};
    r.readAsText(f);
  }

  const filtered=useMemo(()=>{
    if(!q.trim()) return list.slice(0,8);
    const lq=q.toLowerCase();
    return list.filter(c=>c.name.toLowerCase().includes(lq)||c.phone.includes(lq)||c.email.toLowerCase().includes(lq)).slice(0,8);
  },[list,q]);

  return (
    <div style={{border:`1.5px solid ${C.blueBdr}`,borderRadius:10,overflow:"hidden",marginBottom:18,background:C.tint}}>
      <div style={{padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body}}>Import from contacts</p>
          <p style={{fontSize:13,color:C.ink3,marginTop:2,fontFamily:C.body}}>iPhone: Share Contact → Save to Files → upload here</p>
        </div>
        <Btn onClick={()=>ref.current?.click()} variant="soft" small>Upload .vcf</Btn>
      </div>
      <input ref={ref} type="file" accept=".vcf,.vcard" onChange={handleFile} style={{display:"none"}}/>
      {open&&(
        <div style={{borderTop:`1.5px solid ${C.line}`,padding:"12px 16px",background:C.white}}>
          <SearchBar value={q} onChange={setQ} placeholder="Search your people…"/>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {filtered.map((c,i)=>(
              <div key={i} className="tap" onClick={()=>{onImport(c);setOpen(false);setList([]);}} style={{display:"flex",alignItems:"center",gap:11,padding:"9px 12px",background:C.tint,borderRadius:8,border:`1.5px solid ${C.line}`,cursor:"pointer"}}>
                <Av name={c.name} size={30}/>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{fontSize:13,fontWeight:500,color:C.ink,fontFamily:C.body}}>{c.name}</p>
                  <p style={{fontSize:13,color:C.ink3,fontFamily:C.body}}>{[c.phone,c.email].filter(Boolean).join(" · ")||"No details"}</p>
                </div>
                <span style={{fontSize:12,color:C.blue,fontWeight:600,fontFamily:C.body}}>Select →</span>
              </div>
            ))}
            {filtered.length===0&&<p style={{fontSize:13,color:C.ink3,textAlign:"center",padding:"10px 0",fontFamily:C.body}}>No matches</p>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Overview / Dashboard ──────────────────────────────────────────────────── */
function Overview({contacts,groups,myProfile,myUid,onTabChange,onOpenChat,plans,onRsvpPlan}) {
  const urgent=urgentList(contacts);
  const bdays=bdaySoon(contacts);
  const [nudge,setNudge]=useState("");
  const [nLoad,setNLoad]=useState(false);
  const [notifPerm,setNotifPerm]=useState(()=>typeof Notification!=="undefined"?Notification.permission:"unsupported");
  const [insight,setInsight]=useState(null);
  const [insightLoading,setInsightLoading]=useState(false);
  const [showReachedOut,setShowReachedOut]=useState(false);
  const [spark,setSpark]=useState(null);
  const [sparkLoading,setSparkLoading]=useState(false);
  const [sparkIdx,setSparkIdx]=useState(0);
  const [sparkPeople,setSparkPeople]=useState([]);
  const [sparkGroup,setSparkGroup]=useState(null); // group object when a group is selected
  const [sparkPickerOpen,setSparkPickerOpen]=useState(false);
  const [sparkSearch,setSparkSearch]=useState("");

  // Proactively surface the best mutual free window across groups/contacts
  useEffect(()=>{
    const myBusy=myProfile.busyBlocks||[];
    if(!myProfile.calendarConnected||myBusy.length===0) return;
    let cancelled=false;
    async function findInsight(){
      setInsightLoading(true);
      try{
        // 1. Check groups first — most magical
        for(const g of groups){
          if(!g.contactIds?.length) continue;
          const members=contacts.filter(c=>g.contactIds.includes(c.id)&&c.email);
          if(members.length===0) continue;
          const memberBusy=[];
          for(const m of members){
            const busy=await getContactBusy(m);
            if(busy) memberBusy.push(...busy);
          }
          if(memberBusy.length===0) continue;
          const windows=findFreeWindows(myBusy,memberBusy,14);
          if(windows.length>0&&!cancelled){
            setInsight({type:"group",group:g,windows,synced:memberBusy.length>0});
            setInsightLoading(false);
            return;
          }
        }
        // 2. Fall back to individual contacts (urgent ones first)
        const toCheck=[...urgentList(contacts),...contacts].filter((c,i,arr)=>arr.findIndex(x=>x.id===c.id)===i).slice(0,8);
        for(const c of toCheck){
          if(!c.email) continue;
          const busy=await getContactBusy(c);
          if(!busy) continue;
          const windows=findFreeWindows(myBusy,busy,14);
          if(windows.length>0&&!cancelled){
            setInsight({type:"contact",contact:c,windows});
            setInsightLoading(false);
            return;
          }
        }
      }catch(e){}
      if(!cancelled) setInsightLoading(false);
    }
    findInsight();
    return ()=>{cancelled=true;};
  },[myProfile.calendarConnected,groups.length,contacts.length]); // eslint-disable-line

  // Weekend Spark — surfaces up to 3 free windows + who to invite + activity
  useEffect(()=>{
    if(!contacts.length) return;
    let cancelled=false;
    async function buildSpark(){
      setSparkLoading(true);
      setSparkIdx(0);
      try{
        const myBusy=myProfile.busyBlocks||[];
        const today=new Date();
        const wins=[{label:"afternoon",start:12,end:17},{label:"morning",start:9,end:12},{label:"evening",start:17,end:20}];

        // Collect up to 3 free slots across the next 14 days, preferring weekends
        const freeSlots=[];
        for(let i=0;i<14&&freeSlots.length<3;i++){
          const d=new Date(today); d.setDate(today.getDate()+i);
          const isWeekend=d.getDay()===6||d.getDay()===0;
          if(myBusy.length>0&&!isWeekend&&freeSlots.length===0) continue; // weekends first if cal connected
          const dayStr=d.toLocaleDateString("en-US",{weekday:"long"});
          const shortDay=d.toLocaleDateString("en-US",{weekday:"short",month:"numeric",day:"numeric"});
          for(const w of wins){
            const wS=new Date(d); wS.setHours(w.start,0,0,0);
            const wE=new Date(d); wE.setHours(w.end,0,0,0);
            if(wS<=new Date()) continue;
            const overlap=myBusy.reduce((s,b)=>{
              const bS=new Date(b.start).getTime(),bE=new Date(b.end).getTime();
              return s+Math.max(0,Math.min(bE,wE.getTime())-Math.max(bS,wS.getTime()));
            },0);
            if(overlap<45*60*1000){ freeSlots.push({dayStr,shortDay,window:w.label,dayObj:d,dateStr:d.toISOString().split("T")[0]}); break; }
          }
        }
        if(!freeSlots.length||cancelled){ setSparkLoading(false); return; }

        // Pick who to invite
        const inGroup=contacts.filter(c=>groups.some(g=>g.contactIds?.includes(c.id)));
        const pool=[...inGroup,...urgentList(contacts),...contacts.filter(c=>c.stage==="close"||c.type==="family")]
          .filter((c,i,arr)=>arr.findIndex(x=>x.id===c.id)===i&&c.name).slice(0,3);
        if(!pool.length||cancelled){ setSparkLoading(false); return; }

        // Build slot objects with activity + message
        const allH=[...new Set(pool.flatMap(c=>c.hobbies||[]).concat(myProfile.hobbies||[]))];
        const outdoorH=["hiking","running","cycling","tennis","golf","yoga"];
        const indoorH=["cooking","gaming","poker","boardgames","mahjong","gamenight"];
        const names=pool.map(c=>c.name.split(" ")[0]);
        const nameList=names.length===1?names[0]:names.length===2?`${names[0]} and ${names[1]}`:`${names[0]}, ${names[1]} and ${names[2]}`;

        const month=new Date().getMonth();
        const seasonPhrase=month<=1||month===11?" — a good excuse to get out of the house":month<=4?" — nice while the weather's still mild":month<=7?" — before the summer gets too busy":month<=10?" — before things get too hectic":"";

        const slots=freeSlots.map(slot=>{
          const matchOutdoor=allH.find(h=>outdoorH.includes(h));
          const matchIndoor=allH.find(h=>indoorH.includes(h));
          const indoorFallback=slot.window==="morning"?"coffee":"a film night";
          const outdoorFallback=slot.window==="morning"?"a morning walk":"lunch and a walk";
          const activity=matchOutdoor?hobbyLabel(matchOutdoor):matchIndoor?hobbyLabel(matchIndoor):outdoorFallback;
          const message=`Hey${names.length===1?` ${names[0]}`:""}! Anyone free ${slot.dayStr.toLowerCase()} ${slot.window} for ${activity}?${seasonPhrase}`;
          return {...slot,activity,message};
        });

        if(!cancelled){
          setSpark({names,nameList,pool,slots,seasonPhrase});
          setSparkPeople(pool);
        }
      }catch(e){}
      if(!cancelled) setSparkLoading(false);
    }
    buildSpark();
    return()=>{cancelled=true;};
  },[contacts.length,groups.length,myProfile.busyBlocks]); // eslint-disable-line

  async function enableNotifications(){
    if(isNative){
      // Native iOS/Android — use Capacitor push plugin (APNs + FCM)
      const token=await registerNativePush();
      if(token&&myUid){
        await setDoc(doc(db,"users",myUid),{fcmToken:token},{merge:true}).catch(()=>{});
        setNotifPerm("granted");
      } else {
        setNotifPerm("denied");
      }
      return;
    }
    // Web fallback — FCM via service worker
    if(!("Notification" in window)){setNotifPerm("unsupported");return;}
    const result=await Notification.requestPermission();
    setNotifPerm(result);
    if(result==="granted"){
      const vapidKey=process.env.REACT_APP_FCM_VAPID_KEY;
      if(vapidKey&&myUid){
        messagingPromise.then(async messaging=>{
          if(!messaging) return;
          const token=await getToken(messaging,{vapidKey}).catch(()=>null);
          if(token) setDoc(doc(db,"users",myUid),{fcmToken:token},{merge:true}).catch(()=>{});
        });
      }
      try{
        const reg=await navigator.serviceWorker.ready;
        reg.showNotification("You're all set! 🎉",{
          body:"gather gives you a quiet nudge when it's a good time to reach out.",
          icon:"/icon.svg",
          tag:"gather-welcome",
        });
      }catch(e){
        new Notification("You're all set! 🎉",{body:"gather gives you a quiet nudge when it's a good time to reach out.",icon:"/icon.svg"});
      }
    }
  }

  const upcomingPlans=(plans||[]).filter(p=>p.status!=="cancelled"&&p.startISO&&new Date(p.startISO)>new Date()).sort((a,b)=>new Date(a.startISO)-new Date(b.startISO)).slice(0,3);

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>



      {/* Getting started checklist — show until all three steps complete */}
      {contacts.length>0&&(contacts.length<3||!myProfile.calendarConnected||!(myProfile.hobbies?.length>0))&&(
        <div style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:14,padding:"16px 18px"}}>
          <p style={{fontFamily:C.display,fontSize:14,fontWeight:700,color:C.ink,marginBottom:10}}>A few things that make gather work better</p>
          <div style={{display:"flex",flexDirection:"column",gap:7}}>
            {[
              {done:contacts.length>=3, label:"Add 3 or more people", sub:`${contacts.length} of 3 added`, action:()=>onTabChange("contacts")},
              {done:!!myProfile.calendarConnected, label:"Connect your calendar", sub:"Free/busy only — your events stay private", action:()=>onTabChange("profile")},
              {done:!!(myProfile.hobbies?.length>0), label:"Set your interests", sub:"Get better activity ideas", action:()=>onTabChange("profile")},
            ].map((s,i)=>(
              <div key={i} onClick={s.done?undefined:s.action}
                style={{display:"flex",alignItems:"center",gap:11,padding:"9px 12px",background:s.done?C.greenSoft:C.tint,borderRadius:9,border:`1px solid ${s.done?C.green+"28":C.line}`,cursor:s.done?"default":"pointer",transition:"background .15s"}}>
                <div style={{width:20,height:20,borderRadius:"50%",background:s.done?C.green:"#fff",border:`2px solid ${s.done?C.green:C.lineM}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
                  {s.done&&<span style={{color:"#fff",fontSize:10,fontWeight:700,lineHeight:1}}>✓</span>}
                </div>
                <div style={{flex:1}}>
                  <p style={{fontSize:13,fontWeight:s.done?400:500,color:s.done?C.green:C.ink,fontFamily:C.body,textDecoration:s.done?"line-through":"none",opacity:s.done?.7:1}}>{s.label}</p>
                  {!s.done&&<p style={{fontSize:11,color:C.ink3,fontFamily:C.body,marginTop:1}}>{s.sub}</p>}
                </div>
                {!s.done&&<span style={{color:C.ink3,fontSize:13,flexShrink:0}}>→</span>}
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Coming up — upcoming plans */}
      {upcomingPlans.length>0&&(
        <div>
          <p style={{fontSize:13,fontWeight:700,color:C.ink,fontFamily:C.display,marginBottom:10}}>Coming up</p>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {upcomingPlans.map(p=>(
              <div key={p.id} onClick={()=>p.chatId&&onOpenChat&&onOpenChat(p.chatId,p.groupName,p.participants)} style={{cursor:p.chatId&&onOpenChat?"pointer":"default"}}>
                <PlanCard plan={p} myUid={myUid} compact onRsvp={rsvp=>onRsvpPlan&&onRsvpPlan(p.id,rsvp)}/>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ✨ Weekend Spark — proactive gathering suggestion */}
      {(sparkLoading||spark)&&(
        <div className="p" style={{background:"linear-gradient(135deg,#4f46e5,#7c3aed)",borderRadius:16,padding:"18px 20px",color:"#fff",position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:-16,right:-16,width:100,height:100,borderRadius:"50%",background:"rgba(255,255,255,.07)"}}/>
          <div style={{position:"absolute",bottom:-24,left:8,width:70,height:70,borderRadius:"50%",background:"rgba(255,255,255,.04)"}}/>
          {sparkLoading?(
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span className="sp" style={{borderColor:"rgba(255,255,255,.3)",borderTopColor:"#fff",width:16,height:16,flexShrink:0}}/>
              <p style={{fontSize:13,fontFamily:C.body,color:"rgba(255,255,255,.8)"}}>Looking for a good time to gather…</p>
            </div>
          ):spark?(()=>{
            const slot=spark.slots[sparkIdx]||spark.slots[0];
            const hasWeekend=spark.slots.some(s=>s.dayObj.getDay()===6||s.dayObj.getDay()===0);
            // Recompute message — group or individuals
            const msgNames=sparkPeople.map(c=>c.name.split(" ")[0]);
            const liveMsg=sparkGroup
              ?`Hey ${sparkGroup.name}! Anyone free ${slot.dayStr.toLowerCase()} ${slot.window} for ${slot.activity}?${spark.seasonPhrase||""}`
              :sparkPeople.length===0?""
              :`Hey${msgNames.length===1?` ${msgNames[0]}`:""}! Anyone free ${slot.dayStr.toLowerCase()} ${slot.window} for ${slot.activity}?${spark.seasonPhrase||""}`;
            // Picker: contacts not already selected + groups
            const pickerContacts=contacts
              .filter(c=>c.name&&!sparkPeople.some(p=>p.id===c.id)&&c.name.toLowerCase().includes(sparkSearch.toLowerCase()))
              .slice(0,8);
            const pickerGroups=groups
              .filter(g=>g.name.toLowerCase().includes(sparkSearch.toLowerCase())&&g.contactIds?.length>0)
              .slice(0,4);
            return(
            <>
              <p style={{fontSize:11,fontWeight:600,opacity:.7,marginBottom:10,fontFamily:C.body,letterSpacing:".07em",textTransform:"uppercase"}}>
                ✨ {hasWeekend?"This weekend":"Good times ahead"}
              </p>

              {/* People / group pills + picker */}
              <div style={{display:"flex",flexWrap:"wrap",gap:6,alignItems:"center",marginBottom:12}}>
                {sparkGroup?(
                  <button onClick={()=>{setSparkGroup(null);setSparkPeople(spark.pool||[]);}}
                    style={{display:"flex",alignItems:"center",gap:6,background:"rgba(255,255,255,.18)",border:"1.5px solid rgba(255,255,255,.4)",borderRadius:20,padding:"5px 10px 5px 7px",cursor:"pointer",fontFamily:C.body}}>
                    <span style={{fontSize:15}}>{sparkGroup.emoji}</span>
                    <span style={{fontSize:12,color:"#fff",fontWeight:600,whiteSpace:"nowrap"}}>{sparkGroup.name}</span>
                    <span style={{fontSize:11,color:"rgba(255,255,255,.5)",lineHeight:1}}>×</span>
                  </button>
                ):(
                  <>
                    {sparkPeople.map(c=>(
                      <button key={c.id} onClick={()=>{setSparkPeople(p=>p.filter(x=>x.id!==c.id));setSparkPickerOpen(false);}}
                        style={{display:"flex",alignItems:"center",gap:5,background:"rgba(255,255,255,.15)",border:"1.5px solid rgba(255,255,255,.3)",borderRadius:20,padding:"4px 8px 4px 5px",cursor:"pointer",fontFamily:C.body}}>
                        <div style={{width:20,height:20,borderRadius:"50%",background:avBg(c.name),display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:"#fff",flexShrink:0}}>{initials(c.name)}</div>
                        <span style={{fontSize:12,color:"#fff",fontWeight:500,whiteSpace:"nowrap"}}>{c.name.split(" ")[0]}</span>
                        <span style={{fontSize:11,color:"rgba(255,255,255,.5)",lineHeight:1}}>×</span>
                      </button>
                    ))}
                  </>
                )}
                {!sparkGroup&&(
                  <button onClick={()=>{setSparkPickerOpen(o=>!o);setSparkSearch("");}}
                    style={{width:28,height:28,borderRadius:"50%",background:"rgba(255,255,255,.12)",border:"1.5px dashed rgba(255,255,255,.35)",cursor:"pointer",color:"rgba(255,255,255,.7)",fontSize:18,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    +
                  </button>
                )}
              </div>

              {/* Picker dropdown */}
              {sparkPickerOpen&&(
                <div style={{background:"rgba(30,27,75,.85)",backdropFilter:"blur(12px)",borderRadius:10,padding:"8px",marginBottom:12,border:"1.5px solid rgba(255,255,255,.15)"}}>
                  <input
                    autoFocus
                    value={sparkSearch}
                    onChange={e=>setSparkSearch(e.target.value)}
                    placeholder="Search people or groups…"
                    style={{width:"100%",background:"rgba(255,255,255,.1)",border:"none",borderRadius:6,padding:"7px 10px",color:"#fff",fontSize:12,outline:"none",fontFamily:C.body}}
                  />
                  {pickerGroups.length>0&&(
                    <div style={{marginTop:6,marginBottom:4}}>
                      <p style={{fontSize:10,fontWeight:600,color:"rgba(255,255,255,.4)",textTransform:"uppercase",letterSpacing:".06em",padding:"2px 4px 4px",margin:0}}>Groups</p>
                      {pickerGroups.map(g=>(
                        <button key={g.id}
                          onClick={()=>{
                            setSparkGroup(g);
                            setSparkPeople(contacts.filter(c=>g.contactIds.includes(c.id)));
                            setSparkSearch(""); setSparkPickerOpen(false);
                          }}
                          style={{width:"100%",display:"flex",alignItems:"center",gap:8,padding:"7px 4px",background:"none",border:"none",cursor:"pointer",fontFamily:C.body,borderRadius:6}}>
                          <div style={{width:26,height:26,borderRadius:8,background:"rgba(255,255,255,.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>{g.emoji}</div>
                          <div style={{textAlign:"left"}}>
                            <span style={{fontSize:13,color:"#fff",display:"block"}}>{g.name}</span>
                            <span style={{fontSize:11,color:"rgba(255,255,255,.4)"}}>{g.contactIds?.length} {g.contactIds?.length===1?"person":"people"}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {pickerContacts.length>0&&(
                    <div style={{marginTop:4}}>
                      {pickerGroups.length>0&&<p style={{fontSize:10,fontWeight:600,color:"rgba(255,255,255,.4)",textTransform:"uppercase",letterSpacing:".06em",padding:"2px 4px 4px",margin:0}}>People</p>}
                      {pickerContacts.map(c=>(
                        <button key={c.id}
                          onClick={()=>{setSparkPeople(p=>[...p,c]);setSparkGroup(null);setSparkSearch("");setSparkPickerOpen(false);}}
                          style={{width:"100%",display:"flex",alignItems:"center",gap:8,padding:"7px 4px",background:"none",border:"none",cursor:"pointer",fontFamily:C.body,borderRadius:6}}>
                          <div style={{width:26,height:26,borderRadius:"50%",background:avBg(c.name),display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#fff",flexShrink:0}}>{initials(c.name)}</div>
                          <span style={{fontSize:13,color:"#fff",textAlign:"left"}}>{c.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {pickerGroups.length===0&&pickerContacts.length===0&&(
                    <p style={{fontSize:12,color:"rgba(255,255,255,.4)",fontFamily:C.body,padding:"8px 4px 2px"}}>No matches</p>
                  )}
                </div>
              )}

              {/* Day picker row */}
              <div style={{display:"flex",gap:7,marginBottom:14,overflowX:"auto",paddingBottom:2,WebkitOverflowScrolling:"touch",scrollbarWidth:"none",msOverflowStyle:"none"}}>
                {spark.slots.map((s,i)=>{
                  const active=i===sparkIdx;
                  return(
                    <button key={i} onClick={()=>setSparkIdx(i)}
                      style={{flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"8px 12px",borderRadius:10,cursor:"pointer",fontFamily:C.body,
                        background:active?"rgba(255,255,255,.22)":"rgba(255,255,255,.09)",
                        border:`1.5px solid ${active?"rgba(255,255,255,.5)":"rgba(255,255,255,.15)"}`,
                        transition:"background .15s"}}>
                      <span style={{fontSize:18,lineHeight:1}}>📅</span>
                      <span style={{fontSize:11,fontWeight:600,color:"#fff",whiteSpace:"nowrap"}}>{s.dayStr.split(",")[0]}</span>
                      <span style={{fontSize:10,color:"rgba(255,255,255,.6)"}}>{s.window}</span>
                    </button>
                  );
                })}
              </div>

              <p style={{fontSize:13,color:"rgba(255,255,255,.85)",fontFamily:C.body,marginBottom:14,lineHeight:1.55}}>
                {`${slot.dayStr} ${slot.window} looks free — good time for ${slot.activity}.`}
              </p>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {sparkGroup?(
                  // Group selected — always open the in-app group chat
                  <button onClick={()=>{
                    if(onOpenChat){
                      const members=contacts.filter(c=>sparkGroup.contactIds.includes(c.id));
                      const gatherMembers=members.filter(c=>c.gatherId).map(c=>c.gatherId);
                      const participants=[myUid,...gatherMembers];
                      const offPlatform=members.filter(c=>!c.gatherId);
                      onOpenChat("group_"+sparkGroup.id,`${sparkGroup.emoji} ${sparkGroup.name}`,participants,liveMsg,offPlatform);
                    }
                  }}
                    style={{background:"rgba(255,255,255,.22)",border:"1.5px solid rgba(255,255,255,.4)",borderRadius:9,padding:"9px 18px",cursor:"pointer",color:"#fff",fontFamily:C.body,fontSize:13,fontWeight:600,backdropFilter:"blur(8px)",display:"flex",alignItems:"center",gap:6}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    Float the idea
                  </button>
                ):(
                  // Individual people — prefer in-app chat, fall back to SMS
                  <>
                    {sparkPeople.some(c=>c.gatherId)&&onOpenChat?(
                      <button disabled={sparkPeople.length===0} onClick={()=>{
                        const gatherPeople=sparkPeople.filter(c=>c.gatherId);
                        if(gatherPeople.length===1){
                          onOpenChat(`1:1_${[myUid,gatherPeople[0].gatherId].sort().join("_")}`,gatherPeople[0].name,[myUid,gatherPeople[0].gatherId],liveMsg);
                        } else {
                          // multi-person — copy message, direct to reach tab
                          navigator.clipboard?.writeText(liveMsg);
                          showToast("Message copied — open a group chat to send it");
                        }
                      }}
                        style={{background:"rgba(255,255,255,.22)",border:"1.5px solid rgba(255,255,255,.4)",borderRadius:9,padding:"9px 18px",cursor:"pointer",color:"#fff",fontFamily:C.body,fontSize:13,fontWeight:600,backdropFilter:"blur(8px)",display:"flex",alignItems:"center",gap:6}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        Float the idea
                      </button>
                    ):(
                      <button disabled={sparkPeople.length===0} onClick={()=>{
                        const nums=sparkPeople.map(c=>c.phone).filter(Boolean);
                        if(nums.length>0) window.open(`sms:${nums.join(",")}?&body=${encodeURIComponent(liveMsg)}`,"_self");
                        else{navigator.clipboard?.writeText(liveMsg);showToast("Copied to clipboard!");}
                      }}
                        style={{background:sparkPeople.length===0?"rgba(255,255,255,.08)":"rgba(255,255,255,.18)",border:"1.5px solid rgba(255,255,255,.3)",borderRadius:9,padding:"9px 18px",cursor:sparkPeople.length===0?"default":"pointer",color:sparkPeople.length===0?"rgba(255,255,255,.35)":"#fff",fontFamily:C.body,fontSize:13,fontWeight:600,backdropFilter:"blur(8px)"}}>
                        Float it via text 💬
                      </button>
                    )}
                  </>
                )}
              </div>
              <p style={{fontSize:11,color:"rgba(255,255,255,.5)",fontFamily:C.body,marginTop:10,lineHeight:1.5}}>
                This suggests a time — you confirm it works for everyone before anything is locked in.
              </p>
            </>
            );
          })():null}
        </div>
      )}

      {/* Calendar window — shows when contacts also have calendar data */}
      {!spark&&!sparkLoading&&(insightLoading||insight)&&(
        <div className="p" style={{background:"linear-gradient(135deg,#4f46e5,#7c3aed)",borderRadius:16,padding:"18px 20px",color:"#fff",position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:-16,right:-16,width:100,height:100,borderRadius:"50%",background:"rgba(255,255,255,.07)"}}/>
          {insightLoading?(
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span className="sp" style={{borderColor:"rgba(255,255,255,.3)",borderTopColor:"#fff",width:16,height:16,flexShrink:0}}/>
              <p style={{fontSize:13,fontFamily:C.body,color:"rgba(255,255,255,.8)"}}>Finding a good time…</p>
            </div>
          ):insight?.type==="group"?(
            <>
              <p style={{fontSize:11,fontWeight:600,opacity:.7,marginBottom:8,fontFamily:C.body,letterSpacing:".07em",textTransform:"uppercase"}}>✨ You're both free</p>
              <p style={{fontFamily:C.display,fontSize:20,fontWeight:700,color:"#fff",marginBottom:4,lineHeight:1.3}}>{insight.group.emoji} {insight.group.name}</p>
              <p style={{fontSize:13,color:"rgba(255,255,255,.88)",fontFamily:C.body,marginBottom:14,lineHeight:1.5}}>{insight.windows[0]}{insight.windows[1]&&` · ${insight.windows[1]}`}</p>
              <button onClick={()=>onTabChange("reach")} style={{background:"rgba(255,255,255,.18)",border:"1.5px solid rgba(255,255,255,.3)",borderRadius:9,padding:"8px 16px",cursor:"pointer",color:"#fff",fontFamily:C.body,fontSize:12,fontWeight:600,backdropFilter:"blur(8px)"}}>Float the idea →</button>
              <p style={{fontSize:11,color:"rgba(255,255,255,.5)",fontFamily:C.body,marginTop:10,lineHeight:1.5}}>This suggests a time — you still confirm it works before anything is locked in.</p>
            </>
          ):insight?.type==="contact"?(
            <>
              <p style={{fontSize:11,fontWeight:600,opacity:.7,marginBottom:8,fontFamily:C.body,letterSpacing:".07em",textTransform:"uppercase"}}>✨ You're both free</p>
              <p style={{fontFamily:C.display,fontSize:20,fontWeight:700,color:"#fff",marginBottom:4,lineHeight:1.3}}>You & {insight.contact.name}</p>
              <p style={{fontSize:13,color:"rgba(255,255,255,.88)",fontFamily:C.body,marginBottom:14,lineHeight:1.5}}>{insight.windows[0]}{insight.windows[1]&&` · ${insight.windows[1]}`}</p>
              <button onClick={()=>onTabChange("reach")} style={{background:"rgba(255,255,255,.18)",border:"1.5px solid rgba(255,255,255,.3)",borderRadius:9,padding:"8px 16px",cursor:"pointer",color:"#fff",fontFamily:C.body,fontSize:12,fontWeight:600,backdropFilter:"blur(8px)"}}>Float the idea →</button>
              <p style={{fontSize:11,color:"rgba(255,255,255,.5)",fontFamily:C.body,marginTop:10,lineHeight:1.5}}>This suggests a time — you still confirm it works before anything is locked in.</p>
            </>
          ):null}
        </div>
      )}

      {/* Your crews */}
      {groups.length>0&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <p style={{fontFamily:C.display,fontSize:15,fontWeight:700,color:C.ink}}>Your crews</p>
            <button onClick={()=>onTabChange("reach")} style={{background:"none",border:"none",cursor:"pointer",color:C.blue,fontFamily:C.body,fontSize:13,fontWeight:600,padding:0}}>Manage →</button>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {groups.map(g=>{
              const groupPlan=(plans||[]).find(p=>p.status!=="cancelled"&&p.startISO&&new Date(p.startISO)>new Date()&&(p.participants?.includes(myUid)||p.groupName===g.name));
              const members=contacts.filter(c=>g.contactIds?.includes(c.id));
              const memberCount=g.memberUids?.length>0?g.memberUids.length:(members.length+1);
              return (
                <div key={g.id} style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:14,padding:"14px 16px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:44,height:44,borderRadius:12,background:C.tint,border:`1.5px solid ${C.line}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>{g.emoji}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontSize:14,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:2}}>{g.name}</p>
                      <p style={{fontSize:12,color:C.ink3,fontFamily:C.body}}>
                        {memberCount} {memberCount===1?"person":"people"}
                        {groupPlan&&<span style={{color:C.green,fontWeight:500}}> · plan coming up ✓</span>}
                      </p>
                    </div>
                    <button onClick={()=>{
                      if(!onOpenChat) return;
                      const resolvedUids=g.memberUids?.filter(uid=>uid!==myUid)||members.filter(c=>c.gatherId).map(c=>c.gatherId);
                      const offPlatform=members.filter(c=>!c.gatherId);
                      onOpenChat("group_"+g.id,`${g.emoji} ${g.name}`,[myUid,...resolvedUids],"",offPlatform);
                    }} style={{background:C.blueSoft,border:`1.5px solid ${C.blueBdr}`,borderRadius:9,padding:"7px 14px",cursor:"pointer",color:C.blue,fontFamily:C.body,fontSize:13,fontWeight:600,flexShrink:0}}>
                      Chat →
                    </button>
                  </div>
                  {groupPlan&&(
                    <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${C.line}`,display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}}
                      onClick={()=>groupPlan.chatId&&onOpenChat&&onOpenChat(groupPlan.chatId,groupPlan.groupName,groupPlan.participants)}>
                      <p style={{fontSize:13,color:C.ink2,fontFamily:C.body}}>{groupPlan.title||groupPlan.activity} · {new Date(groupPlan.startISO).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}</p>
                      <Pill color={C.green} soft={C.greenSoft}>{Object.values(groupPlan.rsvps||{}).filter(v=>v==="in").length} in</Pill>
                    </div>
                  )}
                  {!groupPlan&&onOpenChat&&(()=>{
                    const resolvedUids=g.memberUids?.filter(uid=>uid!==myUid)||members.filter(c=>c.gatherId).map(c=>c.gatherId);
                    const offPlatform=members.filter(c=>!c.gatherId);
                    return(
                      <button onClick={()=>onOpenChat("group_"+g.id,`${g.emoji} ${g.name}`,[myUid,...resolvedUids],"",offPlatform)}
                        style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${C.line}`,background:"none",border:"none",cursor:"pointer",fontFamily:C.body,fontSize:13,color:C.ink3,padding:"10px 0 0",textAlign:"left",width:"100%"}}>
                        📅 When's everyone free? →
                      </button>
                    );
                  })()}
                </div>
              );
            })}
            <button onClick={()=>onTabChange("reach")} style={{background:"none",border:`1.5px dashed ${C.lineM}`,borderRadius:10,padding:"11px 16px",cursor:"pointer",fontFamily:C.body,fontSize:13,color:C.ink3,display:"flex",alignItems:"center",gap:6,width:"100%",justifyContent:"center",boxSizing:"border-box"}}>
              + Create a crew
            </button>
          </div>
        </div>
      )}

      {/* Prompt to create first crew */}
      {contacts.length>0&&groups.length===0&&(
        <div style={{background:C.tint,border:`1.5px dashed ${C.lineM}`,borderRadius:14,padding:"20px 20px",textAlign:"center"}}>
          <p style={{fontSize:22,marginBottom:8}}>👥</p>
          <p style={{fontSize:14,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:4}}>Group your people</p>
          <p style={{fontSize:13,color:C.ink3,fontFamily:C.body,lineHeight:1.6,marginBottom:14}}>Tennis friends, family, coworkers — create a crew and make plans together.</p>
          <button onClick={()=>onTabChange("reach")} style={{background:C.blue,color:"#fff",border:"none",borderRadius:9,padding:"9px 20px",cursor:"pointer",fontFamily:C.body,fontSize:13,fontWeight:600}}>Create your first crew →</button>
        </div>
      )}

      {/* Birthdays */}
      {bdays.length>0&&(
        <Card>
          <p style={{fontSize:15,fontWeight:700,color:C.ink,fontFamily:C.display,marginBottom:12}}>🎂 Birthdays coming up</p>
          <div style={{display:"flex",flexDirection:"column",gap:7}}>
            {bdays.map(({c,bs})=>(
              <div key={c.id} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 13px",background:bs.days<=3?C.roseSoft:C.tint,borderRadius:10,border:`1.5px solid ${bs.days<=3?C.rose+"28":C.line}`}}>
                <Av name={c.name} size={34}/>
                <div style={{flex:1}}>
                  <p style={{fontSize:13,fontWeight:500,color:C.ink,fontFamily:C.body}}>{c.name}</p>
                  <p style={{fontSize:13,color:C.ink3,fontFamily:C.body}}>{bs.days===0?"Today!":bs.label} · Turning {(getAge(c.birthday)||0)+1}</p>
                </div>
                <Btn onClick={()=>onTabChange("reach")} variant={bs.days<=3?"rose":"ghost"} small>{bs.days===0?"Send wishes":"Plan ahead"}</Btn>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Calendar connect prompt */}
      {!myProfile?.calendarConnected&&contacts.length>0&&(
        <div style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:14,padding:"14px 16px",display:"flex",gap:12,alignItems:"flex-start"}}>
          <div style={{fontSize:22,lineHeight:1,flexShrink:0,marginTop:2}}>📅</div>
          <div style={{flex:1}}>
            <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:3}}>See when everyone's actually free</p>
            <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,lineHeight:1.6,marginBottom:8}}>Gather finds the gaps — real times that work for your whole crew. Your calendar stays completely private: we only see free or busy, never your event titles or details.</p>
            <Btn onClick={()=>onTabChange("profile")} variant="ghost" small>Connect in Profile →</Btn>
          </div>
        </div>
      )}

      {/* Notification permission */}
      {notifPerm==="default"&&(
        <div style={{background:C.blueSoft,border:`1.5px solid ${C.blueBdr}`,borderRadius:14,padding:"16px 18px",display:"flex",gap:14,alignItems:"flex-start"}}>
          <div style={{fontSize:28,lineHeight:1,flexShrink:0,marginTop:2}}>🔔</div>
          <div style={{flex:1}}>
            <p style={{fontSize:14,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:4}}>Get a nudge when a plan is forming</p>
            <p style={{fontSize:13,color:C.ink3,fontFamily:C.body,lineHeight:1.6,marginBottom:12}}>We'll let you know when someone's free or a plan needs your RSVP.</p>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <Btn onClick={enableNotifications} variant="soft" small>Turn on</Btn>
              <Btn onClick={()=>setNotifPerm("dismissed")} variant="ghost" small>Maybe later</Btn>
            </div>
          </div>
        </div>
      )}
      {notifPerm==="denied"&&(
        <div style={{background:C.tint,border:`1.5px solid ${C.line}`,borderRadius:14,padding:"13px 16px"}}>
          <p style={{fontSize:13,color:C.ink3,fontFamily:C.body,lineHeight:1.6}}>🔕 Notifications blocked — open browser settings to allow them for this site.</p>
        </div>
      )}

      {/* Empty state — no contacts yet */}
      {contacts.length===0&&(
        <div style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:16,padding:"28px 24px",textAlign:"center",boxShadow:"0 2px 12px rgba(37,99,235,.06)"}}>
          <div style={{fontSize:40,marginBottom:14}}>✨</div>
          <p style={{fontFamily:C.display,fontSize:22,fontWeight:700,color:C.ink,marginBottom:8,lineHeight:1.3}}>Who's someone you've been meaning to catch up with?</p>
          <p style={{fontSize:13,color:C.ink3,lineHeight:1.7,maxWidth:260,margin:"0 auto 22px",fontFamily:C.body}}>Just one person. That's all it takes.</p>
          <button onClick={()=>onTabChange("contacts")} style={{background:C.blue,color:"#fff",border:"none",borderRadius:12,padding:"13px 28px",cursor:"pointer",fontFamily:C.body,fontSize:14,fontWeight:700,boxShadow:"0 4px 14px rgba(37,99,235,.28)"}}>
            Add them now →
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Inline partner invite button (used in Playdates) ───────────────────────── */
function InvitePartnerBtn({myUid}) {
  const [sent,setSent]=useState(false);
  function send(){
    const url=myUid?`https://heygather.app?ref=${myUid}&partner=1`:"https://heygather.app";
    const msg=`Hey! I've been using gather to keep our family organised — kids, playdates, staying in touch. Join me and we'll share everything automatically! ${url}`;
    window.open(`sms:?&body=${encodeURIComponent(msg)}`,"_self");
    setSent(true); setTimeout(()=>setSent(false),3000);
  }
  return sent
    ? <p style={{fontSize:13,color:C.green,fontWeight:600,fontFamily:C.body}}>✓ Invite sent!</p>
    : <Btn onClick={send} variant="soft" small>💬 Send partner invite</Btn>;
}

/* ── Invite Row (inside contact detail) ────────────────────────────────────── */
function InviteRow({contact,myUid,isPartnerInvite}) {
  const [open,setOpen]=useState(false);
  const [sent,setSent]=useState(false);
  const firstName=contact.name?.split(" ")[0]||contact.name||"";

  function buildUrl(partner){
    const base="https://heygather.app";
    if(!myUid) return base;
    return partner ? `${base}?ref=${myUid}&partner=1` : `${base}?ref=${myUid}`;
  }

  function send(partner){
    const url=buildUrl(partner);
    const msg=partner
      ? `Hey ${firstName}! I've been using gather to keep our family organised — kids, playdates, staying in touch. Join me and we'll share everything automatically! ${url}`
      : `Hey ${firstName}! I've been using gather to stay in better touch with the people who matter to me. Join me at Hey Gather! ${url}`;
    const smsUrl=contact.phone?.trim()
      ? `sms:${contact.phone.replace(/\s/g,"")}?&body=${encodeURIComponent(msg)}`
      : `sms:?&body=${encodeURIComponent(msg)}`;
    window.open(smsUrl,"_self");
    setSent(true);
    setTimeout(()=>{setOpen(false);setSent(false);},2000);
  }

  return (
    <div style={{marginTop:4}}>
      {!open?(
        <button onClick={()=>setOpen(true)} style={{background:"none",border:`1.5px solid ${C.line}`,borderRadius:8,padding:"8px 14px",cursor:"pointer",fontFamily:C.body,fontSize:12,color:C.ink2,display:"flex",alignItems:"center",gap:7,width:"100%",justifyContent:"center"}}>
          <span style={{fontSize:14}}>💌</span> Invite {firstName} to gather
        </button>
      ):sent?(
        <p style={{fontSize:13,color:C.green,fontWeight:600,fontFamily:C.body,textAlign:"center",padding:"8px 0"}}>✓ Invite sent!</p>
      ):(
        <div style={{background:C.tint,borderRadius:10,padding:"12px 14px",border:`1.5px solid ${C.line}`}}>
          <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:4}}>Invite {firstName} to gather</p>
          <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginBottom:12,lineHeight:1.6}}>
            When {firstName} joins using your link, they'll automatically be added to each other's contacts.
          </p>

          {/* Standard invite */}
          <div style={{background:C.white,borderRadius:8,padding:"10px 12px",border:`1.5px solid ${C.line}`,marginBottom:8}}>
            <p style={{fontSize:12,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:4}}>Friend or family invite</p>
            <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginBottom:8,fontStyle:"italic",lineHeight:1.6}}>
              "Hey {firstName}! I've been using gather to stay in better touch with the people who matter to me. Join me at Hey Gather!"
            </p>
            <Btn onClick={()=>send(false)} small>💬 Send invite</Btn>
          </div>

          {/* Partner invite */}
          <div style={{background:C.blueSoft,borderRadius:8,padding:"10px 12px",border:`1.5px solid ${C.blueBdr}`,marginBottom:10}}>
            <p style={{fontSize:12,fontWeight:600,color:C.blue,fontFamily:C.body,marginBottom:4}}>💑 Partner / co-parent invite</p>
            <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginBottom:8,lineHeight:1.6}}>
              Shares your kids and playdate info automatically once they join.
            </p>
            <Btn onClick={()=>send(true)} variant="soft" small>💬 Send partner invite</Btn>
          </div>

          <Btn onClick={()=>setOpen(false)} variant="ghost" small>Cancel</Btn>
        </div>
      )}
    </div>
  );
}

/* ── In-app messaging between Gather users ─────────────────────────────────── */
function GatherChat({myUid, myName, contactGatherId, contactName, onLog, onOpenFull}) {
  const [msgs, setMsgs]     = useState([]);
  const [text, setText]     = useState("");
  const [sending, setSending] = useState(false);
  const [showLogInput, setShowLogInput] = useState(false);
  const [logNote, setLogNote] = useState("");
  const [logSaved, setLogSaved] = useState(false);
  const bottomRef = useRef();
  const chatId = [myUid, contactGatherId].sort().join("_");

  useEffect(()=>{
    if(!myUid||!contactGatherId) return;
    const q = query(collection(db,"chats",chatId,"messages"));
    const unsub = onSnapshot(q, snap=>{
      const all = snap.docs.map(d=>({id:d.id,...d.data()}))
        .sort((a,b)=>new Date(a.createdAt)-new Date(b.createdAt));
      setMsgs(all);
      setTimeout(()=>bottomRef.current?.scrollIntoView({behavior:"smooth"}),50);
    });
    return unsub;
  },[chatId,myUid,contactGatherId]);

  async function send() {
    const t = text.trim();
    if(!t||sending) return;
    setSending(true); setText("");
    await addDoc(collection(db,"chats",chatId,"messages"),{
      from:myUid, text:t, createdAt:new Date().toISOString(), read:false, reactions:{},
    });
    await setDoc(doc(db,"chats",chatId),{
      participants:[myUid,contactGatherId],
      lastMessage:t, lastMessageAt:new Date().toISOString(), lastMessageFrom:myUid,
    },{merge:true});
    setSending(false);
    getDoc(doc(db,"users",contactGatherId)).then(snap=>{
      const fcmToken=snap.data()?.fcmToken;
      if(fcmToken) fetch("/api/notify-chat",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({fcmToken, senderName:myName||"Someone on Gather", message:t}),
      }).catch(()=>{});
    }).catch(()=>{});
  }

  const preview = msgs.slice(-3);

  return (
    <div style={{marginTop:4}}>
      {/* Open full chat button */}
      {onOpenFull&&(
        <button onClick={()=>onOpenFull(chatId,contactName,[myUid,contactGatherId])}
          style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",background:C.tint,border:`1.5px solid ${C.line}`,borderRadius:10,padding:"9px 13px",cursor:"pointer",marginBottom:8,fontFamily:C.body}}>
          <span style={{fontSize:13,fontWeight:600,color:C.ink}}>💬 Chat with {contactName.split(" ")[0]}</span>
          <span style={{fontSize:12,color:C.blue,fontWeight:600}}>Open →</span>
        </button>
      )}
      {/* Preview thread (last 3 messages) */}
      {preview.length>0&&(
        <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:8,padding:"2px 0"}}>
          {preview.map(m=>{
            const isMe=m.from===myUid;
            return (
              <div key={m.id} style={{display:"flex",justifyContent:isMe?"flex-end":"flex-start"}}>
                <div style={{maxWidth:"78%",background:isMe?C.blue:C.tint,color:isMe?"#fff":C.ink,borderRadius:isMe?"14px 14px 3px 14px":"14px 14px 14px 3px",padding:"8px 12px",fontSize:13,fontFamily:C.body,lineHeight:1.55,border:isMe?"none":`1px solid ${C.line}`}}>
                  {m.text}
                  <div style={{fontSize:10,opacity:.6,marginTop:3,textAlign:isMe?"right":"left"}}>
                    {new Date(m.createdAt).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef}/>
        </div>
      )}
      {/* Quick compose */}
      <div style={{display:"flex",gap:7,alignItems:"flex-end"}}>
        <textarea
          value={text}
          onChange={e=>setText(e.target.value)}
          onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
          placeholder={`Message ${contactName.split(" ")[0]}…`}
          rows={2}
          style={{flex:1,resize:"none",border:`1.5px solid ${C.line}`,borderRadius:10,padding:"8px 11px",fontSize:13,fontFamily:C.body,color:C.ink,outline:"none",background:"#fff",lineHeight:1.5}}
        />
        <button onClick={send} disabled={!text.trim()||sending}
          style={{background:text.trim()?C.blue:"#e5e7eb",color:text.trim()?"#fff":C.ink3,border:"none",borderRadius:10,padding:"9px 14px",cursor:text.trim()?"pointer":"default",fontFamily:C.body,fontSize:13,fontWeight:600,flexShrink:0,transition:"all .15s"}}>
          {sending?"…":"Send"}
        </button>
      </div>

      {/* Log a plan from this chat */}
      {onLog&&(
        <div style={{marginTop:10}}>
          {logSaved?(
            <p style={{fontSize:12,color:C.green,fontFamily:C.body,fontWeight:600}}>✓ Plan logged</p>
          ):showLogInput?(
            <div style={{display:"flex",gap:6,alignItems:"center"}}>
              <input
                value={logNote}
                onChange={e=>setLogNote(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();if(logNote.trim()){onLog(logNote.trim());setLogNote("");setShowLogInput(false);setLogSaved(true);setTimeout(()=>setLogSaved(false),2000);}}}}
                placeholder="e.g. Planning coffee Saturday morning"
                autoFocus
                style={{flex:1,border:`1.5px solid ${C.line}`,borderRadius:8,padding:"7px 10px",fontSize:12,fontFamily:C.body,outline:"none",color:C.ink}}
              />
              <button onClick={()=>{if(logNote.trim()){onLog(logNote.trim());setLogNote("");setShowLogInput(false);setLogSaved(true);setTimeout(()=>setLogSaved(false),2000);}}} disabled={!logNote.trim()}
                style={{background:C.blue,color:"#fff",border:"none",borderRadius:8,padding:"7px 12px",fontSize:12,fontFamily:C.body,fontWeight:600,cursor:logNote.trim()?"pointer":"default",opacity:logNote.trim()?1:.4}}>Save</button>
              <button onClick={()=>{setShowLogInput(false);setLogNote("");}}
                style={{background:"none",border:"none",cursor:"pointer",fontSize:14,color:C.ink4,padding:"0 4px"}}>✕</button>
            </div>
          ):(
            <button onClick={()=>setShowLogInput(true)}
              style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.ink3,fontFamily:C.body,padding:0,display:"flex",alignItems:"center",gap:5}}>
              📌 Log a plan from this chat
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Shared helpers ─────────────────────────────────────────────────────────── */

function buildCalLink(startISO,endISO,name){
  const fmt=iso=>iso.replace(/[-:]/g,"").replace(/\.\d{3}/,"");
  const title=encodeURIComponent(name||"Hangout");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(startISO)}/${fmt(endISO)}`;
}

/* ── Plan card ──────────────────────────────────────────────────────────────── */

function PlanCard({plan,myUid,onRsvp,onCancel,compact=false,memberNames={}}){
  const rsvps=plan.rsvps||{};
  const inList=Object.entries(rsvps).filter(([,v])=>v==="in").map(([k])=>k);
  const outList=Object.entries(rsvps).filter(([,v])=>v==="out").map(([k])=>k);
  const pending=(plan.participants||[]).filter(uid=>!rsvps[uid]);
  const myRsvp=rsvps[myUid];
  const isOn=inList.length>=2;
  const isCancelled=plan.status==="cancelled";

  const start=plan.startISO?new Date(plan.startISO):null;
  const dateStr=start?start.toLocaleDateString("en-US",{weekday:"long",month:"short",day:"numeric"}):"";
  const timeStr=start?start.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"}):"";
  const calUrl=plan.startISO&&plan.endISO?buildCalLink(plan.startISO,plan.endISO,plan.title||plan.activity||"Hangout"):null;

  const statusColor=isCancelled?"#94a3b8":isOn?C.green:C.amber;
  const statusBg=isCancelled?"#f8fafc":isOn?"#f0fdf4":C.amberSoft;
  const statusLabel=isCancelled?"Cancelled":isOn?"It's on 🎉":"Tentative";

  function nameFor(uid){ return uid===myUid?"You":(memberNames[uid]||"Someone").split(" ")[0]; }

  return(
    <div style={{background:statusBg,border:`1.5px solid ${statusColor}${isOn?"55":"33"}`,borderRadius:12,padding:compact?"10px 12px":"14px 16px",display:"flex",flexDirection:"column",gap:compact?6:10}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:3}}>
            <span style={{fontSize:compact?15:18}}>{plan.groupEmoji||"📅"}</span>
            <p style={{margin:0,fontSize:compact?13:15,fontWeight:700,color:C.ink,fontFamily:C.display}}>{plan.title||plan.activity||"Hangout"}</p>
            <span style={{fontSize:10,fontWeight:600,color:statusColor,background:"#fff",border:`1px solid ${statusColor}44`,borderRadius:20,padding:"2px 8px",flexShrink:0}}>{statusLabel}</span>
          </div>
          {dateStr&&<p style={{margin:0,fontSize:12,color:C.ink2,fontFamily:C.body}}>{dateStr} · {timeStr}</p>}
        </div>
        {!compact&&onCancel&&!isCancelled&&(
          <button onClick={onCancel} style={{background:"none",border:"none",cursor:"pointer",color:C.ink4,fontSize:18,lineHeight:1,padding:0,flexShrink:0}}>×</button>
        )}
      </div>

      {/* Who's in roll-call */}
      {!compact&&(inList.length>0||outList.length>0||pending.length>0)&&(
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          {inList.length>0&&(
            <div style={{display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
              <span style={{fontSize:11,fontWeight:600,color:C.green,minWidth:40}}>✓ In</span>
              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                {inList.map(uid=>(
                  <div key={uid} style={{display:"flex",alignItems:"center",gap:4,background:C.greenSoft,borderRadius:20,padding:"3px 8px 3px 4px",border:`1px solid ${C.green}33`}}>
                    <Av name={nameFor(uid)} size={18}/>
                    <span style={{fontSize:11,fontWeight:600,color:C.green,fontFamily:C.body}}>{nameFor(uid)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {outList.length>0&&(
            <div style={{display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
              <span style={{fontSize:11,fontWeight:600,color:"#ef4444",minWidth:40}}>✕ Out</span>
              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                {outList.map(uid=>(
                  <div key={uid} style={{display:"flex",alignItems:"center",gap:4,background:"#fef2f2",borderRadius:20,padding:"3px 8px 3px 4px",border:"1px solid #fca5a533"}}>
                    <Av name={nameFor(uid)} size={18}/>
                    <span style={{fontSize:11,color:"#ef4444",fontFamily:C.body}}>{nameFor(uid)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {pending.length>0&&(
            <div style={{display:"flex",alignItems:"center",gap:7}}>
              <span style={{fontSize:11,color:C.ink4,minWidth:40}}>? Pending</span>
              <div style={{display:"flex",gap:4}}>
                {pending.map(uid=>(
                  <div key={uid} title={nameFor(uid)} style={{borderRadius:"50%",opacity:.55}}>
                    <Av name={nameFor(uid)} size={18}/>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Compact tally */}
      {compact&&(
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          {inList.length>0&&<span style={{fontSize:12,color:C.green,fontWeight:600,fontFamily:C.body}}>✓ {inList.length} in</span>}
          {outList.length>0&&<span style={{fontSize:12,color:"#ef4444",fontWeight:500,fontFamily:C.body}}>✕ {outList.length} out</span>}
          {pending.length>0&&<span style={{fontSize:12,color:C.ink4,fontFamily:C.body}}>{pending.length} pending</span>}
        </div>
      )}

      {/* RSVP buttons */}
      {!compact&&!isCancelled&&(
        <div style={{display:"flex",gap:7}}>
          <button onClick={()=>onRsvp&&onRsvp("in")}
            style={{flex:1,background:myRsvp==="in"?C.green:"#fff",color:myRsvp==="in"?"#fff":C.ink2,border:`1.5px solid ${myRsvp==="in"?C.green:C.line}`,borderRadius:8,padding:"8px",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:C.body,transition:"all .15s"}}>
            {myRsvp==="in"?"✓ I'm in":"I'm in"}
          </button>
          <button onClick={()=>onRsvp&&onRsvp("out")}
            style={{flex:1,background:myRsvp==="out"?"#ef4444":"#fff",color:myRsvp==="out"?"#fff":C.ink3,border:`1.5px solid ${myRsvp==="out"?"#ef4444":C.line}`,borderRadius:8,padding:"8px",cursor:"pointer",fontSize:13,fontFamily:C.body,transition:"all .15s"}}>
            {myRsvp==="out"?"✕ Can't make it":"Can't make it"}
          </button>
        </div>
      )}

      {/* Calendar link — shown when confirmed */}
      {isOn&&!isCancelled&&calUrl&&(
        <a href={calUrl} target="_blank" rel="noreferrer"
          style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:C.green,color:"#fff",borderRadius:8,padding:"9px",fontSize:13,fontWeight:600,textDecoration:"none",fontFamily:C.body}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" stroke="#fff" strokeWidth="2"/><path d="M16 2v4M8 2v4M3 10h18" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
          Add to calendar
        </a>
      )}
    </div>
  );
}

/* ── ChatView — full-screen messaging overlay ──────────────────────────────── */

function freeWindowSlots(myBusy, othersBusy, days){
  // Returns rich slot objects {str, startISO, endISO} for time proposals
  const allBusy=[...(myBusy||[]),...(othersBusy||[])];
  const now=new Date();
  const windows=[
    {label:"morning",  sh:"9am–12pm", start:9,  end:12},
    {label:"afternoon",sh:"12pm–5pm", start:12, end:17},
    {label:"evening",  sh:"5pm–8pm",  start:17, end:20},
  ];
  const slots=[];
  for(let d=0;d<Math.max(days,28)&&slots.length<6;d++){
    const date=new Date(now);
    date.setDate(date.getDate()+d);
    const dayStr=date.toLocaleDateString("en-US",{weekday:"long",month:"short",day:"numeric"});
    for(const w of windows){
      const wStart=new Date(date); wStart.setHours(w.start,0,0,0);
      const wEnd=new Date(date);   wEnd.setHours(w.end,0,0,0);
      if(wStart<=now) continue;
      const overlap=allBusy.reduce((sum,b)=>{
        const ov=Math.min(new Date(b.end).getTime(),wEnd.getTime())-Math.max(new Date(b.start).getTime(),wStart.getTime());
        return sum+Math.max(0,ov);
      },0);
      if(overlap<45*60*1000) slots.push({str:`${dayStr} ${w.label} (${w.sh})`,startISO:wStart.toISOString(),endISO:wEnd.toISOString()});
      if(slots.length>=6) break;
    }
  }
  return slots;
}

function memberAvail(busyBlocks,startISO,endISO){
  if(!busyBlocks||busyBlocks.length===0) return "unknown";
  const s=new Date(startISO),e=new Date(endISO);
  const busy=busyBlocks.some(b=>{
    const ov=Math.min(new Date(b.end),e)-Math.max(new Date(b.start),s);
    return ov>15*60*1000;
  });
  return busy?"busy":"free";
}

function quickWindows(){
  const now=new Date();
  const SLOTS=[
    {label:"Morning",   sh:"9am–12pm", start:9,  end:12},
    {label:"Afternoon", sh:"12pm–5pm", start:12, end:17},
    {label:"Evening",   sh:"5pm–9pm",  start:17, end:21},
  ];
  const wins=[];
  for(let d=0;d<4&&wins.length<8;d++){
    const date=new Date(now);
    date.setDate(date.getDate()+d);
    const dayLabel=d===0?"Today":d===1?"Tomorrow":date.toLocaleDateString("en-US",{weekday:"long"});
    for(const s of SLOTS){
      const start=new Date(date); start.setHours(s.start,0,0,0);
      const end=new Date(date);   end.setHours(s.end,0,0,0);
      if(end<=now) continue;
      wins.push({label:`${dayLabel} ${s.label}`,sub:s.sh,startISO:start.toISOString(),endISO:end.toISOString()});
    }
  }
  return wins;
}

function detectActivity(name){
  if(!name) return "";
  const lower=name.toLowerCase();
  const map=[["tennis","Tennis"],["golf","Golf"],["dinner","Dinner"],["lunch","Lunch"],["coffee","Coffee"],["drinks","Drinks"],["run","Running"],["gym","Gym"],["hike","Hike"],["hiking","Hike"],["football","Football"],["basketball","Basketball"],["swim","Swimming"],["yoga","Yoga"],["poker","Poker"],["book","Book club"],["brunch","Brunch"],["padel","Padel"],["squash","Squash"],["cycling","Cycling"],["climb","Climbing"]];
  for(const [kw,label] of map) if(lower.includes(kw)) return label;
  return "";
}

function ChatView({chatId,chatName,participants,myUid,myName,contacts,busyBlocks,othersBusyBlocks,initialText,plans,onCreatePlan,onRsvpPlan,onCancelPlan,onClose,onFindTime,offPlatformContacts=[]}){
  const [msgs,setMsgs]=useState([]);
  const [text,setText]=useState(initialText||"");
  const [sending,setSending]=useState(false);
  const [showTimePicker,setShowTimePicker]=useState(false);
  const [timeSlots,setTimeSlots]=useState([]);
  const [allOthersBusy,setAllOthersBusy]=useState(othersBusyBlocks||[]);
  const [showWhosFree,setShowWhosFree]=useState(false);
  const [whosFreeWindow,setWhosFreeWindow]=useState(null);
  const [memberProfiles,setMemberProfiles]=useState([]);
  const [showCreatePlan,setShowCreatePlan]=useState(false);
  const [planForm,setPlanForm]=useState({title:"",startISO:"",endISO:""});
  const bottomRef=useRef();
  const inputRef=useRef();

  // For group chats, fetch all participants' profiles from Firestore
  useEffect(()=>{
    const others=(participants||[]).filter(uid=>uid!==myUid);
    if(others.length===0){ setAllOthersBusy([]); setMemberProfiles([]); return; }
    Promise.all(others.map(uid=>
      getDoc(doc(db,"users",uid))
        .then(s=>({uid,name:s.data()?.name||contacts?.find(c=>c.gatherId===uid)?.name||"Someone",busyBlocks:s.data()?.busyBlocks||[]}))
        .catch(()=>({uid,name:contacts?.find(c=>c.gatherId===uid)?.name||"Someone",busyBlocks:[]}))
    )).then(profiles=>{
      setMemberProfiles(profiles);
      setAllOthersBusy(profiles.flatMap(p=>p.busyBlocks));
    });
  },[chatId]); // eslint-disable-line

  // Subscribe to messages
  useEffect(()=>{
    if(!chatId||!myUid) return;
    const q=query(collection(db,"chats",chatId,"messages"));
    const unsub=onSnapshot(q,snap=>{
      const all=snap.docs.map(d=>({id:d.id,...d.data()}))
        .sort((a,b)=>new Date(a.createdAt)-new Date(b.createdAt));
      setMsgs(all);
      setTimeout(()=>bottomRef.current?.scrollIntoView({behavior:"smooth"}),80);
      snap.docs.forEach(d=>{
        if(d.data().from!==myUid&&!d.data().read)
          setDoc(doc(db,"chats",chatId,"messages",d.id),{read:true},{merge:true}).catch(()=>{});
      });
    });
    return unsub;
  },[chatId,myUid]);

  async function send(){
    const t=text.trim();
    if(!t||sending) return;
    setSending(true); setText("");
    const msgData={from:myUid,text:t,createdAt:new Date().toISOString(),read:false};
    await addDoc(collection(db,"chats",chatId,"messages"),msgData);
    await setDoc(doc(db,"chats",chatId),{participants,lastMessage:t,lastMessageAt:new Date().toISOString(),lastMessageFrom:myUid},{merge:true});
    setSending(false);
    (participants||[]).filter(uid=>uid!==myUid).forEach(uid=>{
      getDoc(doc(db,"users",uid)).then(s=>{
        const token=s.data()?.fcmToken;
        if(token) fetch("/api/notify-chat",{method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({fcmToken:token,senderName:myName||"Someone on Gather",message:t})}).catch(()=>{});
      }).catch(()=>{});
    });
  }

  function openTimePicker(){
    const slots=freeWindowSlots(busyBlocks||[],allOthersBusy,7);
    setTimeSlots(slots);
    setShowTimePicker(true);
  }

  async function sendTimeProposal(slot){
    setShowTimePicker(false);
    const msgData={
      from:myUid,createdAt:new Date().toISOString(),read:false,
      type:"time_proposal",slotStr:slot.str,startISO:slot.startISO,endISO:slot.endISO,
      status:"pending",proposedBy:myUid,
    };
    const ref=await addDoc(collection(db,"chats",chatId,"messages"),msgData);
    const preview=`📅 ${slot.str}`;
    await setDoc(doc(db,"chats",chatId),{participants,lastMessage:preview,lastMessageAt:new Date().toISOString(),lastMessageFrom:myUid},{merge:true});
    (participants||[]).filter(uid=>uid!==myUid).forEach(uid=>{
      getDoc(doc(db,"users",uid)).then(s=>{
        const token=s.data()?.fcmToken;
        if(token) fetch("/api/notify-chat",{method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({fcmToken:token,senderName:myName||"Someone on Gather",message:`Suggested a time: ${slot.str}`})}).catch(()=>{});
      }).catch(()=>{});
    });
  }

  async function respondToProposal(msgId,accept){
    await setDoc(doc(db,"chats",chatId,"messages",msgId),{status:accept?"accepted":"declined"},{merge:true}).catch(()=>{});
    if(accept){
      const statusMsg={from:myUid,text:"Confirmed! Adding it to my calendar.",createdAt:new Date().toISOString(),read:false};
      await addDoc(collection(db,"chats",chatId,"messages"),statusMsg);
      await setDoc(doc(db,"chats",chatId),{participants,lastMessage:"Confirmed! Adding it to my calendar.",lastMessageAt:new Date().toISOString(),lastMessageFrom:myUid},{merge:true});
    }
  }

  function calLink(startISO,endISO,name){ return buildCalLink(startISO,endISO,name); }

  const isGroup=chatId?.startsWith("group_");
  const firstName=chatName?.split(" ")[0]||"";

  // Build uid→firstName map for PlanCard roll-call
  const memberNames=useMemo(()=>{
    const map={[myUid]:myName||"You"};
    memberProfiles.forEach(p=>{map[p.uid]=p.name;});
    (contacts||[]).forEach(c=>{if(c.gatherId) map[c.gatherId]=c.name;});
    return map;
  },[myUid,myName,memberProfiles,contacts]);

  // Group availability — compute mutual free slots across all synced members
  const groupFreeSlots=useMemo(()=>{
    if(!isGroup) return [];
    const calConnected=(busyBlocks||[]).length>0||allOthersBusy.length>0;
    if(!calConnected) return [];
    return freeWindowSlots(busyBlocks||[],allOthersBusy,7).slice(0,3);
  },[isGroup,busyBlocks,allOthersBusy]);

  return(
    <div style={{position:"fixed",inset:0,background:C.bg,display:"flex",flexDirection:"column",zIndex:300,fontFamily:C.body}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",background:"#fff",borderBottom:`1px solid ${C.line}`,flexShrink:0}}>
        <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",padding:4,display:"flex",alignItems:"center",color:C.blue}}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M19 12H5M12 5l-7 7 7 7" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
        {isGroup
          ?<div style={{width:38,height:38,borderRadius:"50%",background:C.tint,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{chatName?.slice(0,2)||"👥"}</div>
          :<Av name={chatName} size={38}/>
        }
        <p style={{flex:1,fontSize:15,fontWeight:700,color:C.ink,margin:0,fontFamily:C.display}}>{chatName}</p>
        {isGroup&&(
          <button onClick={()=>{setShowWhosFree(true);setWhosFreeWindow(null);}}
            style={{background:showWhosFree?C.blueSoft:"none",border:`1.5px solid ${showWhosFree?C.blueBdr:C.line}`,borderRadius:8,padding:"6px 10px",cursor:"pointer",fontSize:12,color:showWhosFree?C.blue:C.ink3,display:"flex",alignItems:"center",gap:4,flexShrink:0,transition:"all .15s"}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="9" cy="7" r="4" stroke={showWhosFree?C.blue:C.ink3} strokeWidth="1.8"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" stroke={showWhosFree?C.blue:C.ink3} strokeWidth="1.8" strokeLinecap="round"/><path d="M16 11l2 2 4-4" stroke={showWhosFree?C.blue:C.ink3} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Who's free?
          </button>
        )}
        {!isGroup&&onFindTime&&(
          <button onClick={onFindTime} style={{background:"none",border:`1.5px solid ${C.line}`,borderRadius:8,padding:"6px 10px",cursor:"pointer",fontSize:12,color:C.ink3,display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" stroke={C.ink3} strokeWidth="1.8"/><path d="M16 2v4M8 2v4M3 10h18" stroke={C.ink3} strokeWidth="1.8" strokeLinecap="round"/></svg>
            Find time
          </button>
        )}
      </div>

      {/* Who's free panel */}
      {isGroup&&showWhosFree&&(
        <div style={{background:"#fff",borderBottom:`1.5px solid ${C.line}`,padding:"14px 16px",flexShrink:0,maxHeight:"55vh",overflowY:"auto"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <p style={{fontSize:14,fontWeight:700,color:C.ink,margin:0,fontFamily:C.display}}>Who's free?</p>
            <button onClick={()=>{setShowWhosFree(false);setWhosFreeWindow(null);}} style={{background:"none",border:"none",cursor:"pointer",color:C.ink4,fontSize:20,lineHeight:1,padding:0}}>×</button>
          </div>
          {/* Time window picker */}
          <p style={{fontSize:11,fontWeight:600,color:C.ink4,textTransform:"uppercase",letterSpacing:".05em",margin:"0 0 8px"}}>Pick a window</p>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
            {quickWindows().map((w,i)=>{
              const active=whosFreeWindow?.startISO===w.startISO;
              return(
                <button key={i} onClick={()=>setWhosFreeWindow(w)}
                  style={{background:active?C.blueSoft:"#f8f9fb",border:`1.5px solid ${active?C.blueBdr:C.line}`,borderRadius:20,padding:"5px 11px",cursor:"pointer",fontSize:12,color:active?C.blue:C.ink2,fontFamily:C.body,whiteSpace:"nowrap",transition:"all .12s"}}>
                  {w.label} <span style={{opacity:.6}}>{w.sub}</span>
                </button>
              );
            })}
          </div>
          {/* Per-member availability */}
          {whosFreeWindow&&(()=>{
            const allMembers=[
              {uid:myUid,name:myName||"You",busyBlocks:busyBlocks||[]},
              ...memberProfiles,
            ];
            const free=allMembers.filter(m=>memberAvail(m.busyBlocks,whosFreeWindow.startISO,whosFreeWindow.endISO)==="free");
            const busy=allMembers.filter(m=>memberAvail(m.busyBlocks,whosFreeWindow.startISO,whosFreeWindow.endISO)==="busy");
            const unknown=allMembers.filter(m=>memberAvail(m.busyBlocks,whosFreeWindow.startISO,whosFreeWindow.endISO)==="unknown");
            return(
              <div>
                <p style={{fontSize:11,fontWeight:600,color:C.ink4,textTransform:"uppercase",letterSpacing:".05em",margin:"0 0 10px"}}>{whosFreeWindow.label} · {whosFreeWindow.sub}</p>
                <div style={{display:"flex",flexDirection:"column",gap:7,marginBottom:14}}>
                  {allMembers.map(m=>{
                    const avail=memberAvail(m.busyBlocks,whosFreeWindow.startISO,whosFreeWindow.endISO);
                    const color=avail==="free"?C.green:avail==="busy"?"#ef4444":"#94a3b8";
                    const bg=avail==="free"?C.greenSoft:avail==="busy"?"#fef2f2":"#f8fafc";
                    const label=avail==="free"?"Free":avail==="busy"?"Busy":"No calendar";
                    return(
                      <div key={m.uid} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 12px",background:bg,borderRadius:10}}>
                        <div style={{display:"flex",alignItems:"center",gap:9}}>
                          <Av name={m.uid===myUid?"You":m.name} size={28}/>
                          <p style={{margin:0,fontSize:13,fontWeight:500,color:C.ink,fontFamily:C.body}}>{m.uid===myUid?"You":m.name}</p>
                        </div>
                        <span style={{fontSize:11,fontWeight:600,color,background:"#fff",border:`1px solid ${color}33`,borderRadius:20,padding:"3px 9px"}}>{label}</span>
                      </div>
                    );
                  })}
                </div>
                {free.length>=2&&(
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>{
                      const names=free.filter(m=>m.uid!==myUid).map(m=>m.name.split(" ")[0]);
                      setText(`${names.join(", ")} — ${whosFreeWindow.label.toLowerCase()}? 🎾`);
                      setShowWhosFree(false);
                      setWhosFreeWindow(null);
                    }}
                      style={{flex:1,background:C.blue,color:"#fff",border:"none",borderRadius:10,padding:"10px 14px",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:C.body}}>
                      Message {free.filter(m=>m.uid!==myUid).length} free {free.filter(m=>m.uid!==myUid).length===1?"person":"people"}
                    </button>
                    <button onClick={()=>{
                      sendTimeProposal(whosFreeWindow);
                      setShowWhosFree(false);
                      setWhosFreeWindow(null);
                    }}
                      style={{background:C.tint,color:C.ink2,border:`1.5px solid ${C.line}`,borderRadius:10,padding:"10px 14px",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:C.body,flexShrink:0}}>
                      📅 Propose
                    </button>
                  </div>
                )}
                {free.length<2&&free.length>0&&(
                  <p style={{fontSize:12,color:C.ink4,textAlign:"center",margin:0}}>Only {free.length===1&&free[0].uid===myUid?"you're":"one person is"} free here — try a different window.</p>
                )}
                {free.length===0&&(
                  <p style={{fontSize:12,color:C.ink4,textAlign:"center",margin:0}}>No one's free in this window — try another time.</p>
                )}
              </div>
            );
          })()}
          {!whosFreeWindow&&memberProfiles.every(m=>m.busyBlocks.length===0)&&(busyBlocks||[]).length===0&&(
            <p style={{fontSize:13,color:C.ink4,textAlign:"center",padding:"8px 0"}}>Connect your calendars to see who's actually free.</p>
          )}
        </div>
      )}

      {/* Group availability banner (when Who's free panel is closed) */}
      {isGroup&&!showWhosFree&&groupFreeSlots.length>0&&(
        <div style={{background:C.tint,borderBottom:`1px solid ${C.line}`,padding:"10px 16px",flexShrink:0}}>
          <p style={{fontSize:11,fontWeight:600,color:C.ink4,textTransform:"uppercase",letterSpacing:".05em",margin:"0 0 6px"}}>Everyone's free</p>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {groupFreeSlots.map((slot,i)=>(
              <button key={i} onClick={()=>sendTimeProposal(slot)}
                style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:20,padding:"5px 11px",cursor:"pointer",fontSize:12,color:C.ink2,fontFamily:C.body,whiteSpace:"nowrap"}}>
                📅 {slot.str.replace(/ \(.*?\)/,"")}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Active plan banner */}
      {(()=>{
        const activePlan=(plans||[]).find(p=>p.chatId===chatId&&p.status!=="cancelled");
        if(!activePlan) return null;
        return(
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.line}`,flexShrink:0}}>
            <PlanCard plan={activePlan} myUid={myUid} memberNames={memberNames}
              onRsvp={rsvp=>onRsvpPlan&&onRsvpPlan(activePlan.id,rsvp)}
              onCancel={()=>onCancelPlan&&onCancelPlan(activePlan.id)}/>
          </div>
        );
      })()}

      {/* Create plan sheet */}
      {isGroup&&showCreatePlan&&(
        <div style={{background:"#fff",borderBottom:`1.5px solid ${C.line}`,padding:"16px",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <p style={{fontSize:14,fontWeight:700,color:C.ink,margin:0,fontFamily:C.display}}>Create a plan</p>
            <button onClick={()=>setShowCreatePlan(false)} style={{background:"none",border:"none",cursor:"pointer",color:C.ink4,fontSize:20,lineHeight:1,padding:0}}>×</button>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div>
              <p style={{fontSize:11,fontWeight:600,color:C.ink3,fontFamily:C.body,marginBottom:5}}>What are you doing?</p>
              <Field value={planForm.title} onChange={v=>setPlanForm(f=>({...f,title:v}))} placeholder="e.g. Tennis, Dinner, Coffee"/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <div>
                <p style={{fontSize:11,fontWeight:600,color:C.ink3,fontFamily:C.body,marginBottom:5}}>Date & time</p>
                <input type="datetime-local" value={planForm.startISO?planForm.startISO.slice(0,16):""}
                  onChange={e=>{const v=e.target.value;const s=new Date(v);const en=new Date(s.getTime()+2*3600000);setPlanForm(f=>({...f,startISO:s.toISOString(),endISO:en.toISOString()}));}}
                  style={{width:"100%",border:`1.5px solid ${C.line}`,borderRadius:8,padding:"8px 10px",fontSize:13,fontFamily:C.body,color:C.ink,outline:"none",background:"#f8f9fb",boxSizing:"border-box"}}/>
              </div>
              <div>
                <p style={{fontSize:11,fontWeight:600,color:C.ink3,fontFamily:C.body,marginBottom:5}}>Ends</p>
                <input type="datetime-local" value={planForm.endISO?planForm.endISO.slice(0,16):""}
                  onChange={e=>{const v=e.target.value;setPlanForm(f=>({...f,endISO:new Date(v).toISOString()}));}}
                  style={{width:"100%",border:`1.5px solid ${C.line}`,borderRadius:8,padding:"8px 10px",fontSize:13,fontFamily:C.body,color:C.ink,outline:"none",background:"#f8f9fb",boxSizing:"border-box"}}/>
              </div>
            </div>
            <button
              disabled={!planForm.title.trim()||!planForm.startISO}
              onClick={async()=>{
                const groupId=chatId.replace("group_","");
                await onCreatePlan({
                  title:planForm.title.trim(),
                  activity:planForm.title.trim().toLowerCase(),
                  startISO:planForm.startISO,
                  endISO:planForm.endISO||planForm.startISO,
                  chatId,groupId,
                  groupName:chatName,
                  groupEmoji:chatName?.split(" ")[0]||"📅",
                  participants,
                });
                setPlanForm({title:"",startISO:"",endISO:""});
                setShowCreatePlan(false);
              }}
              style={{background:planForm.title.trim()&&planForm.startISO?C.blue:"#e2e8f0",color:planForm.title.trim()&&planForm.startISO?"#fff":C.ink4,border:"none",borderRadius:10,padding:"11px",cursor:planForm.title.trim()&&planForm.startISO?"pointer":"default",fontSize:14,fontWeight:600,fontFamily:C.body,transition:"background .15s"}}>
              Create plan
            </button>
            {/* SMS invite for off-platform group members */}
            {offPlatformContacts.length>0&&planForm.title.trim()&&planForm.startISO&&(
              <div style={{background:C.tint,borderRadius:10,padding:"10px 12px",border:`1.5px solid ${C.line}`}}>
                <p style={{fontSize:11,fontWeight:600,color:C.ink3,margin:"0 0 8px",textTransform:"uppercase",letterSpacing:".05em"}}>Not on Gather yet</p>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {offPlatformContacts.map(c=>{
                    const dateStr=planForm.startISO?new Date(planForm.startISO).toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric",hour:"numeric",minute:"2-digit"}):"";
                    const smsBody=encodeURIComponent(`Hey ${c.name.split(" ")[0]}! We're planning ${planForm.title} on ${dateStr}. Join us on Gather: https://heygather.app?ref=plan`);
                    return(
                      <a key={c.id||c.name} href={`sms:${c.phone||""}?body=${smsBody}`}
                        style={{display:"flex",alignItems:"center",gap:9,background:"#fff",borderRadius:8,padding:"8px 10px",textDecoration:"none",border:`1px solid ${C.line}`}}>
                        <Av name={c.name} size={26}/>
                        <div style={{flex:1,minWidth:0}}>
                          <p style={{margin:0,fontSize:13,fontWeight:500,color:C.ink,fontFamily:C.body}}>{c.name.split(" ")[0]}</p>
                          <p style={{margin:0,fontSize:11,color:C.ink4,fontFamily:C.body}}>Invite to plan via SMS</p>
                        </div>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke={C.blue} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{flex:1,overflowY:"auto",padding:"16px",display:"flex",flexDirection:"column",gap:2}}>
        {msgs.length===0&&(
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:8,color:C.ink4,paddingTop:60}}>
            <div style={{fontSize:40}}>💬</div>
            <p style={{fontSize:14,textAlign:"center",fontFamily:C.body}}>Start the conversation with {firstName}</p>
          </div>
        )}
        {msgs.map((m,i)=>{
          const isMe=m.from===myUid;
          const prevMsg=msgs[i-1];
          const nextMsg=msgs[i+1];
          const sameFromPrev=prevMsg?.from===m.from;
          const sameFromNext=nextMsg?.from===m.from;
          const showTime=!nextMsg||new Date(nextMsg.createdAt)-new Date(m.createdAt)>300000;

          // RSVP update bubble
          if(m.type==="rsvp_update"){
            return(
              <div key={m.id} style={{display:"flex",justifyContent:"center",margin:"6px 0"}}>
                <span style={{fontSize:12,color:m.rsvp==="in"?C.green:"#94a3b8",fontFamily:C.body,background:m.rsvp==="in"?C.greenSoft:"#f1f5f9",borderRadius:20,padding:"4px 12px",border:`1px solid ${m.rsvp==="in"?C.green+"33":"#e2e8f0"}`}}>
                  {m.text}
                </span>
              </div>
            );
          }

          // Plan confirmed celebration
          if(m.type==="plan_confirmed"){
            return(
              <div key={m.id} style={{display:"flex",justifyContent:"center",margin:"10px 0"}}>
                <div style={{background:"linear-gradient(135deg,#f0fdf4,#dcfce7)",border:`1.5px solid ${C.green}55`,borderRadius:14,padding:"10px 16px",textAlign:"center",maxWidth:"85%"}}>
                  <p style={{margin:0,fontSize:14,fontWeight:700,color:C.green,fontFamily:C.display}}>{m.text}</p>
                </div>
              </div>
            );
          }

          // Time proposal card
          if(m.type==="time_proposal"){
            const isProposer=m.proposedBy===myUid;
            const accepted=m.status==="accepted";
            const declined=m.status==="declined";
            const pending=m.status==="pending";
            return(
              <div key={m.id} style={{display:"flex",justifyContent:isProposer?"flex-end":"flex-start",marginBottom:sameFromNext?2:10}}>
                <div style={{maxWidth:"80%",background:"#fff",border:`1.5px solid ${accepted?C.green:declined?"#f1f5f9":C.blueBdr}`,borderRadius:14,overflow:"hidden",boxShadow:"0 2px 8px rgba(37,99,235,.08)"}}>
                  <div style={{background:accepted?`${C.green}15`:declined?"#f8fafc":C.blueSoft,padding:"10px 14px",borderBottom:`1px solid ${accepted?C.green+"33":declined?"#e2e8f0":C.blueBdr}`}}>
                    <p style={{fontSize:11,fontWeight:600,color:accepted?C.green:declined?C.ink4:C.blue,margin:"0 0 2px",textTransform:"uppercase",letterSpacing:".05em"}}>
                      {accepted?"Confirmed ✓":declined?"Declined":isProposer?"You suggested":"Time suggestion"}
                    </p>
                    <p style={{fontSize:14,fontWeight:600,color:C.ink,margin:0}}>📅 {m.slotStr}</p>
                  </div>
                  <div style={{padding:"10px 14px",display:"flex",flexDirection:"column",gap:8}}>
                    {accepted&&(
                      <a href={calLink(m.startISO,m.endISO,isProposer?firstName:myName||"Friend")} target="_blank" rel="noreferrer"
                        style={{display:"block",background:C.blue,color:"#fff",textAlign:"center",borderRadius:8,padding:"8px 14px",fontSize:13,fontWeight:600,textDecoration:"none"}}>
                        Add to Google Calendar →
                      </a>
                    )}
                    {pending&&!isProposer&&(
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>respondToProposal(m.id,true)}
                          style={{flex:1,background:C.green,color:"#fff",border:"none",borderRadius:8,padding:"8px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                          Accept
                        </button>
                        <button onClick={()=>respondToProposal(m.id,false)}
                          style={{flex:1,background:"#f1f5f9",color:C.ink3,border:"none",borderRadius:8,padding:"8px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                          Decline
                        </button>
                      </div>
                    )}
                    {pending&&isProposer&&(
                      <p style={{fontSize:12,color:C.ink4,margin:0,textAlign:"center"}}>Waiting for response…</p>
                    )}
                    {showTime&&(
                      <p style={{fontSize:10,color:C.ink4,margin:0,textAlign:isProposer?"right":"left"}}>
                        {new Date(m.createdAt).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          return(
            <div key={m.id} style={{display:"flex",flexDirection:"column",alignItems:isMe?"flex-end":"flex-start",marginBottom:sameFromNext?2:8}}>
              <div style={{maxWidth:"72%",
                background:isMe?C.blue:"#fff",color:isMe?"#fff":C.ink,
                borderRadius:isMe
                  ?(sameFromPrev?"14px 3px 3px 14px":"14px 14px 3px 14px")
                  :(sameFromPrev?"3px 14px 14px 3px":"14px 14px 14px 3px"),
                padding:"9px 13px",fontSize:14,lineHeight:1.55,
                border:isMe?"none":`1px solid ${C.line}`,wordBreak:"break-word",
              }}>
                {m.text}
                {showTime&&(
                  <div style={{fontSize:10,opacity:.55,marginTop:3,textAlign:isMe?"right":"left"}}>
                    {new Date(m.createdAt).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}
                    {isMe&&m.read&&<span style={{marginLeft:4}}>✓✓</span>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>

      {/* Time picker sheet */}
      {showTimePicker&&(
        <div style={{background:"#fff",borderTop:`1.5px solid ${C.line}`,padding:"16px",flexShrink:0,maxHeight:"50vh",overflowY:"auto"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
            <p style={{fontSize:14,fontWeight:600,color:C.ink,margin:0}}>Suggest a time</p>
            <button onClick={()=>setShowTimePicker(false)} style={{background:"none",border:"none",cursor:"pointer",color:C.ink4,fontSize:20,lineHeight:1}}>×</button>
          </div>
          <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginBottom:12,lineHeight:1.5}}>Pick a slot to float — they still confirm it works for them before it's locked in.</p>
          {timeSlots.length===0?(
            <p style={{fontSize:13,color:C.ink4,textAlign:"center",padding:"12px 0"}}>
              {(busyBlocks||[]).length===0?"Connect your calendar to see your free windows":"No free windows found in the next week"}
            </p>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {timeSlots.map((slot,i)=>(
                <button key={i} onClick={()=>sendTimeProposal(slot)}
                  style={{background:C.tint,border:`1.5px solid ${C.line}`,borderRadius:10,padding:"11px 14px",cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:10,width:"100%"}}>
                  <span style={{fontSize:18}}>📅</span>
                  <span style={{fontSize:13,fontWeight:500,color:C.ink,fontFamily:C.body}}>{slot.str}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Compose */}
      <div style={{padding:"10px 12px",background:"#fff",borderTop:(showTimePicker||showCreatePlan)?"none":`1px solid ${C.line}`,display:"flex",gap:8,alignItems:"flex-end",flexShrink:0,paddingBottom:`calc(10px + env(safe-area-inset-bottom))`}}>
        <button onClick={openTimePicker} title="Suggest a time"
          style={{width:40,height:40,borderRadius:10,background:showTimePicker?C.blueSoft:C.tint,border:`1.5px solid ${showTimePicker?C.blueBdr:C.line}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"background .15s"}}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" stroke={showTimePicker?C.blue:C.ink3} strokeWidth="1.8"/><path d="M16 2v4M8 2v4M3 10h18" stroke={showTimePicker?C.blue:C.ink3} strokeWidth="1.8" strokeLinecap="round"/></svg>
        </button>
        {isGroup&&onCreatePlan&&!(plans||[]).some(p=>p.chatId===chatId&&p.status!=="cancelled")&&(
          <button onClick={()=>{
            if(!showCreatePlan){
              const auto=detectActivity(chatName);
              if(auto) setPlanForm(f=>({...f,title:auto}));
            }
            setShowCreatePlan(o=>!o);setShowTimePicker(false);
          }} title="Create a plan"
            style={{width:40,height:40,borderRadius:10,background:showCreatePlan?C.greenSoft:C.tint,border:`1.5px solid ${showCreatePlan?C.green:C.line}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"background .15s"}}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke={showCreatePlan?C.green:C.ink3} strokeWidth="2" strokeLinecap="round"/></svg>
          </button>
        )}
        <textarea
          ref={inputRef}
          value={text}
          onChange={e=>setText(e.target.value)}
          onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
          placeholder={`Message ${firstName}…`}
          rows={1}
          style={{flex:1,resize:"none",border:`1.5px solid ${C.line}`,borderRadius:20,padding:"10px 14px",fontSize:14,fontFamily:C.body,color:C.ink,outline:"none",background:"#f8f9fb",lineHeight:1.5,maxHeight:100,overflowY:"auto"}}
          onInput={e=>{e.target.style.height="auto";e.target.style.height=Math.min(e.target.scrollHeight,100)+"px";}}
        />
        <button onClick={send} disabled={!text.trim()||sending}
          style={{width:40,height:40,borderRadius:"50%",background:text.trim()?C.blue:C.line,border:"none",cursor:text.trim()?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"background .15s"}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 2L15 22 11 13 2 9l20-7z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </button>
      </div>
    </div>
  );
}

/* ── RecentChats — conversations list ──────────────────────────────────────── */
function RecentChats({myUid,contacts,groups,onOpenChat}){
  const [chats,setChats]=useState([]);
  const [unread,setUnread]=useState({});

  useEffect(()=>{
    if(!myUid) return;
    const q=query(collection(db,"chats"),where("participants","array-contains",myUid));
    const unsub=onSnapshot(q,snap=>{
      const all=snap.docs.map(d=>({id:d.id,...d.data()}))
        .filter(c=>c.lastMessage)
        .sort((a,b)=>new Date(b.lastMessageAt)-new Date(a.lastMessageAt));
      setChats(all);
    });
    return unsub;
  },[myUid]);

  // Track unread per chat (filter sender client-side to avoid composite index)
  useEffect(()=>{
    if(!myUid||chats.length===0) return;
    const unsubs=chats.map(chat=>{
      const q=query(collection(db,"chats",chat.id,"messages"),where("read","==",false));
      return onSnapshot(q,snap=>{
        const count=snap.docs.filter(d=>d.data().from!==myUid).length;
        setUnread(prev=>({...prev,[chat.id]:count}));
      });
    });
    return ()=>unsubs.forEach(u=>u());
  },[chats,myUid]);

  if(chats.length===0) return null;

  function resolveName(chat){
    if(chat.id.startsWith("group_")){
      const gid=chat.id.replace("group_","");
      const g=groups?.find(g=>g.id===gid);
      return g?`${g.emoji} ${g.name}`:(chat.groupName||"Group chat");
    }
    const otherUid=chat.participants?.find(p=>p!==myUid);
    const c=contacts?.find(c=>c.gatherId===otherUid);
    return c?.name||"Chat";
  }

  function timeAgo(iso){
    if(!iso) return "";
    const diff=Date.now()-new Date(iso).getTime();
    if(diff<3600000) return `${Math.floor(diff/60000)||1}m`;
    if(diff<86400000) return `${Math.floor(diff/3600000)}h`;
    return `${Math.floor(diff/86400000)}d`;
  }

  return(
    <div style={{marginBottom:24}}>
      <p style={{fontSize:11,fontWeight:600,color:C.ink4,letterSpacing:".06em",textTransform:"uppercase",margin:"0 0 8px",fontFamily:C.body}}>Messages</p>
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {chats.slice(0,6).map(chat=>{
          const name=resolveName(chat);
          const isGroup=chat.id.startsWith("group_");
          const count=unread[chat.id]||0;
          const isFromMe=chat.lastMessageFrom===myUid;
          return(
            <button key={chat.id} onClick={()=>onOpenChat(chat.id,name,chat.participants||[])}
              style={{display:"flex",alignItems:"center",gap:12,background:"#fff",border:`1.5px solid ${count>0?C.blue:C.line}`,borderRadius:12,padding:"11px 14px",cursor:"pointer",textAlign:"left",width:"100%",transition:"border-color .15s",fontFamily:C.body}}>
              {isGroup
                ?<div style={{width:38,height:38,borderRadius:"50%",background:C.tint,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{name.slice(0,2)||"👥"}</div>
                :<Av name={name} size={38}/>
              }
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:2}}>
                  <p style={{fontSize:14,fontWeight:count>0?700:500,color:C.ink,margin:0}}>{name}</p>
                  <span style={{fontSize:11,color:C.ink4,flexShrink:0,marginLeft:8}}>{timeAgo(chat.lastMessageAt)}</span>
                </div>
                <p style={{fontSize:13,color:count>0?C.ink2:C.ink4,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:count>0?600:400}}>
                  {isFromMe?"You: ":""}{chat.lastMessage}
                </p>
              </div>
              {count>0&&(
                <div style={{width:20,height:20,borderRadius:"50%",background:C.blue,color:"#fff",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{count}</div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Gather Tab — groups, plans, conversations ─────────────────────────────── */
function GatherTab({groups,contacts,plans,myUid,myName,onOpenChat,onAddGroup,onUpdateGroup,onDeleteGroup,onRsvpPlan,myProfile}){
  const [expandedGroupId,setExpandedGroupId]=useState(null);
  const [gMode,setGMode]=useState(null);
  const [gForm,setGForm]=useState({name:"",emoji:"👥",contactIds:[],notes:""});
  const [showEmojiPicker,setShowEmojiPicker]=useState(false);

  const upcomingPlans=useMemo(()=>
    (plans||[]).filter(p=>p.status!=="cancelled"&&p.startISO&&new Date(p.startISO)>new Date())
      .sort((a,b)=>new Date(a.startISO)-new Date(b.startISO))
      .slice(0,5)
  ,[plans]);

  async function saveGroup(){
    if(!gForm.name.trim()) return;
    if(gMode==="add") await onAddGroup(gForm);
    else await onUpdateGroup(gMode,gForm);
    setGMode(null); setGForm({name:"",emoji:"👥",contactIds:[],notes:""});
  }

  const EMOJIS=["👥","🎾","⚽","🏀","🎱","🎿","🏊","🚴","🧘","🍕","🍻","🎮","🎬","📚","🎸","🃏","🏌️","🎯","🥾","🧗","🏋️","🎭","🍽️","☕","🧩"];

  if(groups.length===0&&!gMode) return(
    <div className="i" style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"52vh",padding:"40px 24px",textAlign:"center"}}>
      <div style={{fontSize:52,marginBottom:16}}>👥</div>
      <p style={{fontFamily:C.display,fontSize:22,fontWeight:700,color:C.ink,marginBottom:8}}>Create your first group</p>
      <p style={{fontSize:14,color:C.ink3,lineHeight:1.7,maxWidth:270,margin:"0 auto 24px",fontFamily:C.body}}>
        Group your people — tennis crew, family, coworkers — and gather finds time and makes planning effortless.
      </p>
      <button onClick={()=>{setGForm({name:"",emoji:"👥",contactIds:[],notes:""});setGMode("add");}}
        style={{background:C.blue,color:"#fff",border:"none",borderRadius:12,padding:"14px 28px",cursor:"pointer",fontFamily:C.body,fontSize:14,fontWeight:700,boxShadow:"0 4px 14px rgba(37,99,235,.3)"}}>
        + Create a group
      </button>
    </div>
  );

  return(
    <div>
      {/* Group form */}
      {gMode&&(
        <div style={{background:"#fff",border:`1.5px solid ${C.blueBdr}`,borderRadius:14,padding:"18px 20px",marginBottom:18}}>
          <p style={{fontSize:15,fontWeight:700,color:C.ink,fontFamily:C.display,marginBottom:14}}>{gMode==="add"?"New group":"Edit group"}</p>
          <div style={{display:"flex",gap:10,marginBottom:12,alignItems:"center"}}>
            <div style={{position:"relative"}}>
              <div style={{width:44,height:44,borderRadius:10,background:C.tint,border:`1.5px solid ${C.line}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,cursor:"pointer"}}
                onClick={()=>setShowEmojiPicker(v=>!v)}>
                {gForm.emoji}
              </div>
              {showEmojiPicker&&(
                <div style={{position:"absolute",top:50,left:0,zIndex:50,background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:12,padding:10,width:224,display:"flex",flexWrap:"wrap",gap:4,boxShadow:"0 4px 20px rgba(0,0,0,.12)"}}>
                  {EMOJIS.map(e=>(
                    <button key={e} onClick={()=>{setGForm(f=>({...f,emoji:e}));setShowEmojiPicker(false);}}
                      style={{background:"none",border:"none",cursor:"pointer",fontSize:20,padding:"4px 6px",borderRadius:6}}>{e}</button>
                  ))}
                </div>
              )}
            </div>
            <Field value={gForm.name} onChange={v=>setGForm(f=>({...f,name:v}))} placeholder="Group name (e.g. Tennis Tuesdays)" style={{flex:1}}/>
          </div>
          <p style={{fontSize:12,fontWeight:600,color:C.ink3,fontFamily:C.body,marginBottom:8}}>Members</p>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
            {contacts.filter(c=>gForm.contactIds.includes(c.id)).map(c=>(
              <div key={c.id} style={{display:"flex",alignItems:"center",gap:5,background:C.blueSoft,border:`1px solid ${C.blueBdr}`,borderRadius:20,padding:"4px 10px 4px 6px"}}>
                <Av name={c.name} size={18}/>
                <span style={{fontSize:12,color:C.blue,fontFamily:C.body}}>{c.name.split(" ")[0]}</span>
                <button onClick={()=>setGForm(f=>({...f,contactIds:f.contactIds.filter(id=>id!==c.id)}))}
                  style={{background:"none",border:"none",cursor:"pointer",color:C.blue,fontSize:14,lineHeight:1,padding:0,marginLeft:2}}>×</button>
              </div>
            ))}
          </div>
          <div style={{maxHeight:160,overflowY:"auto",border:`1.5px solid ${C.line}`,borderRadius:10,marginBottom:12}}>
            {contacts.filter(c=>!gForm.contactIds.includes(c.id)).map(c=>(
              <div key={c.id} onClick={()=>setGForm(f=>({...f,contactIds:[...f.contactIds,c.id]}))}
                style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",cursor:"pointer",borderBottom:`1px solid ${C.line}`}}>
                <Av name={c.name} size={26}/>
                <span style={{fontSize:13,color:C.ink,fontFamily:C.body}}>{c.name}</span>
                {c.gatherId&&<span style={{fontSize:11,color:C.green,marginLeft:"auto"}}>on gather</span>}
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={saveGroup} disabled={!gForm.name.trim()}>Save group</Btn>
            <Btn onClick={()=>setGMode(null)} variant="ghost">Cancel</Btn>
          </div>
        </div>
      )}

      {/* Groups */}
      {!gMode&&(
        <>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <p style={{fontSize:15,fontWeight:700,color:C.ink,fontFamily:C.display}}>Your groups</p>
          <Btn onClick={()=>{setGForm({name:"",emoji:"👥",contactIds:[],notes:""});setGMode("add");}} variant="ghost" small>+ New group</Btn>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:28}}>
          {groups.map(g=>{
            const members=contacts.filter(c=>g.contactIds.includes(c.id));
            const chatParticipants=[myUid,...members.filter(c=>c.gatherId).map(c=>c.gatherId)];
            const isExpanded=expandedGroupId===g.id;
            const offPlatform=members.filter(m=>!m.gatherId);
            const hasGatherMembers=chatParticipants.length>1;
            return(
              <div key={g.id} style={{background:"#fff",border:`1.5px solid ${isExpanded?C.blueBdr:C.line}`,borderRadius:14,overflow:"hidden",transition:"border-color .15s"}}>
                <div onClick={()=>setExpandedGroupId(isExpanded?null:g.id)}
                  style={{display:"flex",alignItems:"center",gap:12,padding:"13px 16px",cursor:"pointer"}}>
                  <div style={{width:42,height:42,borderRadius:12,background:isExpanded?C.blueSoft:C.tint,border:`1.5px solid ${isExpanded?C.blueBdr:C.line}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0,transition:"all .15s"}}>{g.emoji}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontSize:14,fontWeight:700,color:C.ink,fontFamily:C.body,marginBottom:2}}>{g.name}</p>
                    <p style={{fontSize:12,color:C.ink3,fontFamily:C.body}}>
                      {members.length} {members.length===1?"person":"people"}
                      {members.length>0&&<> · {members.map(m=>m.name.split(" ")[0]).slice(0,3).join(", ")}{members.length>3?` +${members.length-3}`:""}</>}
                    </p>
                  </div>
                  {hasGatherMembers&&(
                    <button onClick={e=>{e.stopPropagation();onOpenChat("group_"+g.id,`${g.emoji} ${g.name}`,chatParticipants,"",offPlatform);}}
                      style={{background:C.blue,color:"#fff",border:"none",borderRadius:9,padding:"8px 14px",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:C.body,flexShrink:0,display:"flex",alignItems:"center",gap:5}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Chat
                    </button>
                  )}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{transition:"transform .2s",transform:isExpanded?"rotate(180deg)":"none",flexShrink:0}}><path d="M6 9l6 6 6-6" stroke={C.ink3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                {isExpanded&&(
                  <div style={{borderTop:`1px solid ${C.line}`,padding:"12px 16px",background:C.tint,display:"flex",flexDirection:"column",gap:10}}>
                    <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
                      {members.map(m=>(
                        <div key={m.id} style={{display:"flex",alignItems:"center",gap:6}}>
                          <Av name={m.name} size={26}/>
                          <div>
                            <span style={{fontSize:12,color:C.ink2,fontFamily:C.body}}>{m.name.split(" ")[0]}</span>
                            {m.gatherId&&<span style={{fontSize:10,color:C.green,display:"block"}}>on gather</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                    {offPlatform.length>0&&(
                      <p style={{fontSize:12,color:C.ink4,fontFamily:C.body}}>{offPlatform.map(m=>m.name.split(" ")[0]).join(", ")} {offPlatform.length===1?"isn't":"aren't"} on Gather yet</p>
                    )}
                    {(!g.createdBy||g.createdBy===myUid)&&(
                      <div style={{display:"flex",gap:7}}>
                        <button onClick={()=>{setGMode(g.id);setGForm(g);setExpandedGroupId(null);}}
                          style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:8,padding:"7px 14px",cursor:"pointer",fontSize:12,color:C.ink3,fontFamily:C.body}}>Edit</button>
                        <button onClick={async()=>{await onDeleteGroup&&onDeleteGroup(g.id);setExpandedGroupId(null);}}
                          style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:"#ef4444",fontFamily:C.body,padding:"7px 10px"}}>Delete</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Upcoming plans */}
        {upcomingPlans.length>0&&(
          <div style={{marginBottom:28}}>
            <p style={{fontSize:15,fontWeight:700,color:C.ink,fontFamily:C.display,marginBottom:12}}>Coming up</p>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {upcomingPlans.map(p=>(
                <div key={p.id} onClick={()=>p.chatId&&onOpenChat(p.chatId,p.groupName,p.participants)}
                  style={{cursor:p.chatId?"pointer":"default"}}>
                  <PlanCard plan={p} myUid={myUid} compact onRsvp={rsvp=>onRsvpPlan&&onRsvpPlan(p.id,rsvp)}/>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent chats */}
        <RecentChats myUid={myUid} contacts={contacts} groups={groups} onOpenChat={onOpenChat}/>
        </>
      )}
    </div>
  );
}

/* ── Reach Out ─────────────────────────────────────────────────────────────── */
function ReachOut({contacts,myProfile,onUpdate,myUid,busyBlocks=[],initialOpenId,onClearInitial,onOpenChat}) {
  const [openId,setOpenId]=useState(initialOpenId||null);
  const [ai_,setAi]=useState({});
  const [copied,setCopied]=useState({});
  const [lsm,setLsm]=useState({});
  const [logIn,setLogIn]=useState({});
  const [sq,setSq]=useState("");
  const [justSent,setJustSent]=useState({});
  const [nudgeSent,setNudgeSent]=useState({});
  const [nudgeIntent,setNudgeIntent]=useState({});
  const [nudgeEdit,setNudgeEdit]=useState({});
  const [showAllHistory,setShowAllHistory]=useState({});
  const [cPlanResult,setCPlanResult]=useState({});
  const [cPlanLoading,setCPlanLoading]=useState({});
  // gatherId cache: {[contactId]: string|null}  — null means not on Gather
  const [gatherIds,setGatherIds]=useState({});
  const [showSmsAlt,setShowSmsAlt]=useState({});
  const [showMore,setShowMore]=useState({});

  const urgent=urgentList(contacts);
  const bdays=bdaySoon(contacts);

  // If arriving from Contacts with a specific person, load their context
  useEffect(()=>{
    if(!initialOpenId) return;
    const contact=contacts.find(c=>c.id===initialOpenId);
    if(contact){
      // Set openId directly (don't use open() which would toggle-close since
      // useState already initialised openId to initialOpenId)
      setOpenId(initialOpenId);
      // Auto-select default intent
      if(!nudgeIntent[initialOpenId]){
        setNudgeIntent(m=>({...m,[initialOpenId]:"checkin"}));
        const template=buildNudge(myProfile,contact,daysAgo(contact.lastSeen),"checkin");
        setNudgeEdit(m=>({...m,[initialOpenId]:template}));
      }
      // Run the same gatherId lookup that open() would do
      if(contact.gatherId&&contact.gatherId!==myUid){
        setGatherIds(m=>({...m,[initialOpenId]:contact.gatherId}));
      } else if(!(initialOpenId in gatherIds)&&contact.email){
        getDoc(doc(db,"emails",contact.email.toLowerCase())).then(snap=>{
          const uid=snap.exists()?snap.data().uid:null;
          setGatherIds(m=>({...m,[initialOpenId]:uid&&uid!==myUid?uid:null}));
        }).catch(()=>setGatherIds(m=>({...m,[initialOpenId]:null})));
      }
    }
    if(onClearInitial) onClearInitial();
  },[initialOpenId]); // eslint-disable-line

  // New connections (added but never reached out to)
  const newConnections=useMemo(()=>
    contacts.filter(c=>!c.lastSeen&&!c.snoozedUntil&&!urgent.some(u=>u.id===c.id))
  ,[contacts,urgent]);

  // Gather tab shows only the action queue — urgent + new connections.
  // Search escapes this to show all contacts so you can still find anyone.
  const displayList=useMemo(()=>{
    if(sq.trim()){
      const q=sq.toLowerCase();
      const filtered=contacts.filter(c=>c.name.toLowerCase().includes(q));
      if(!openId) return filtered;
      const alreadyIn=filtered.some(c=>c.id===openId);
      if(alreadyIn) return filtered;
      const pinned=contacts.find(c=>c.id===openId);
      return pinned?[pinned,...filtered]:filtered;
    }
    const queue=[...newConnections,...urgent];
    if(!openId) return queue;
    const alreadyIn=queue.some(c=>c.id===openId);
    if(alreadyIn) return queue;
    const pinned=contacts.find(c=>c.id===openId);
    return pinned?[pinned,...queue]:queue;
  },[urgent,newConnections,contacts,sq,openId]);

  const INTENTS=[
    {id:"checkin",  label:"Just saying hi",        emoji:"👋"},
    {id:"coffee",   label:"Coffee",                 emoji:"☕"},
    {id:"dinner",   label:"Dinner",                 emoji:"🍽️"},
    {id:"drinks",   label:"Drinks",                 emoji:"🍺"},
    {id:"lunch",    label:"Lunch",                  emoji:"🥗"},
    {id:"walk",     label:"A walk",                 emoji:"🚶"},
    {id:"tennis",   label:"Tennis",                 emoji:"🎾"},
    {id:"golf",     label:"Golf",                   emoji:"⛳"},
    {id:"call",     label:"Catch-up call",          emoji:"📞"},
  ];

  // Professional-specific intents — shown first for lead contacts
  const LEAD_INTENTS=[
    {id:"share",    label:"Share something useful", emoji:"💡"},
    {id:"followup", label:"Follow up on last chat", emoji:"📋"},
    {id:"congrats", label:"Congratulate them",      emoji:"🎉"},
    {id:"coffee",   label:"Coffee / lunch",         emoji:"☕"},
    {id:"call",     label:"Catch-up call",          emoji:"📞"},
    {id:"checkin",  label:"Just saying hi",         emoji:"👋"},
  ];

  const INTENT_GOAL={
    checkin:  "just check in warmly — no agenda, just saying hello and showing you're thinking of them",
    coffee:   "invite them for a coffee catch-up — keep it casual and easy to say yes to",
    dinner:   "invite them to dinner — warm and specific, suggest finding a time soon",
    drinks:   "invite them for drinks — relaxed and fun",
    lunch:    "invite them for lunch — light and easy",
    walk:     "invite them for a walk — low-key and easy to say yes to",
    tennis:   "invite them for a game of tennis",
    golf:     "invite them for a round of golf",
    call:     "suggest a proper catch-up phone call or video call",
    share:    "share something genuinely useful — a relevant article, insight, or introduction. Zero pitch, just adding value to their life or work",
    followup: "follow up warmly on the last conversation — reference something specific they mentioned and move the relationship forward naturally. Not transactional, just human",
    congrats: "congratulate them warmly and specifically on a recent achievement or life event — genuine, personal, no agenda",
  };

  async function open(contact) {
    const id=contact.id;
    if(openId===id){setOpenId(null);return;}
    setOpenId(id);
    // Auto-select default intent so message is immediately ready
    if(!nudgeIntent[id]){
      const defaultId=contact.type==="lead"?"checkin":"checkin";
      setNudgeIntent(m=>({...m,[id]:defaultId}));
      const template=buildNudge(myProfile,contact,daysAgo(contact.lastSeen),defaultId);
      setNudgeEdit(m=>({...m,[id]:template}));
    }
    // Look up if this contact is on Gather (for in-app messaging)
    // gatherId stored on the contact is the definitive link — use it directly
    if(contact.gatherId&&contact.gatherId!==myUid){
      setGatherIds(m=>({...m,[id]:contact.gatherId}));
    } else if(!(id in gatherIds)&&contact.email){
      getDoc(doc(db,"emails",contact.email.toLowerCase())).then(snap=>{
        const uid=snap.exists()?snap.data().uid:null;
        setGatherIds(m=>({...m,[id]:uid&&uid!==myUid?uid:null}));
      }).catch(()=>setGatherIds(m=>({...m,[id]:null})));
    }
  }

  async function draftForIntent(contact, intentId) {
    const id=contact.id;
    setAi(a=>({...a,[id]:{...a[id],draft:null,intent:intentId,loadingDraft:true}}));
    const tone={friend:"casual and warm",family:"warm and loving",acquaintance:"warm but not overly familiar",lead:"warm but professionally aware"}[contact.type]||"warm";
    const genderCtx=myProfile.gender==="man"?"The sender is a man — natural masculine tone, no excessive emojis.":myProfile.gender==="woman"?"The sender is a woman — warm and genuine.":"";
    const ageCtx=myProfile.ageRange==="70plus"||myProfile.ageRange==="50s60s"?"Classic warm style — no slang, no Gen-Z phrases.":myProfile.ageRange==="under30"?"Casual tone fine.":"";
    const logHistory=(contact.log||[]).slice(-8).map(e=>`${e.date}: ${e.note}`).join("\n")||"none";
    const goal=INTENT_GOAL[intentId]||"check in warmly";
    const tc=contact.touchCount||0;
    const touchCtx=(contact.type==="lead"||contact.type==="acquaintance")&&tc<7
      ? `This is touch ${tc+1} of 7 in building this relationship. ${tc<3?"Keep it warm and low-pressure.":tc<6?"You know each other now — be more personal.":"You're close to an established relationship — be genuine and direct."}`
      : "";
    const lifeCtx=(contact.lifeEvents||[]).slice(-2).map(e=>`${e.emoji} ${e.label} (${e.date})`).join(", ");
    const proCtx=contact.type==="lead"
      ? `This is a professional contact. The message must feel warm and human — NOT like a sales follow-up or CRM template. No "I wanted to circle back", no "touching base", no "reaching out to see if...", no "per my last email". Write like a real person who genuinely values this relationship.`
      : "";
    const styleCtx=buildStyleCtx(myProfile);
    const t=await callAI(`Write a short ${tone} message from ${myProfile.name||"me"} to ${contact.name}.
Goal: ${goal}
${styleCtx}
${myProfile.voiceNote?`Additional style notes: ${myProfile.voiceNote}`:""}
${genderCtx}
${ageCtx}
${touchCtx}
${proCtx}
Last contact: ${daysAgo(contact.lastSeen)} days ago
Conversation history (newest last):\n${logHistory}
Notes: ${contact.notes||"none"}
${lifeCtx?`Recent life events: ${lifeCtx} — acknowledge naturally if relevant.`:""}
Genuine. Reference a specific past conversation detail if there is one. No "Hope you're well!" Zero AI phrases.`);
    setAi(a=>({...a,[id]:{...a[id],draft:t,loadingDraft:false}}));
    // Auto-apply the AI draft directly into the editable textarea — no extra tap needed
    if(t) setNudgeEdit(m=>({...m,[id]:t}));
  }

  async function regen(contact) {
    const id=contact.id;
    const intentId=ai_[id]?.intent||"checkin";
    setAi(a=>({...a,[id]:{...a[id],draft:null,loadingDraft:true}}));
    const tone={friend:"casual warm",family:"warm loving",acquaintance:"warm friendly",lead:"warm professional"}[contact.type]||"warm";
    const genderCtx=myProfile.gender==="man"?"Sender is a man — natural masculine tone, no excessive emojis.":myProfile.gender==="woman"?"Sender is a woman.":"";
    const ageCtx=myProfile.ageRange==="70plus"||myProfile.ageRange==="50s60s"?"Classic warm style — no slang.":myProfile.ageRange==="under30"?"Casual tone fine.":"";
    const logHistory=(contact.log||[]).slice(-8).map(e=>`${e.date}: ${e.note}`).join("\n")||"none";
    const goal=INTENT_GOAL[intentId]||"check in warmly";
    const styleCtx=buildStyleCtx(myProfile);
    const t=await callAI(`Write a DIFFERENT short ${tone} message. Goal: ${goal}. From ${myProfile.name||"me"} to ${contact.name}. ${styleCtx} ${genderCtx} ${ageCtx} Conversation history:\n${logHistory}\nNotes: ${contact.notes||"none"}. Completely different phrasing from before. Reference a specific past conversation detail if available.`);
    // Keep old draft if AI fails so the button doesn't disappear; auto-apply to textarea if successful
    setAi(a=>({...a,[id]:{...a[id],draft:t||a[id]?.draft,loadingDraft:false}}));
    if(t) setNudgeEdit(m=>({...m,[id]:t}));
  }

  async function addTimesToDraft(contact) {
    const id=contact.id;
    const currentDraft=ai_[id]?.draft;
    if(!currentDraft) return;
    setAi(a=>({...a,[id]:{...a[id],loadingTimes:true}}));
    // Find the user's free windows over the next 14 days
    const freeSlots=findFreeWindows(busyBlocks,[],14);
    // Pick up to 3 varied slots (spread across different days if possible)
    const picked=[];
    const usedDays=new Set();
    for(const slot of freeSlots){
      const dayKey=slot.split(" ").slice(0,3).join(" ");
      if(!usedDays.has(dayKey)){usedDays.add(dayKey);picked.push(slot);}
      if(picked.length===3) break;
    }
    if(picked.length===0){
      // No calendar data — add a generic "let me know what works" line
      const t=await callAI(`Here is a message:
"${currentDraft}"

Add one short natural sentence at the end suggesting they find a time to meet — keep it casual and open-ended (e.g. "Would love to find a time soon — let me know what works!"). Return ONLY the updated full message.`);
      setAi(a=>({...a,[id]:{...a[id],draft:t||currentDraft,loadingTimes:false,timesAdded:true}}));
      return;
    }
    // Format slots for the AI prompt
    const slotList=picked.map(s=>`• ${s}`).join("\n");
    const genderCtx=myProfile.gender==="man"?"The sender is a man — keep the tone natural and direct.":myProfile.gender==="woman"?"The sender is a woman — warm and natural.":"";
    const t=await callAI(`Here is a message:
"${currentDraft}"

Rewrite it to naturally include these specific available times so they can pick one:
${slotList}

Weave the times in conversationally — e.g. "I'm free Saturday morning or Sunday afternoon — does either work?"
${genderCtx}
Keep the overall length similar. Return ONLY the updated full message with the times included naturally.`);
    setAi(a=>({...a,[id]:{...a[id],draft:t||currentDraft,loadingTimes:false,timesAdded:true}}));
  }

  async function genBday(contact) {
    const id=contact.id;
    setAi(a=>({...a,[id]:{...a[id],bdayLoading:true}}));
    const age=getAge(contact.birthday);
    const genderCtx=myProfile.gender==="man"?"Sender is a man — keep it genuine but not overly sentimental.":myProfile.gender==="woman"?"Sender is a woman — warm and heartfelt.":"";
    const ageCtx=myProfile.ageRange==="70plus"||myProfile.ageRange==="50s60s"?"Classic, warm style. No slang.":"";
    const logHistory=(contact.log||[]).slice(-5).map(e=>`${e.date}: ${e.note}`).join("\n")||"none";
    const t=await callAI(`Write a birthday message from ${myProfile.name||"me"} to ${contact.name}.
Style: ${myProfile.voiceNote||"Genuine, I write how I talk."}
${genderCtx} ${ageCtx}
Relationship: ${TYPES[contact.type]?.label||"friend"}
${age?`They're turning ${age+1}.`:""}
Notes: ${contact.notes||"none"}
Recent conversations:\n${logHistory}
2–3 sentences. Personal and genuine. Text message energy. Reference something specific about them if you can. No "Hope your day is amazing!"`);
    setAi(a=>({...a,[id]:{...a[id],bdayDraft:t,bdayLoading:false}}));
  }

  async function findTimeFor(contact) {
    const id=contact.id;
    setCPlanLoading(m=>({...m,[id]:true}));
    setCPlanResult(m=>({...m,[id]:null}));
    const busy=await getContactBusy(contact);
    const freeWindows=findFreeWindows(busyBlocks,busy||[],14);
    const myH=myProfile.hobbies||[];
    const cH=contact.hobbies||[];
    const shared=myH.filter(h=>cH.includes(h));
    const allH=[...new Set([...myH,...cH])];

    const availSection=freeWindows.length>0
      ?`Best windows: ${freeWindows.slice(0,5).join(", ")}`
      :busy
        ?"Schedules are full — suggest short easy meetups (coffee, a walk, lunch)."
        :"No calendar data — suggest realistic times based on a typical busy week.";
    const res=await callAIJSON(`Plan a hangout for ${myProfile.name||"me"} and ${contact.name} (${TYPES[contact.type]?.label||"friend"}).
${availSection}
Shared interests (prioritise these): ${shared.length>0?shared.map(hobbyLabel).join(", "):"none listed"}
All interests: ${allH.map(hobbyLabel).join(", ")||"general"}
Suggest 2 specific time windows with a concrete activity. Keep it realistic and fun.
Activities must be accessible to anyone — think walks, parks, cooking together, coffee, game nights, home hangouts. Avoid venues that need memberships or special access.
Return ONLY a JSON array: [{"time":"...","activity":"...","vibe":"one word"}]`,
    "You are a scheduling assistant. Respond with ONLY a valid JSON array — no explanation, no markdown.");
    setCPlanResult(m=>({...m,[id]:res||[]}));
    setCPlanLoading(m=>({...m,[id]:false}));
  }

  function copy(key,text) {
    navigator.clipboard?.writeText(text);
    setCopied(c=>({...c,[key]:true}));
    setTimeout(()=>setCopied(c=>({...c,[key]:false})),2000);
  }

  function logSeen(contact, touchType="meetup", noteOverride) {
    const id=contact.id;
    const date=lsm[id]||todayISO();
    const note=noteOverride!==undefined ? noteOverride : (logIn[id]||"");
    const newLog=[...(contact.log||[]),...(note?[{date,note}]:[])];
    // Increment touch count — important for networking and new relationships
    const newTouchCount=(contact.touchCount||0)+1;
    const newTouchLog=[...(contact.touchLog||[]),{date,type:touchType,note:note||"Reached out"}];
    // Update streak — localStorage for instant read, Firestore for cross-device sync
    const today=todayISO();
    const yesterday=new Date(Date.now()-864e5).toISOString().split("T")[0];
    const lastDate=localStorage.getItem("gather_streak_date");
    const streak=parseInt(localStorage.getItem("gather_streak")||"0");
    let newStreak=streak;
    if(lastDate!==today){
      newStreak=lastDate===yesterday?streak+1:1;
      localStorage.setItem("gather_streak_date",today);
      localStorage.setItem("gather_streak",String(newStreak));
    }
    if(myUid){
      setDoc(doc(db,"users",myUid),{streak:newStreak,streakDate:today},{merge:true}).catch(()=>{});
    }
    onUpdate({...contact,lastSeen:date,followUpSet:date,log:newLog,touchCount:newTouchCount,touchLog:newTouchLog});
    track("reach_out_logged",{contact_type:contact.type,touch_type:touchType,touch_count:newTouchCount});
    // Milestone-aware celebration toast
    const firstName=contact.name?.split(" ")[0]||contact.name;
    const toastMsg=
      newTouchCount===1&&(contact.touchCount||0)===0 ? `Good start — ${firstName} will appreciate hearing from you.` :
      newTouchCount%10===0 ? `Nice — ${firstName} will appreciate it.` :
      `Nice — ${firstName} will be glad you reached out 💙`;
    showToast(toastMsg);
    setLsm(m=>({...m,[id]:""}));
    setLogIn(m=>({...m,[id]:""}));
    setJustSent(m=>({...m,[id]:false}));
  }

  function snooze(contact,days) {
    const until=new Date(Date.now()+days*864e5).toISOString().split("T")[0];
    onUpdate({...contact,snoozedUntil:until});
    if(openId===contact.id) setOpenId(null);
  }

  if(contacts.length===0&&!openId) return (
    <div className="i" style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"60vh",padding:"40px 24px",textAlign:"center"}}>
      <div style={{fontSize:52,marginBottom:20}}>💙</div>
      <p style={{fontFamily:C.display,fontSize:26,fontWeight:700,color:C.ink,marginBottom:10,lineHeight:1.3}}>Your circle will live here</p>
      <p style={{fontSize:14,color:C.ink3,lineHeight:1.7,maxWidth:280,margin:"0 auto 28px",fontFamily:C.body}}>
        Add your people and gather shows you who to reach out to — and makes it easy.
      </p>
      <button onClick={()=>window.dispatchEvent(new CustomEvent("gather:goto",{detail:"contacts"}))}
        style={{background:C.blue,color:"#fff",border:"none",borderRadius:12,padding:"14px 28px",cursor:"pointer",fontFamily:C.body,fontSize:14,fontWeight:700,boxShadow:"0 4px 14px rgba(37,99,235,.3)"}}>
        Add your first person →
      </button>
      <p style={{fontSize:12,color:C.ink4,marginTop:16,fontFamily:C.body}}>Takes about 30 seconds</p>
    </div>
  );

  const allCaughtUp=urgent.length===0&&bdays.filter(({bs})=>bs.days<=7).length===0&&newConnections.length===0&&!sq.trim();

  return (
    <div>
      {/* All caught up — full-page celebration, not a banner above a list */}
      {allCaughtUp&&(
        <div className="i" style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"52vh",padding:"40px 24px",textAlign:"center"}}>
          <div style={{fontSize:52,marginBottom:16}}>🌟</div>
          <p style={{fontFamily:C.display,fontSize:22,fontWeight:700,color:C.ink,marginBottom:8}}>You're all caught up</p>
          <p style={{fontSize:14,color:C.ink3,lineHeight:1.7,maxWidth:260,margin:"0 auto 24px",fontFamily:C.body}}>
            Everyone's heard from you recently. Check back tomorrow — or search for someone specific.
          </p>
          <SearchBar value={sq} onChange={setSq} placeholder="Find someone…"/>
        </div>
      )}

      {!allCaughtUp&&(
        <>
        {/* Queue header */}
        {!sq.trim()&&(urgent.length>0||newConnections.length>0)&&(
          <div style={{marginBottom:14}}>
            <p style={{fontFamily:C.display,fontSize:19,fontWeight:700,color:C.ink,marginBottom:3}}>
              {newConnections.length>0?"New connections":`${urgent.length} ${urgent.length===1?"person":"people"} to reconnect with`}
            </p>
            <p style={{fontSize:13,color:C.ink3,fontFamily:C.body}}>
              {newConnections.length>0?"Reach out to make the connection real.":"These people haven't heard from you in a while."}
            </p>
          </div>
        )}

        {/* Birthday banners */}
        {bdays.filter(({bs})=>bs.days<=7).map(({c,bs})=>(
          <div key={c.id} className="u" style={{background:bs.days===0?C.roseSoft:C.amberSoft,border:`1.5px solid ${bs.days===0?C.rose+"30":C.amber+"30"}`,borderRadius:12,padding:"12px 16px",marginBottom:10,display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:22}}>🎂</span>
            <div style={{flex:1}}>
              <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body}}>{bs.days===0?`It's ${c.name}'s birthday today!`:`${c.name}'s birthday in ${bs.days} day${bs.days===1?"":"s"}`}</p>
              <p style={{fontSize:13,color:C.ink3,fontFamily:C.body}}>Turning {(getAge(c.birthday)||0)+1}</p>
            </div>
            <Btn onClick={()=>{setOpenId(c.id);genBday(c);}} variant={bs.days===0?"rose":"amber"} small>Send wishes</Btn>
          </div>
        ))}

        <SearchBar value={sq} onChange={setSq} placeholder="Find someone…"/>

        {/* Reconnect section label — shown when both groups present */}
        {!sq.trim()&&urgent.length>0&&newConnections.length>0&&(
          <p style={{fontSize:11,fontWeight:600,color:C.ink4,fontFamily:C.body,letterSpacing:".06em",textTransform:"uppercase",padding:"12px 2px 6px"}}>Good time to reconnect</p>
        )}

      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {displayList.map((contact,idx)=>{
          const id=contact.id;
          const ct=TYPES[contact.type]||TYPES.friend;
          const fs=fupStatus(contact);
          const bs=bdayStatus(contact.birthday);
          const isOpen=openId===id;
          const data=ai_[id]||{};

          return (
            <React.Fragment key={id}>
            <div className={`u d${Math.min(idx+1,5)}`} style={{background:C.white,border:`1.5px solid ${isOpen?C.blue:C.line}`,borderRadius:14,overflow:"hidden",boxShadow:isOpen?"0 6px 24px rgba(37,99,235,.12)":"0 1px 4px rgba(37,99,235,.05)",transition:"border-color .2s,box-shadow .2s"}}>

              {/* Row */}
              <div className="tap" onClick={()=>open(contact)} style={{padding:"14px 18px",display:"flex",alignItems:"center",gap:13}}>
                <Av name={contact.name}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:7,flexWrap:"wrap",marginBottom:3}}>
                    <span style={{fontSize:15,fontWeight:600,color:C.ink,fontFamily:C.body}}>{contact.name}</span>
                    {bs&&<Pill color={bs.color} soft={bs.soft}>{bs.label}</Pill>}
                    {fs&&!bs&&<Pill color={fs.color} soft={fs.color+"18"}>{fs.label}</Pill>}
                  </div>
                  <p style={{fontSize:13,color:C.ink3,fontFamily:C.body}}>
                    <span style={{color:ct.color}}>{ct.dot}</span>
                    {" "}{ct.label}
                    {contact.metAt&&contact.metAt!=="gather invite"&&contact.metAt!=="Joined via gather invite"&&<span> · {contact.metAt}</span>}
                    {!contact.lastSeen&&<span style={{color:C.ink4}}> · new connection</span>}
                  </p>
                </div>
                <div style={{width:26,height:26,borderRadius:"50%",background:C.tint,display:"flex",alignItems:"center",justifyContent:"center",transition:"transform .25s cubic-bezier(.16,1,.3,1)",transform:isOpen?"rotate(180deg)":"none",flexShrink:0}}>
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke={C.ink3} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
              </div>

              {/* Expanded */}
              {isOpen&&(
                <div className="p" style={{borderTop:`1.5px solid ${C.line}`,padding:"16px 18px 18px"}}>

                  {/* Birthday */}
                  {bs&&bs.days<=7&&<>
                    <Label>Birthday message</Label>
                    {data.bdayLoading
                      ?<div style={{display:"flex",alignItems:"center",gap:10,color:C.ink3,fontSize:13,padding:"8px 0",fontFamily:C.body}}><span className="sp"/>Writing…</div>
                      :data.bdayDraft
                        ?<>
                          <div style={{background:C.roseSoft,border:`1.5px solid ${C.rose}28`,borderRadius:10,padding:"12px 15px",fontSize:13,lineHeight:1.85,color:C.ink,whiteSpace:"pre-wrap",marginBottom:10,fontFamily:C.body}}>{data.bdayDraft}</div>
                          <div style={{display:"flex",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                            <Btn onClick={()=>copy("bday_"+id,data.bdayDraft)} variant="rose" small>{copied["bday_"+id]?"Copied ✓":"Copy"}</Btn>
                            {contact.phone&&<Btn onClick={()=>{window.open(`sms:${contact.phone.replace(/\s/g,"")}?&body=${encodeURIComponent(data.bdayDraft)}`,"_self");track("birthday_sent",{method:"sms"});}} variant="ghost" small>💬 Text →</Btn>}
                            {contact.phone&&<Btn onClick={()=>{window.open(`https://wa.me/${contact.phone.replace(/[^0-9+]/g,"")}?text=${encodeURIComponent(data.bdayDraft)}`,"_blank");track("birthday_sent",{method:"whatsapp"});}} variant="ghost" small>WhatsApp →</Btn>}
                            <Btn onClick={()=>genBday(contact)} variant="ghost" small>Regenerate</Btn>
                          </div>
                        </>
                        :<Btn onClick={()=>genBday(contact)} variant="rose" small>Generate birthday message</Btn>
                    }
                    <Hr/>
                  </>}

                  {/* ── Gather contact: chat only ──────────────────────────── */}
                  {gatherIds[id]?(
                    <>
                      <GatherChat myUid={myUid} myName={myProfile.name} contactGatherId={gatherIds[id]} contactName={contact.name}
                        onLog={note=>logSeen(contact,"plan",note)}
                        onOpenFull={onOpenChat}/>
                      {/* Escape hatch — send a text instead */}
                      <button onClick={()=>setShowSmsAlt(m=>({...m,[id]:!m[id]}))}
                        style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.ink4,fontFamily:C.body,padding:"8px 0 0",display:"flex",alignItems:"center",gap:4}}>
                        <span style={{fontSize:10}}>{showSmsAlt[id]?"▾":"▸"}</span>
                        {showSmsAlt[id]?"Hide text options":"Prefer to send a text?"}
                      </button>
                    </>
                  ):null}

                  {/* Find a time — shown first for non-leads */}
                  {contact.type!=="lead"&&(
                    <div style={{marginBottom:10}}>
                      {cPlanLoading[id]?(
                        <div style={{display:"flex",alignItems:"center",gap:8,color:C.ink3,fontSize:12,fontFamily:C.body,padding:"4px 0 8px"}}>
                          <span className="sp"/>Finding the best times…
                        </div>
                      ):cPlanResult[id]?(
                        <>
                          {cPlanResult[id].length===0
                            ?<p style={{fontSize:13,color:C.ink3,fontFamily:C.body,marginBottom:8}}>
                              {!myProfile.calendarConnected
                                ?"Connect your calendar in Profile to get smarter suggestions."
                                :"Schedules look packed — try a quick coffee or short walk?"}
                            </p>
                            :<div style={{display:"flex",flexDirection:"column",gap:7,marginBottom:8}}>
                              {cPlanResult[id].map((r,i)=>{
                                const msg=`Hey ${contact.name.split(" ")[0]}! ${r.activity} — how does ${r.time} sound?`;
                                return (
                                  <div key={i} style={{background:C.tint,border:`1.5px solid ${C.line}`,borderRadius:10,padding:"11px 14px"}}>
                                    <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:2}}>{r.time}</p>
                                    <p style={{fontSize:13,color:C.blue,fontFamily:C.body,fontWeight:500,marginBottom:6}}>{r.activity}</p>
                                    <p style={{fontSize:11,color:C.ink4,fontFamily:C.body,fontStyle:"italic",marginBottom:8}}>{r.vibe}</p>
                                    {contact.phone&&<Btn onClick={()=>window.open(`sms:${contact.phone.replace(/\s/g,"")}?&body=${encodeURIComponent(msg)}`,"_self")} variant="soft" small>💬 Text this</Btn>}
                                  </div>
                                );
                              })}
                            </div>
                          }
                          <Btn onClick={()=>findTimeFor(contact)} variant="ghost" small>↺ Try different times</Btn>
                        </>
                      ):(
                        <Btn onClick={()=>findTimeFor(contact)} variant="ghost" small>📅 Find a time to hang out</Btn>
                      )}
                      <Hr/>
                    </div>
                  )}

                  {/* ── SMS reach-out: chips + composer ────────────────────── */}
                  {(!gatherIds[id]||showSmsAlt[id])&&<>
                  <div style={{display:"flex",gap:6,overflowX:"auto",marginBottom:10,paddingBottom:2,WebkitOverflowScrolling:"touch",msOverflowStyle:"none",scrollbarWidth:"none"}}>
                    {(contact.type==="lead"?LEAD_INTENTS:INTENTS).map(intent=>{
                      const active=nudgeIntent[id]===intent.id;
                      return(
                      <span key={intent.id} className="tog"
                        onClick={()=>{
                          const isSame=active;
                          const next=isSame?null:intent.id;
                          setNudgeIntent(m=>({...m,[id]:next}));
                          if(!isSame){
                            const template=buildNudge(myProfile,contact,daysAgo(contact.lastSeen),intent.id);
                            setNudgeEdit(m=>({...m,[id]:template}));
                            setAi(a=>({...a,[id]:{...a[id],draft:null,intent:null,loadingDraft:false}}));
                          }
                        }}
                        style={{flexShrink:0,display:"inline-flex",alignItems:"center",gap:4,
                          background:active?(contact.type==="lead"?C.violetSoft:C.blueSoft):C.tint,
                          color:active?(contact.type==="lead"?C.violet:C.blue):C.ink2,
                          border:`1.5px solid ${active?(contact.type==="lead"?"rgba(124,58,237,.35)":C.blueBdr):C.line}`,
                          borderRadius:20,fontSize:12,fontWeight:active?600:400,
                          padding:"5px 11px",cursor:"pointer",fontFamily:C.body,whiteSpace:"nowrap"}}>
                        {intent.emoji} {intent.label}
                      </span>
                    );})}
                  </div>

                  {nudgeIntent[id]&&(()=>{
                    const editedMsg=nudgeEdit[id]!==undefined?nudgeEdit[id]:"";
                    const aiReady=!data.loadingDraft&&data.draft&&data.intent===nudgeIntent[id];
                    function sendText(){window.open(`sms:${(contact.phone||"").replace(/\s/g,"")}?&body=${encodeURIComponent(editedMsg)}`,"_self");}
                    function sendWA(){window.open(`https://wa.me/${(contact.phone||"").replace(/[^0-9+]/g,"")}?text=${encodeURIComponent(editedMsg)}`,"_blank");}
                    return (
                      <>
                        <textarea
                          value={editedMsg}
                          onChange={e=>setNudgeEdit(m=>({...m,[id]:e.target.value}))}
                          rows={3}
                          style={{width:"100%",background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:8,
                            color:C.ink,fontFamily:C.body,fontSize:13,padding:"10px 12px",lineHeight:1.6,
                            outline:"none",resize:"none",boxSizing:"border-box",marginBottom:8}}
                        />
                        {/* AI + send on same row */}
                        <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap",marginBottom:6}}>
                          {(data.loadingDraft||data.loadingTimes)?(
                            <span style={{display:"flex",alignItems:"center",gap:6,color:C.ink3,fontSize:12,fontFamily:C.body}}>
                              <span className="sp"/>{data.loadingTimes?"Adding times…":"Personalising…"}
                            </span>
                          ):aiReady?(
                            <Btn onClick={()=>regen(contact)} variant="ghost" small>↺ Retry</Btn>
                          ):(
                            <Btn onClick={()=>draftForIntent(contact,nudgeIntent[id])} variant="ghost" small>✨ Personalise</Btn>
                          )}
                          <div style={{flex:1}}/>
                          {contact.phone?(
                            <>
                              <Btn onClick={()=>{sendText();setNudgeSent(m=>({...m,[id]:true}));track("message_sent",{contact_type:contact.type,intent:nudgeIntent[id],method:"sms"});}} variant="green" small>💬 Text →</Btn>
                              <Btn onClick={()=>{sendWA();setNudgeSent(m=>({...m,[id]:true}));track("message_sent",{contact_type:contact.type,intent:nudgeIntent[id],method:"whatsapp"});}} variant="ghost" small>WhatsApp</Btn>
                            </>
                          ):(
                            <Btn onClick={()=>{copy(id,editedMsg);setNudgeSent(m=>({...m,[id]:true}));track("message_sent",{contact_type:contact.type,intent:nudgeIntent[id],method:"copy"});}} variant="green" small>{copied[id]?"Copied ✓":"Copy message"}</Btn>
                          )}
                        </div>
                        {aiReady&&nudgeIntent[id]!=="checkin"&&nudgeIntent[id]!=="call"&&!data.timesAdded&&(
                          <div style={{background:C.amberSoft,border:`1.5px solid ${C.amber}28`,borderRadius:9,padding:"9px 12px",marginBottom:8,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                            <span style={{fontSize:12,color:C.ink2,fontFamily:C.body,flex:1,lineHeight:1.5}}>📅 Add times you're free to make it easy to say yes.</span>
                            <Btn onClick={()=>addTimesToDraft(contact)} variant="amber" small>Add times</Btn>
                          </div>
                        )}
                        {nudgeSent[id]&&(
                          <div className="p" style={{background:C.greenSoft,border:`1.5px solid ${C.green}28`,borderRadius:9,padding:"9px 12px",marginTop:6,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                            <span style={{fontSize:13,color:C.ink2,fontFamily:C.body,flex:1}}>Did it go? Mark it so we remember.</span>
                            <div style={{display:"flex",gap:6}}>
                              <Btn onClick={()=>{logSeen(contact,"text");setNudgeSent(m=>({...m,[id]:false}));}} variant="green" small>✓ Log it</Btn>
                              <Btn onClick={()=>setNudgeSent(m=>({...m,[id]:false}))} variant="ghost" small>Not yet</Btn>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  </>}

                  <Hr/>

                  {/* Log — compact */}
                  <p style={{fontSize:12,fontWeight:600,color:C.ink3,fontFamily:C.body,marginBottom:7,textTransform:"uppercase",letterSpacing:".05em"}}>Mark it</p>
                  <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:7}}>
                    {[["☕","coffee","Coffee"],["📱","text","Texted"],["🍽️","dinner","Dinner"],["🚶","walk","Walk"],["📞","call","Called"],["🎉","hangout","Hung out"]].map(([icon,type,label])=>(
                      <button key={type} onClick={()=>logSeen(contact,type,label)}
                        style={{background:C.tint,border:`1.5px solid ${C.line}`,borderRadius:20,padding:"4px 10px",fontSize:12,cursor:"pointer",fontFamily:C.body,color:C.ink2,display:"flex",alignItems:"center",gap:4}}>
                        {icon} {label}
                      </button>
                    ))}
                  </div>
                  <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap"}}>
                    <Field value={lsm[id]||""} onChange={v=>setLsm(m=>({...m,[id]:v}))} type="date" style={{maxWidth:155}}/>
                    <Btn onClick={()=>logSeen(contact)} variant="green" small>Save</Btn>
                    <Btn onClick={()=>logSeen(contact,"other")} variant="soft" small>✓ We've connected</Btn>
                  </div>

                  {/* Snooze — compact inline */}
                  <div style={{display:"flex",alignItems:"center",gap:6,marginTop:10,flexWrap:"wrap"}}>
                    <p style={{fontSize:11,color:C.ink4,fontFamily:C.body,flexShrink:0}}>Snooze:</p>
                    {[3,7,14,30].map(d=>(
                      <Btn key={d} onClick={()=>snooze(contact,d)} variant="ghost" small>{d}d</Btn>
                    ))}
                  </div>


                  {/* History toggle */}
                  <button onClick={()=>setShowMore(m=>({...m,[id]:!m[id]}))}
                    style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.ink3,fontFamily:C.body,padding:"8px 0 2px",display:"flex",alignItems:"center",gap:5,marginBottom:showMore[id]?12:0}}>
                    <span style={{fontSize:10}}>{showMore[id]?"▾":"▸"}</span>
                    {showMore[id]?"Hide history":"History"}
                  </button>

                  {showMore[id]&&<>
                    <Hr/>
                    {(gatherIds[id]||contact.gatherId)?(
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",background:C.greenSoft,borderRadius:8,border:`1px solid ${C.green}33`}}>
                        <div style={{display:"flex",alignItems:"center",gap:7}}>
                          <span style={{fontSize:14}}>✓</span>
                          <p style={{margin:0,fontSize:12,fontWeight:600,color:C.green,fontFamily:C.body}}>{contact.name?.split(" ")[0]||contact.name} is on gather</p>
                        </div>
                      </div>
                    ):(
                      <InviteRow contact={contact} myUid={myUid}/>
                    )}
                  </>}

                  {/* Close */}
                  <div style={{display:"flex",justifyContent:"center",marginTop:16,paddingTop:14,borderTop:`1px solid ${C.line}`}}>
                    <button onClick={()=>setOpenId(null)}
                      style={{background:"none",border:`1.5px solid ${C.line}`,borderRadius:8,padding:"7px 24px",cursor:"pointer",fontFamily:C.body,fontSize:13,color:C.ink3,display:"flex",alignItems:"center",gap:6}}>
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 8l4-4 4 4" stroke={C.ink3} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
            </React.Fragment>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
}

/* ── Custom hobby input (shared by contact form and profile) ───────────────── */
function CustomHobbyInput({hobbies, onChange, placeholder="Add your own (poker, mahjong, board games…)"}) {
  const [val, setVal] = useState("");
  function add() {
    const t = val.trim().toLowerCase();
    if (!t || hobbies.includes(t)) { setVal(""); return; }
    onChange([...hobbies, t]);
    setVal("");
  }
  return (
    <div style={{display:"flex",gap:6,marginTop:4}}>
      <input
        value={val}
        onChange={e=>setVal(e.target.value)}
        onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();add();}}}
        placeholder={placeholder}
        style={{flex:1,fontSize:13,padding:"6px 10px",borderRadius:8,border:`1.5px solid ${C.line}`,
          fontFamily:C.body,color:C.ink,background:"#fff",outline:"none"}}
      />
      <button onClick={add} disabled={!val.trim()}
        style={{padding:"6px 12px",borderRadius:8,fontSize:12,fontWeight:600,fontFamily:C.body,
          background:val.trim()?C.blue:"#e5e7eb",color:val.trim()?"#fff":C.ink3,border:"none",cursor:val.trim()?"pointer":"default",transition:"all .15s"}}>
        + Add
      </button>
    </div>
  );
}
// Aliases so JSX references below stay readable
const ContactCustomHobbyInput = CustomHobbyInput;
const ProfileCustomHobbyInput = CustomHobbyInput;

/* ── Contacts ──────────────────────────────────────────────────────────────── */
function ContactsTab({contacts,onAddContact,onUpdateContact,onDeleteContact,groups,onAddGroup,onUpdateGroup,onDeleteGroup,myUid,myName,onReachOut,onOpenChat}) {
  const blank={name:"",type:"friend",stage:"close",metAt:"",notes:"",hobbies:[],lastSeen:"",birthday:"",followUpDays:14,phone:"",email:"",calType:"none",calUrl:"",manualSlots:"",log:[],snoozedUntil:null,touchCount:0,touchLog:[],lifeEvents:[]};
  const [mode,setMode]=useState(null);
  const [form,setForm]=useState(blank);
  const [filter,setFilter]=useState("all");
  const [openContactId,setOpenContactId]=useState(null);
  const [sq,setSq]=useState("");
  const [gMode,setGMode]=useState(null);
  const [gForm,setGForm]=useState({name:"",emoji:"👥",contactIds:[],notes:""});
  const [expandedGroupId,setExpandedGroupId]=useState(null);
  const [pickerContacts,setPickerContacts]=useState(null);
  const [importSaving,setImportSaving]=useState(false);
  const [vcfList,setVcfList]=useState(null);
  const [quickName,setQuickName]=useState("");
  const [quickAdding,setQuickAdding]=useState(false);
  const [showImportPrompt,setShowImportPrompt]=useState(false);
  const quickRef=useRef();
  const [vcfSelected,setVcfSelected]=useState([]);
  const [vcfSaving,setVcfSaving]=useState(false);
  const vcfRef=useRef();
  const contactPickerSupported=typeof navigator!=="undefined"&&"contacts" in navigator&&"ContactsManager" in window;

  // Duplicate detection — group by normalised name or matching phone
  const [mergeId,setMergeId]=useState(null);
  const duplicatePairs=useMemo(()=>{
    const pairs=[];
    const seen=new Set();
    for(let i=0;i<contacts.length;i++){
      for(let j=i+1;j<contacts.length;j++){
        const a=contacts[i],b=contacts[j];
        const key=`${a.id}-${b.id}`;
        if(seen.has(key)) continue;
        const nameMatch=a.name?.trim().toLowerCase()===b.name?.trim().toLowerCase()&&a.name?.trim();
        const phoneMatch=a.phone&&b.phone&&a.phone.replace(/\D/g,"")===b.phone.replace(/\D/g,"")&&a.phone.replace(/\D/g,"").length>=7;
        if(nameMatch||phoneMatch){ seen.add(key); pairs.push([a,b]); }
      }
    }
    return pairs;
  },[contacts]);

  function mergeContacts(keep,drop){
    const merged={
      ...drop,...keep,
      email:keep.email||drop.email,
      phone:keep.phone||drop.phone,
      notes:[keep.notes,drop.notes].filter(Boolean).join(" | "),
      log:[...(keep.log||[]),...(drop.log||[])].sort((a,b)=>new Date(b.date)-new Date(a.date)),
      touchLog:[...(keep.touchLog||[]),...(drop.touchLog||[])],
      lifeEvents:[...(keep.lifeEvents||[]),...(drop.lifeEvents||[])],
      hobbies:[...new Set([...(keep.hobbies||[]),...(drop.hobbies||[])])],
      touchCount:(keep.touchCount||0)+(drop.touchCount||0),
      gatherId:keep.gatherId||drop.gatherId||null,
    };
    onUpdateContact(keep.id,merged);
    onDeleteContact(drop.id);
    setMergeId(null);
  }

  function handleVcfFile(e){
    const f=e.target.files[0]; if(!f) return;
    e.target.value="";
    const r=new FileReader();
    r.onload=ev=>{
      const parsed=parseVCF(ev.target.result).filter(c=>c.name);
      setVcfList(parsed);
      setVcfSelected(parsed.map((_,i)=>i));
    };
    r.readAsText(f);
  }

  async function saveVcfContacts(){
    if(!vcfList) return;
    setVcfSaving(true);
    const blank={type:"friend",stage:"close",metAt:"",notes:"",hobbies:[],lastSeen:"",birthday:"",followUpDays:14,calType:"none",calUrl:"",manualSlots:"",log:[],snoozedUntil:null,touchCount:0,touchLog:[],lifeEvents:[]};
    for(const i of vcfSelected){
      const c=vcfList[i];
      await onAddContact({...blank,name:c.name,phone:c.phone||"",email:c.email||""});
    }
    setVcfList(null); setVcfSelected([]); setVcfSaving(false);
  }

  async function pickFromPhone(){
    try{
      const picked=await navigator.contacts.select(["name","tel","email"],{multiple:true});
      const mapped=picked.map(c=>({
        ...blank,
        name:(c.name&&c.name[0])||"",
        phone:(c.tel&&c.tel[0])||"",
        email:(c.email&&c.email[0])||"",
      })).filter(c=>c.name.trim());
      setPickerContacts(mapped);
    }catch(e){if(e.name!=="AbortError") alert("Couldn't open contacts. Try adding manually.");}
  }

  async function savePickedContacts(){
    if(!pickerContacts||pickerContacts.length===0) return;
    setImportSaving(true);
    for(const c of pickerContacts) await onAddContact(c);
    setPickerContacts(null);
    setImportSaving(false);
  }

  const [deleteId,setDeleteId]=useState(null);
  const [inviteOpen,setInviteOpen]=useState(false);
  const [inviteName,setInviteName]=useState("");
  const [invitePhone,setInvitePhone]=useState("");
  const [inviteSent,setInviteSent]=useState(false);

  function sendInvite(){
    if(!inviteName.trim()) return;
    const appUrl=myUid ? `https://heygather.app?ref=${myUid}` : "https://heygather.app";
    const msg=`Hey ${inviteName.split(" ")[0]}! I've been using gather to stay in better touch with the people who matter to me. Join me at Hey Gather! ${appUrl}`;
    const smsUrl=invitePhone.trim()
      ? `sms:${invitePhone.replace(/\s/g,"")}?&body=${encodeURIComponent(msg)}`
      : `sms:?&body=${encodeURIComponent(msg)}`;
    window.open(smsUrl,"_self");
    // Also save as a pending contact
    onAddContact({...blank,name:inviteName.trim(),phone:invitePhone.trim(),notes:"Invited via Gather link",stage:"new"});
    setInviteSent(true);
    setTimeout(()=>{setInviteOpen(false);setInviteName("");setInvitePhone("");setInviteSent(false);},2000);
  }

  async function addQuickPerson(){
    const name=quickName.trim(); if(!name) return;
    setQuickAdding(true);
    await onAddContact({...blank,name,followUpSet:todayISO()});
    const firstName=name.split(" ")[0];
    showToast(`${firstName} is in. gather will remind you when it's time to say hi 💙`);
    setQuickName("");
    setQuickAdding(false);
    setShowImportPrompt(true);
  }

  function openAdd(){setForm(blank);setMode("add");}
  function openEdit(c){setForm({...c,manualSlots:Array.isArray(c.manualSlots)?c.manualSlots.join("\n"):""});setMode(c.id);}
  function close(){setMode(null);}

  function save(){
    const slots=form.manualSlots?form.manualSlots.split("\n").map(s=>s.trim()).filter(Boolean):[];
    const r={...form,followUpDays:parseInt(form.followUpDays)||14,manualSlots:slots,followUpSet:form.lastSeen||todayISO(),log:form.log||[],snoozedUntil:form.snoozedUntil||null};
    if(mode==="add") onAddContact(r);
    else onUpdateContact(mode,r);
    showToast(mode==="add"?"Contact added!":"Contact saved!");
    close();
  }

  function remove(id){setDeleteId(id);}

  function saveGroup(){
    if(!gForm.name.trim()) return;
    if(gMode==="add") onAddGroup(gForm);
    else onUpdateGroup(gMode,gForm);
    setGMode(null);
  }

  const isEditing=mode!==null&&mode!=="add";

  const filtered=useMemo(()=>{
    let list=filter==="all"?contacts:contacts.filter(c=>c.type===filter);
    if(sq.trim()){const q=sq.toLowerCase();list=list.filter(c=>c.name.toLowerCase().includes(q)||c.notes?.toLowerCase().includes(q));}
    return list;
  },[contacts,filter,sq]);

  // ── First-person focused moment ────────────────────────────────────────────
  if(contacts.length===0&&mode===null){
    return (
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"72vh",padding:"32px 24px",textAlign:"center"}}>
        {/* Wordmark */}
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:36}}>
          <div style={{width:32,height:32,borderRadius:9,background:C.blue,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 10px rgba(37,99,235,.3)"}}>
            <div style={{width:9,height:9,borderRadius:"50%",background:"rgba(255,255,255,.9)"}}/>
          </div>
          <span style={{fontFamily:C.display,fontSize:24,fontWeight:700,color:C.ink,letterSpacing:"-.02em"}}>gather</span>
        </div>

        {/* The question */}
        <p style={{fontFamily:C.display,fontSize:28,fontWeight:700,color:C.ink,lineHeight:1.3,marginBottom:10,maxWidth:300}}>
          Who's someone you've been meaning to catch up with?
        </p>
        <p style={{fontSize:14,color:C.ink3,lineHeight:1.7,maxWidth:280,margin:"0 auto 32px",fontFamily:C.body}}>
          Just one person — a friend, family member, old colleague. Whoever came to mind first.
        </p>

        {/* Name input + button */}
        <div style={{width:"100%",maxWidth:320,display:"flex",flexDirection:"column",gap:10}}>
          <input
            ref={quickRef}
            value={quickName}
            onChange={e=>setQuickName(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&addQuickPerson()}
            placeholder="Their full name"
            autoFocus
            style={{width:"100%",padding:"14px 16px",fontSize:16,fontFamily:C.body,border:`2px solid ${quickName.trim()?C.blue:C.line}`,borderRadius:12,outline:"none",boxSizing:"border-box",color:C.ink,background:C.white,transition:"border-color .2s"}}
          />
          <button
            onClick={addQuickPerson}
            disabled={!quickName.trim()||quickAdding}
            style={{width:"100%",background:quickName.trim()?C.blue:"#cbd5e1",color:"#fff",border:"none",borderRadius:12,padding:"14px",cursor:quickName.trim()?"pointer":"default",fontFamily:C.body,fontSize:15,fontWeight:700,transition:"background .2s",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}
          >
            {quickAdding
              ? <><span className="sp" style={{width:14,height:14,borderColor:"rgba(255,255,255,.4)",borderTopColor:"#fff"}}/> Adding…</>
              : "Add them to gather →"}
          </button>
        </div>

        {/* Divider */}
        <div style={{display:"flex",alignItems:"center",gap:12,width:"100%",maxWidth:320,margin:"28px 0 20px"}}>
          <div style={{flex:1,height:1,background:C.line}}/>
          <span style={{fontSize:12,color:C.ink4,fontFamily:C.body}}>or add your whole circle at once</span>
          <div style={{flex:1,height:1,background:C.line}}/>
        </div>

        {/* Import option */}
        <div style={{display:"flex",flexDirection:"column",gap:8,width:"100%",maxWidth:320}}>
          {contactPickerSupported
            ? <button onClick={pickFromPhone} style={{width:"100%",background:C.tint,border:`1.5px solid ${C.line}`,borderRadius:12,padding:"12px",cursor:"pointer",color:C.ink2,fontFamily:C.body,fontSize:14,fontWeight:500}}>
                📱 Import from your contacts
              </button>
            : <button onClick={()=>vcfRef.current?.click()} style={{width:"100%",background:C.tint,border:`1.5px solid ${C.line}`,borderRadius:12,padding:"12px",cursor:"pointer",color:C.ink2,fontFamily:C.body,fontSize:14,fontWeight:500}}>
                📎 Import .vcf from iPhone Contacts
              </button>
          }
          <button onClick={openAdd} style={{background:"none",border:"none",cursor:"pointer",color:C.ink4,fontFamily:C.body,fontSize:13,padding:"4px"}}>
            or add someone manually with full details
          </button>
        </div>

        <input ref={vcfRef} type="file" accept=".vcf,.vcard" onChange={handleVcfFile} style={{display:"none"}}/>
      </div>
    );
  }

  return (
    <div>
      {/* Action bar */}
      <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:7,marginBottom:12,flexWrap:"wrap"}}>
        <Btn onClick={()=>{setGForm({name:"",emoji:"👥",contactIds:[],notes:""});setGMode("add");}} variant="ghost" small>+ Group</Btn>
        {contactPickerSupported
          ? <Btn onClick={pickFromPhone} variant="soft" small>📱 Import</Btn>
          : <Btn onClick={()=>vcfRef.current?.click()} variant="soft" small>📎 Import</Btn>}
        <input ref={vcfRef} type="file" accept=".vcf,.vcard" onChange={handleVcfFile} style={{display:"none"}}/>
        <Btn onClick={()=>{setInviteOpen(o=>!o);setInviteSent(false);}} variant="soft" small>✉️ Invite</Btn>
        <Btn onClick={openAdd} variant="primary" small>+ Add person</Btn>
      </div>

      {/* Search */}
      <SearchBar value={sq} onChange={setSq} placeholder="Search your people…"/>

      {/* Filter chips */}
      <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:10,marginBottom:14}}>
        {[["all","All"],["friend","Friends"],["family","Family"],["acquaintance","Acquaintances"],["lead","Network"]].map(([v,l])=>(
          <Chip key={v} color={C.blue} soft={C.blueSoft} active={filter===v} small onClick={()=>setFilter(v)}>{l}</Chip>
        ))}
      </div>

      {/* Contextual panels — triggered from action bar */}
      {showImportPrompt&&contacts.length<5&&(
        <div style={{background:`linear-gradient(135deg,${C.blueSoft},#eff6ff)`,border:`1.5px solid ${C.blueBdr}`,borderRadius:14,padding:"18px 20px",marginBottom:14,position:"relative"}}>
          <button onClick={()=>setShowImportPrompt(false)} style={{position:"absolute",top:10,right:12,background:"none",border:"none",cursor:"pointer",color:C.ink4,fontSize:18,lineHeight:1}}>×</button>
          <p style={{fontFamily:C.display,fontSize:16,fontWeight:700,color:C.ink,marginBottom:4}}>Great start! Want to add your wider circle?</p>
          <p style={{fontSize:13,color:C.ink3,fontFamily:C.body,lineHeight:1.6,marginBottom:14}}>gather works best with 5–10 people. Import from your phone contacts in seconds.</p>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {contactPickerSupported
              ? <Btn onClick={()=>{setShowImportPrompt(false);pickFromPhone();}} variant="primary" small>📱 Import from contacts</Btn>
              : <Btn onClick={()=>{setShowImportPrompt(false);vcfRef.current?.click();}} variant="primary" small>📎 Import .vcf</Btn>
            }
            <Btn onClick={()=>{setShowImportPrompt(false);openAdd();}} variant="ghost" small>+ Add another person</Btn>
          </div>
        </div>
      )}
      {inviteOpen&&(
        <Card className="p" style={{marginBottom:14,border:`1.5px solid ${C.blueBdr}`,background:C.tint}}>
          <p style={{fontFamily:C.display,fontSize:16,fontWeight:700,color:C.ink,marginBottom:4}}>Invite someone to Gather</p>
          <p style={{fontSize:12,color:C.ink3,marginBottom:14,lineHeight:1.6,fontFamily:C.body}}>
            Enter their name and number — we'll open a pre-written text for you to send. They'll be added to your people straight away.
          </p>
          {inviteSent?(
            <p className="i" style={{fontSize:14,color:C.green,fontWeight:600,fontFamily:C.body,textAlign:"center",padding:"8px 0"}}>✓ Invite sent!</p>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <input value={inviteName} onChange={e=>setInviteName(e.target.value)} placeholder="Their full name *"
                style={{width:"100%",background:"#fff",border:`1.5px solid ${C.lineM}`,borderRadius:8,color:C.ink,fontFamily:C.body,fontSize:13,padding:"10px 13px",outline:"none",boxSizing:"border-box"}}/>
              <input value={invitePhone} onChange={e=>setInvitePhone(e.target.value)} placeholder="Their phone number (optional)" type="tel"
                style={{width:"100%",background:"#fff",border:`1.5px solid ${C.lineM}`,borderRadius:8,color:C.ink,fontFamily:C.body,fontSize:13,padding:"10px 13px",outline:"none",boxSizing:"border-box"}}/>
              <div style={{background:"rgba(37,99,235,.05)",borderRadius:8,padding:"10px 12px",border:`1px dashed ${C.blueBdr}`}}>
                <p style={{fontSize:13,color:C.ink3,fontFamily:C.body,lineHeight:1.6}}>
                  💬 <em>"Hey {inviteName.split(" ")[0]||"[name]"}! I've been using gather to stay in better touch with the people who matter to me. Join me at Hey Gather! heygather.app"</em>
                </p>
              </div>
              <div style={{display:"flex",gap:8}}>
                <Btn onClick={sendInvite} disabled={!inviteName.trim()}>Send via text →</Btn>
                <Btn onClick={()=>setInviteOpen(false)} variant="ghost">Cancel</Btn>
              </div>
            </div>
          )}
        </Card>
      )}
      {!contactPickerSupported&&contacts.length===0&&(
        <div style={{background:"#fffbeb",border:"1.5px solid #fde68a",borderRadius:10,padding:"12px 14px",marginBottom:14,display:"flex",gap:10,alignItems:"flex-start"}}>
          <span style={{fontSize:18,flexShrink:0}}>📲</span>
          <div>
            <p style={{fontSize:13,fontWeight:600,color:"#92400e",fontFamily:C.body,marginBottom:3}}>Import from iPhone Contacts</p>
            <p style={{fontSize:12,color:"#a16207",lineHeight:1.6,fontFamily:C.body}}>
              Open <strong>Contacts</strong> → tap a contact → <strong>Share Contact</strong> → <strong>Save to Files</strong>. Then tap <strong>📎 Import</strong> to add them here. You can share multiple contacts at once from the Contacts list.
            </p>
          </div>
        </div>
      )}
      {pickerContacts&&(
        <Card className="p" style={{marginBottom:14,border:`1.5px solid ${C.blueBdr}`,background:C.tint}}>
          <p style={{fontFamily:C.display,fontSize:16,fontWeight:700,color:C.ink,marginBottom:4}}>
            {pickerContacts.length} contact{pickerContacts.length!==1?"s":""} selected
          </p>
          <p style={{fontSize:12,color:C.ink3,marginBottom:12,fontFamily:C.body}}>Review and remove anyone you don't want to add.</p>
          <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:220,overflowY:"auto",marginBottom:14}}>
            {pickerContacts.map((c,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"#fff",borderRadius:8,padding:"8px 12px",border:`1px solid ${C.line}`}}>
                <div>
                  <p style={{fontSize:13,fontWeight:500,color:C.ink,fontFamily:C.body}}>{c.name}</p>
                  {(c.phone||c.email)&&<p style={{fontSize:13,color:C.ink3,fontFamily:C.body}}>{c.phone||c.email}</p>}
                </div>
                <button onClick={()=>setPickerContacts(prev=>prev.filter((_,idx)=>idx!==i))}
                  style={{background:"none",border:"none",color:C.ink3,cursor:"pointer",fontSize:18,padding:"0 4px",lineHeight:1}}>×</button>
              </div>
            ))}
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={savePickedContacts} loading={importSaving} disabled={pickerContacts.length===0}>
              Add {pickerContacts.length} contact{pickerContacts.length!==1?"s":""}
            </Btn>
            <Btn onClick={()=>setPickerContacts(null)} variant="ghost">Cancel</Btn>
          </div>
        </Card>
      )}
      {vcfList&&(
        <Card className="p" style={{marginBottom:14,border:`1.5px solid ${C.blueBdr}`,background:C.tint}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
            <p style={{fontFamily:C.display,fontSize:16,fontWeight:700,color:C.ink}}>
              {vcfList.length} contact{vcfList.length!==1?"s":""} found
            </p>
            <button onClick={()=>setVcfSelected(vcfSelected.length===vcfList.length?[]:[...vcfList.keys()])}
              style={{background:"none",border:"none",fontSize:12,color:C.blue,fontWeight:600,cursor:"pointer",fontFamily:C.body}}>
              {vcfSelected.length===vcfList.length?"Deselect all":"Select all"}
            </button>
          </div>
          <p style={{fontSize:12,color:C.ink3,marginBottom:12,fontFamily:C.body}}>Tap to deselect anyone you don't want to add.</p>
          <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:260,overflowY:"auto",marginBottom:14}}>
            {vcfList.map((c,i)=>{
              const sel=vcfSelected.includes(i);
              return(
                <div key={i} onClick={()=>setVcfSelected(prev=>sel?prev.filter(x=>x!==i):[...prev,i])}
                  style={{display:"flex",alignItems:"center",gap:10,background:sel?"#fff":C.tint2,borderRadius:8,
                    padding:"8px 12px",border:`1.5px solid ${sel?C.line:"transparent"}`,cursor:"pointer",opacity:sel?1:.45,transition:"all .15s"}}>
                  <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${sel?C.blue:C.lineM}`,background:sel?C.blue:"transparent",
                    display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
                    {sel&&<span style={{color:"#fff",fontSize:11,fontWeight:700}}>✓</span>}
                  </div>
                  <Av name={c.name} size={28}/>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{fontSize:13,fontWeight:500,color:C.ink,fontFamily:C.body}}>{c.name}</p>
                    {(c.phone||c.email)&&<p style={{fontSize:13,color:C.ink3,fontFamily:C.body,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.phone||c.email}</p>}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={saveVcfContacts} loading={vcfSaving} disabled={vcfSelected.length===0}>
              Add {vcfSelected.length} contact{vcfSelected.length!==1?"s":""}
            </Btn>
            <Btn onClick={()=>{setVcfList(null);setVcfSelected([]);}} variant="ghost">Cancel</Btn>
          </div>
        </Card>
      )}

      {/* Duplicate contacts banner */}
      {duplicatePairs.length>0&&(
        <div style={{background:"#fffbeb",border:"1.5px solid #fbbf24",borderRadius:12,padding:"12px 16px"}}>
          <p style={{fontSize:13,fontWeight:600,color:"#92400e",fontFamily:C.body,marginBottom:8}}>
            {duplicatePairs.length===1?"1 possible duplicate found":`${duplicatePairs.length} possible duplicates found`}
          </p>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {duplicatePairs.map(([a,b],i)=>(
              <div key={i} style={{background:"#fff",borderRadius:8,padding:"10px 12px",border:"1px solid #fde68a"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                  <div style={{flex:1}}>
                    <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body}}>{a.name}</p>
                    <p style={{fontSize:11,color:C.ink3,fontFamily:C.body}}>{[a.phone,a.email].filter(Boolean).join(" · ")||"No phone or email"}</p>
                  </div>
                  <span style={{color:C.ink4,fontSize:13}}>+</span>
                  <div style={{flex:1}}>
                    <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body}}>{b.name}</p>
                    <p style={{fontSize:11,color:C.ink3,fontFamily:C.body}}>{[b.phone,b.email].filter(Boolean).join(" · ")||"No phone or email"}</p>
                  </div>
                </div>
                {mergeId===`${a.id}-${b.id}`?(
                  <div style={{display:"flex",gap:7}}>
                    <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,flex:1,alignSelf:"center"}}>Which one to keep?</p>
                    <button onClick={()=>mergeContacts(a,b)} style={{background:C.blue,color:"#fff",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer",fontSize:12,fontFamily:C.body,fontWeight:600}}>Keep {a.name.split(" ")[0]}</button>
                    <button onClick={()=>mergeContacts(b,a)} style={{background:C.blue,color:"#fff",border:"none",borderRadius:7,padding:"6px 12px",cursor:"pointer",fontSize:12,fontFamily:C.body,fontWeight:600}}>Keep {b.name.split(" ")[0]}</button>
                    <button onClick={()=>setMergeId(null)} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.ink3,fontFamily:C.body,padding:"6px 8px"}}>Cancel</button>
                  </div>
                ):(
                  <div style={{display:"flex",gap:7}}>
                    <button onClick={()=>setMergeId(`${a.id}-${b.id}`)} style={{background:C.blue,color:"#fff",border:"none",borderRadius:7,padding:"6px 14px",cursor:"pointer",fontSize:12,fontFamily:C.body,fontWeight:600}}>Merge</button>
                    <button onClick={()=>onDeleteContact(b.id)} style={{background:"none",border:"1px solid #fbbf24",borderRadius:7,padding:"6px 14px",cursor:"pointer",fontSize:12,fontFamily:C.body,color:"#92400e"}}>Remove {b.name.split(" ")[0]}</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Group form */}
      {gMode&&(
        <Card className="p" style={{marginBottom:14,border:`1.5px solid ${C.blueBdr}`,background:C.tint}}>
          <p style={{fontFamily:C.display,fontSize:18,fontWeight:700,color:C.ink,marginBottom:4}}>{gMode==="add"?"Create a group":"Edit group"}</p>
          <p style={{fontSize:13,color:C.ink3,fontFamily:C.body,marginBottom:16}}>Name it whatever makes sense to you — book club, family, work crew, anything.</p>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            {/* Emoji picker */}
            <div>
              <Label>Pick an icon</Label>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:6}}>
                {["👨‍👩‍👧‍👦","🏠","🤝","💼","⚽","🎾","🎮","🎵","🍕","☕","🎉","🌍","🏋️","🎬","📚","🎨","🎸","🧘","🍻","🎲","✈️","🐾","🎓","🌿","🏃","🧩","🎤","🌊","🏄","🎯","🧁"].map(e=>(
                  <button key={e} onClick={()=>setGForm(f=>({...f,emoji:e}))}
                    style={{width:36,height:36,borderRadius:8,border:`1.5px solid ${gForm.emoji===e?C.blue:C.line}`,background:gForm.emoji===e?C.blueSoft:"#fff",cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",transition:"all .12s"}}>
                    {e}
                  </button>
                ))}
              </div>
            </div>
            {/* Name field */}
            <div>
              <Label>Group name</Label>
              <Field value={gForm.name} onChange={v=>setGForm(f=>({...f,name:v}))} placeholder="Book club, family, work crew, hiking crew…"/>
            </div>
            {/* Members */}
            <div>
              <Label>Who's in it?</Label>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {contacts.map(c=>(
                  <Chip key={c.id} color={C.blue} soft={C.blueSoft} small active={gForm.contactIds.includes(c.id)}
                    onClick={()=>setGForm(f=>({...f,contactIds:f.contactIds.includes(c.id)?f.contactIds.filter(x=>x!==c.id):[...f.contactIds,c.id]}))}>
                    {c.name}
                  </Chip>
                ))}
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,marginTop:16}}>
            <Btn onClick={saveGroup} disabled={!gForm.name.trim()}>{gMode==="add"?"Create group":"Save changes"}</Btn>
            <Btn onClick={()=>setGMode(null)} variant="ghost">Cancel</Btn>
          </div>
        </Card>
      )}

      {/* Contact form */}
      {mode!==null&&(
        <Card className="p" style={{marginBottom:14,border:`1.5px solid ${C.blueBdr}`}}>
          <p style={{fontFamily:C.display,fontSize:18,fontWeight:700,color:C.ink,marginBottom:16}}>{isEditing?"Edit contact":"Add a contact"}</p>
          {!isEditing&&<VCFImporter onImport={imported=>setForm(f=>({...f,name:imported.name||f.name,phone:imported.phone||f.phone,email:imported.email||f.email,birthday:imported.birthday||f.birthday}))}/>}
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div>
              <Label>Type</Label>
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8}}>
                {Object.entries(TYPES).map(([k,{label,dot,color,soft}])=>(
                  <div key={k} className="tap" onClick={()=>setForm(f=>({...f,type:k,stage:(k==="friend"||k==="family")?"close":"new"}))} style={{borderRadius:10,padding:"11px 13px",cursor:"pointer",background:form.type===k?soft:C.tint,border:`1.5px solid ${form.type===k?color+"40":C.line}`,transition:"all .15s",display:"flex",alignItems:"center",gap:9}}>
                    <span style={{color:form.type===k?color:C.ink3,fontSize:14}}>{dot}</span>
                    <p style={{fontSize:12,fontWeight:form.type===k?600:400,color:form.type===k?color:C.ink2,fontFamily:C.body}}>{label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div><Label>Full name</Label><Field value={form.name} onChange={v=>setForm(f=>({...f,name:v}))} placeholder="Sarah Miller"/></div>
              <div><Label>Where you met</Label><Field value={form.metAt} onChange={v=>setForm(f=>({...f,metAt:v}))} placeholder="yoga studio…"/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div><Label>Phone</Label><Field value={form.phone||""} onChange={v=>setForm(f=>({...f,phone:v}))} placeholder="+1 555 000 0000" type="tel"/></div>
              <div><Label>Email</Label><Field value={form.email||""} onChange={v=>setForm(f=>({...f,email:v}))} placeholder="sarah@email.com" type="email"/></div>
            </div>
            <div><Label note="More detail = better AI suggestions">Notes</Label><Field value={form.notes} onChange={v=>setForm(f=>({...f,notes:v}))} multi placeholder="Things to remember, topics discussed, context…"/></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
              <div><Label>Last seen</Label><Field value={form.lastSeen} onChange={v=>setForm(f=>({...f,lastSeen:v}))} type="date"/></div>
              <div><Label>Birthday</Label><Field value={form.birthday||""} onChange={v=>setForm(f=>({...f,birthday:v}))} type="date"/></div>
              <div>
                <Label>Check in every</Label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {[[7,"Weekly"],[14,"2 weeks"],[30,"Monthly"],[90,"Quarterly"]].map(([d,l])=>(
                    <Chip key={d} color={C.blue} soft={C.blueSoft} small active={parseInt(form.followUpDays)===d} onClick={()=>setForm(f=>({...f,followUpDays:d}))}>{l}</Chip>
                  ))}
                </div>
              </div>
            </div>
            {(form.type!=="friend"&&form.type!=="family")&&(
              <div>
                <Label>Nurture stage</Label>
                <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                  {Object.entries(NURTURE).map(([k,{label,color,soft}])=>(
                    <Chip key={k} color={color} soft={soft} active={form.stage===k} onClick={()=>setForm(f=>({...f,stage:k}))}>{label}</Chip>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label>Interests</Label>
              <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:8}}>
                {[...Object.keys(HOBBIES), ...(form.hobbies||[]).filter(h=>!HOBBIES[h])].map(h=>(
                  <Chip key={h} color={C.blue} soft={C.blueSoft} small active={form.hobbies.includes(h)}
                    onClick={()=>setForm(f=>({...f,hobbies:f.hobbies.includes(h)?f.hobbies.filter(x=>x!==h):[...f.hobbies,h]}))}>
                    {hobbyEmoji(h)} {hobbyLabel(h)}
                  </Chip>
                ))}
              </div>
              <ContactCustomHobbyInput
                hobbies={form.hobbies||[]}
                onChange={hobbies=>setForm(f=>({...f,hobbies}))}
              />
            </div>
            {/* Life events */}
            <div>
              <Label note="Helps AI write more relevant, personal messages">Life events</Label>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
                {Object.entries(LIFE_EVENTS_TYPES).map(([k,{label,emoji}])=>(
                  <button key={k} onClick={()=>{
                    const already=(form.lifeEvents||[]).some(e=>e.type===k&&e.date===todayISO());
                    if(!already) setForm(f=>({...f,lifeEvents:[...(f.lifeEvents||[]),{date:todayISO(),type:k,emoji,label,note:""}]}));
                  }} style={{background:C.tint,border:`1.5px solid ${C.line}`,borderRadius:20,padding:"5px 11px",fontSize:12,cursor:"pointer",fontFamily:C.body,color:C.ink2,display:"flex",alignItems:"center",gap:4}}>
                    <span>{emoji}</span>{label}
                  </button>
                ))}
              </div>
              {(form.lifeEvents||[]).length>0&&(
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  {form.lifeEvents.map((ev,i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:8,background:C.blueSoft,border:`1px solid ${C.blueBdr}`,borderRadius:8,padding:"6px 10px"}}>
                      <span style={{fontSize:14}}>{ev.emoji}</span>
                      <span style={{fontSize:12,fontWeight:500,color:C.ink,fontFamily:C.body,flex:1}}>{ev.label}</span>
                      <span style={{fontSize:11,color:C.ink3,fontFamily:C.body}}>{ev.date}</span>
                      <button onClick={()=>setForm(f=>({...f,lifeEvents:f.lifeEvents.filter((_,j)=>j!==i)}))} style={{background:"none",border:"none",cursor:"pointer",fontSize:14,color:C.ink3,padding:"0 2px",lineHeight:1}}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label note="Helps gather find times you're both free to hang out">Find time with {form.name?.split(" ")[0]||"them"}</Label>
              <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginBottom:9,lineHeight:1.6}}>
                If {form.name?.split(" ")[0]||"they"} are on gather, add their email address above and their calendar syncs automatically. Until then, you can add their general availability.
              </p>
              <div style={{display:"flex",gap:7,marginBottom:9,flexWrap:"wrap"}}>
                <Chip color={C.blue} soft={C.blueSoft} small active={form.calType==="none"} onClick={()=>setForm(f=>({...f,calType:"none"}))}>Not set</Chip>
                <Chip color={C.blue} soft={C.blueSoft} small active={form.calType==="ics-url"} onClick={()=>setForm(f=>({...f,calType:"ics-url"}))}>They shared a calendar link</Chip>
                <Chip color={C.blue} soft={C.blueSoft} small active={form.calType==="manual"} onClick={()=>setForm(f=>({...f,calType:"manual"}))}>I know their general schedule</Chip>
              </div>
              {form.calType==="ics-url"&&(
                <>
                  <div style={{background:C.tint,borderRadius:8,padding:"9px 12px",marginBottom:8}}>
                    <p style={{fontSize:12,fontWeight:600,color:C.ink2,fontFamily:C.body,marginBottom:4}}>How to get their calendar link:</p>
                    <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,lineHeight:1.7}}>
                      <b>Google:</b> Settings → click calendar name → "Secret address in iCal format"<br/>
                      <b>Apple/iCloud:</b> Calendar app → right-click calendar → Share → "Public Calendar"<br/>
                      <b>Outlook:</b> Settings → View all → Calendar → Shared calendars → Publish → ICS link
                    </p>
                  </div>
                  <Field value={form.calUrl} onChange={v=>setForm(f=>({...f,calUrl:v}))} placeholder="https://… or webcal://…"/>
                </>
              )}
              {form.calType==="manual"&&(
                <>
                  <p style={{fontSize:13,color:C.ink3,fontFamily:C.body,marginBottom:6}}>Just describe when they're usually free — gather will use this when suggesting hangout times.</p>
                  <Field value={form.manualSlots} onChange={v=>setForm(f=>({...f,manualSlots:v}))} multi placeholder={"Mon–Fri afternoons\nWeekend mornings\nNot Thursdays"}/>
                </>
              )}
            </div>
          </div>
          <div style={{display:"flex",gap:8,marginTop:18}}>
            <Btn onClick={save} disabled={!form.name.trim()}>Save contact</Btn>
            <Btn onClick={close} variant="ghost">Cancel</Btn>
          </div>
        </Card>
      )}

      {/* First-group nudge */}
      {groups.length===0&&contacts.length>=2&&filter==="all"&&!sq&&!gMode&&(
        <div style={{background:`linear-gradient(135deg,#ede9fe,#f5f3ff)`,border:`1.5px solid #c4b5fd`,borderRadius:14,padding:"16px 18px",marginBottom:14}}>
          <p style={{fontFamily:C.display,fontSize:15,fontWeight:700,color:"#5b21b6",marginBottom:4}}>Create your first group</p>
          <p style={{fontSize:13,color:"#6d28d9",fontFamily:C.body,lineHeight:1.6,marginBottom:12}}>
            Group your people — family, a friend crew, coworkers — and gather can plan hangouts for everyone at once.
          </p>
          <Btn onClick={()=>{setGForm({name:"",emoji:"👥",contactIds:[],notes:""});setGMode("add");}} variant="ghost" small>+ Create a group →</Btn>
        </div>
      )}

      {/* Groups strip */}
      {groups.length>0&&filter==="all"&&!sq&&(
        <div style={{marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <p style={{fontSize:15,fontWeight:700,color:C.ink,fontFamily:C.display}}>Your groups</p>
            <Btn onClick={()=>{setGForm({name:"",emoji:"👥",contactIds:[],notes:""});setGMode("add");}} variant="ghost" small>+ New</Btn>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:7}}>
            {groups.map(g=>{
              const members=contacts.filter(c=>g.contactIds.includes(c.id));
              const worstDays=members.reduce((max,c)=>{const d=daysAgo(c.lastSeen);return d>max?d:max;},0);
              const healthColor=worstDays<14?C.green:worstDays<30?C.amber:C.red;
              const healthLabel=worstDays<14?"Active":worstDays<30?"Worth a gather":"It's been a while";
              const showHealth=members.length>0&&worstDays<999;
              const isExpanded=expandedGroupId===g.id;
              const chatParticipants=[myUid,...members.filter(c=>c.gatherId).map(c=>c.gatherId)];
              return (
                <div key={g.id} style={{background:C.white,border:`1.5px solid ${isExpanded?C.blueBdr:C.line}`,borderRadius:12,overflow:"hidden",transition:"border-color .15s"}}>
                  {/* Summary row — tap to expand */}
                  <div className="tap" onClick={()=>setExpandedGroupId(isExpanded?null:g.id)}
                    style={{display:"flex",alignItems:"center",gap:12,padding:"11px 14px",cursor:"pointer"}}>
                    <div style={{width:36,height:36,borderRadius:10,background:isExpanded?C.blueSoft:C.tint,border:`1.5px solid ${isExpanded?C.blueBdr:C.line}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0,transition:"all .15s"}}>{g.emoji}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:2}}>{g.name}</p>
                      <p style={{fontSize:12,color:C.ink3,fontFamily:C.body}}>
                        {members.length} {members.length===1?"person":"people"}
                        {members.length>0&&<span> · {members.map(c=>c.name.split(" ")[0]).slice(0,3).join(", ")}{members.length>3?` +${members.length-3}`:""}</span>}
                      </p>
                    </div>
                    {showHealth&&!isExpanded&&<Pill color={healthColor} soft={healthColor+"18"}>{healthLabel}</Pill>}
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{transition:"transform .2s",transform:isExpanded?"rotate(180deg)":"none",flexShrink:0}}><path d="M6 9l6 6 6-6" stroke={C.ink3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  {/* Expanded panel */}
                  {isExpanded&&(
                    <div style={{borderTop:`1px solid ${C.line}`,padding:"12px 14px",background:C.tint,display:"flex",flexDirection:"column",gap:10}}>
                      {/* Member avatars */}
                      {members.length>0&&(
                        <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                          {members.map(m=>(
                            <div key={m.id} style={{display:"flex",alignItems:"center",gap:6}}>
                              <Av name={m.name} size={26}/>
                              <span style={{fontSize:12,color:C.ink2,fontFamily:C.body}}>{m.name.split(" ")[0]}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Primary actions */}
                      <div style={{display:"flex",gap:7}}>
                        {onOpenChat&&chatParticipants.length>1&&(
                          <button onClick={()=>onOpenChat("group_"+g.id,`${g.emoji} ${g.name}`,chatParticipants,"",members.filter(m=>!m.gatherId))}
                            style={{flex:1,background:C.blue,color:"#fff",border:"none",borderRadius:9,padding:"9px 12px",cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:C.body,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            Chat
                          </button>
                        )}
                        {members.length>0&&members.every(m=>!m.gatherId)&&(
                          <button onClick={()=>{setExpandedGroupId(null);setTimeout(()=>{setGForm(g);setGMode(g.id);},50);}}
                            style={{flex:1,background:"#fff",color:C.ink2,border:`1.5px solid ${C.line}`,borderRadius:9,padding:"9px 12px",cursor:"pointer",fontSize:13,fontFamily:C.body,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                            Invite members to gather
                          </button>
                        )}
                      </div>
                      {/* Secondary actions — creator only */}
                      {(!g.createdBy||g.createdBy===myUid)&&(
                        <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                          <button onClick={()=>{setExpandedGroupId(null);setGForm(g);setGMode(g.id);}}
                            style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.ink3,fontFamily:C.body,padding:"4px 8px"}}>Edit</button>
                          <button onClick={()=>{onDeleteGroup&&onDeleteGroup(g.id);setExpandedGroupId(null);}}
                            style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.red,fontFamily:C.body,padding:"4px 8px"}}>Delete</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <Hr/>
        </div>
      )}

      {/* List */}
      {filtered.length===0
        ?(sq
          ?<EmptyState icon="⌕" title="No matches" note="Try a different search term."/>
          :contacts.length===0
            ?(
              <div className="i" style={{textAlign:"center",padding:"48px 24px"}}>
                <div style={{fontSize:44,marginBottom:14}}>👋</div>
                <p style={{fontFamily:C.display,fontSize:22,fontWeight:700,color:C.ink,marginBottom:8}}>Add the people who matter</p>
                <p style={{fontSize:13,color:C.ink3,lineHeight:1.7,maxWidth:280,margin:"0 auto 22px",fontFamily:C.body}}>gather works best when it knows your people. Start by adding 3–5 friends or family members you want to stay close with.</p>
                <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
                  <Btn onClick={openAdd} variant="primary">+ Add contact</Btn>
                  {contactPickerSupported
                    ?<Btn onClick={pickFromPhone} variant="soft">📱 From phone</Btn>
                    :<Btn onClick={()=>vcfRef.current?.click()} variant="soft">📎 Upload .vcf</Btn>}
                </div>
              </div>
            )
            :<EmptyState icon="○" title="No contacts in this group" note="Switch to 'All' or add more contacts."/>
        )
        :(
          <div style={{display:"flex",flexDirection:"column",gap:7}}>
            {filtered.map((c,idx)=>{
              const fs=fupStatus(c);
              const bs=bdayStatus(c.birthday);
              const ct=TYPES[c.type]||TYPES.friend;
              const st=NURTURE[c.stage];
              const isOpen=openContactId===c.id;
              return (
                <div key={c.id} className={`u d${Math.min(idx+1,5)}`} style={{background:C.white,border:`1.5px solid ${isOpen?C.blue:C.line}`,borderRadius:14,overflow:"hidden",boxShadow:isOpen?"0 6px 24px rgba(37,99,235,.11)":"0 1px 4px rgba(37,99,235,.05)",transition:"border-color .2s,box-shadow .2s"}}>

                  {/* Tappable summary row */}
                  <div className="tap" onClick={()=>setOpenContactId(isOpen?null:c.id)} style={{padding:"13px 16px",display:"flex",alignItems:"center",gap:12,cursor:"pointer"}}>
                    <Av name={c.name} size={42}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:4}}>
                        <span style={{fontSize:14,fontWeight:600,color:C.ink,fontFamily:C.body}}>{c.name}</span>
                        {bs&&<Pill color={bs.color} soft={bs.soft}>🎂</Pill>}
                        {c.type!=="friend"&&c.type!=="family"&&st&&<Pill color={st.color} soft={st.soft}>{st.label}</Pill>}
                      </div>
                      <p style={{fontSize:13,color:C.ink3,fontFamily:C.body}}>
                        <span style={{color:ct.color}}>{ct.dot}</span>
                        {" "}{ct.label}
                        {c.metAt&&c.metAt!=="gather invite"&&c.metAt!=="Joined via gather invite"&&<span> · {c.metAt}</span>}
                        {c.hobbies?.length>0&&<span style={{marginLeft:6}}>{c.hobbies.slice(0,4).map(h=>hobbyEmoji(h)).join(" ")}</span>}
                      </p>
                    </div>
                    <div style={{width:26,height:26,borderRadius:"50%",background:C.tint,display:"flex",alignItems:"center",justifyContent:"center",transition:"transform .25s cubic-bezier(.16,1,.3,1)",transform:isOpen?"rotate(180deg)":"none",flexShrink:0}}>
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 4l4 4 4-4" stroke={C.ink3} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                  </div>

                  {/* Expanded detail panel */}
                  {isOpen&&(
                    <div className="p" style={{borderTop:`1.5px solid ${C.line}`,padding:"14px 16px 16px"}}>

                      {/* Primary actions */}
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
                        <Btn onClick={()=>{onReachOut&&onReachOut(c.id);}} variant="primary" small>👋 Reach out</Btn>
                        <Btn onClick={()=>{openEdit(c);setOpenContactId(null);}} variant="ghost" small>Edit</Btn>
                        <Btn onClick={()=>exportVCF(c)} variant="ghost" small>💾 Save to phone</Btn>
                        {deleteId===c.id
                          ?<div style={{display:"flex",gap:6,alignItems:"center"}}>
                            <Btn onClick={()=>{onDeleteContact(c.id);setDeleteId(null);setOpenContactId(null);}} variant="red" small>Confirm delete</Btn>
                            <Btn onClick={()=>setDeleteId(null)} variant="ghost" small>Cancel</Btn>
                          </div>
                          :<Btn onClick={()=>remove(c.id)} variant="red" small>✕ Remove</Btn>
                        }
                      </div>

                      {/* Key details */}
                      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:c.notes?12:0}}>
                        {(c.phone||c.email)&&(
                          <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
                            {c.phone&&<a href={`tel:${c.phone}`} style={{fontSize:13,color:C.blue,fontFamily:C.body,textDecoration:"none",display:"flex",alignItems:"center",gap:5}}><span>📞</span>{c.phone}</a>}
                            {c.email&&<a href={`mailto:${c.email}`} style={{fontSize:13,color:C.blue,fontFamily:C.body,textDecoration:"none",display:"flex",alignItems:"center",gap:5}}><span>✉️</span>{c.email}</a>}
                          </div>
                        )}
                        <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
                          {c.birthday&&<span style={{fontSize:12,color:C.ink3,fontFamily:C.body}}>🎂 {new Date(c.birthday+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</span>}
                          {(c.touchCount||0)>0&&<span style={{fontSize:12,color:C.ink3,fontFamily:C.body}}>🤝 {c.touchCount}×</span>}
                        </div>
                      </div>

                      {/* Notes */}
                      {c.notes&&c.notes!=="Connected via gather invite"&&c.notes!=="Joined gather via your invite"&&(
                        <div style={{background:C.tint,borderRadius:9,padding:"9px 12px",marginBottom:10,border:`1px solid ${C.line}`}}>
                          <p style={{fontSize:12,color:C.ink2,fontFamily:C.body,lineHeight:1.6}}>{c.notes}</p>
                        </div>
                      )}

                      {/* Quick interests — tap to tag what they're into */}
                      <div style={{marginBottom:12}}>
                        <p style={{fontSize:11,color:C.ink4,fontFamily:C.body,fontWeight:600,marginBottom:7,textTransform:"uppercase",letterSpacing:".4px"}}>What's {c.name.split(" ")[0]} into?</p>
                        <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                          {Object.entries(HOBBIES).map(([h,emoji])=>{
                            const active=(c.hobbies||[]).includes(h);
                            return (
                              <span key={h} onClick={()=>{
                                const next=active?(c.hobbies||[]).filter(x=>x!==h):[...(c.hobbies||[]),h];
                                onUpdate({...c,hobbies:next});
                              }} style={{display:"inline-flex",alignItems:"center",gap:3,padding:"4px 10px",borderRadius:20,cursor:"pointer",fontSize:12,fontFamily:C.body,background:active?C.blueSoft:C.tint,color:active?C.blue:C.ink3,border:`1px solid ${active?C.blueBdr:C.line}`,fontWeight:active?600:400,transition:"all .12s",WebkitUserSelect:"none",userSelect:"none"}}>
                                {emoji} {hobbyLabel(h)}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      {/* On Gather badge — tap Say Hi to chat */}
                      {c.gatherId&&c.gatherId!==myUid&&(
                        <div style={{borderTop:`1.5px solid ${C.line}`,paddingTop:10,marginTop:4,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                          <span style={{fontSize:12,color:C.green,fontFamily:C.body,fontWeight:500}}>✓ On Gather</span>
                          <button onClick={()=>onReachOut(c.id)} style={{background:C.blue,color:"#fff",border:"none",borderRadius:8,padding:"6px 14px",cursor:"pointer",fontFamily:C.body,fontSize:12,fontWeight:600}}>
                            Message →
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      }
    </div>
  );
}

/* ── Shared calendar helper (used by Overview + PlanTab) ───────────────────── */
async function getContactBusy(contact){
  // 1. Best case: contact is on Gather — pull their live busy blocks
  if(contact.email){
    try{
      const emailDoc=await getDoc(doc(db,"emails",contact.email.toLowerCase()));
      if(emailDoc.exists()){
        const uid=emailDoc.data().uid;
        const userDoc=await getDoc(doc(db,"users",uid));
        if(userDoc.exists()) return userDoc.data().busyBlocks||null;
      }
    }catch(e){}
  }
  // 2. Fallback: they shared an iCal/ICS feed URL — fetch via server proxy
  if(contact.calType==="ics-url"&&contact.calUrl){
    try{
      const r=await fetch("/api/ical-proxy",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({url:contact.calUrl}),
      });
      if(r.ok){
        const data=await r.json();
        return data.events||null;
      }
    }catch(e){}
  }
  return null;
}

/* ── Plan ──────────────────────────────────────────────────────────────────── */
function PlanTab({contacts,groups,myProfile,busyBlocks}) {
  const [selGroup,setSelGroup]=useState(null);
  const [constraints,setConstraints]=useState("");
  const [result,setResult]=useState(null);
  const [loading,setLoading]=useState(false);
  const [calStatus,setCalStatus]=useState({});
  const [lookAhead,setLookAhead]=useState(14);
  const [shareSent,setShareSent]=useState(false);

  const activeIds=selGroup?(groups.find(g=>g.id===selGroup)?.contactIds||[]):[];

  async function find() {
    setLoading(true);setResult(null);
    const group=contacts.filter(c=>activeIds.includes(c.id));
    // Shared = interests that appear in EVERYONE's list including the user's own
    const allParticipants=[[...(myProfile.hobbies||[])], ...group.map(c=>c.hobbies||[])];
    const shared=allParticipants.reduce((acc,hobbies,i)=>i===0?[...hobbies]:acc.filter(h=>hobbies.includes(h)),[]);
    const allH=[...new Set(group.flatMap(c=>c.hobbies||[]).concat(myProfile.hobbies||[]))];
    const hasLead=group.some(c=>c.type==="lead");

    // Fetch calendar data for contacts who are on Gather
    const contactBusyArrays=[];
    const calStatusUpdate={};
    for(const c of group){
      const busy=await getContactBusy(c);
      if(busy){ contactBusyArrays.push(busy); calStatusUpdate[c.id]="found"; }
      else calStatusUpdate[c.id]="none";
    }
    setCalStatus(calStatusUpdate);

    // Find mutual free windows if we have calendar data
    const allContactBusy=contactBusyArrays.flat();
    const freeWindows=busyBlocks.length>0||allContactBusy.length>0
      ? findFreeWindows(busyBlocks, allContactBusy, lookAhead)
      : [];

    // Fallback: manual availability from contact records
    const manualAvail=group
      .filter(c=>calStatusUpdate[c.id]==="none"&&c.calType==="manual"&&c.manualSlots?.length)
      .map(c=>`${c.name}: ${c.manualSlots.join(", ")}`).join("\n");

    const hasCalData=busyBlocks.length>0||allContactBusy.length>0;
    const availSection=freeWindows.length>0
      ? `Best available windows from calendar data (lighter periods identified): ${freeWindows.join(", ")}`
      : hasCalData
        ? `Calendar checked — schedules are very full. Suggest 3 creative times: early mornings (7–9am), weekday lunches (12–1pm), or weekend slots. Keep suggestions realistic and short — coffee, a walk, a quick catch-up.`
        : manualAvail
          ? `Manual availability:\n${manualAvail}`
          : "No calendar data — suggest 3 realistic times based on a typical busy week.";

    const res=await callAIJSON(`Plan hangouts for ${myProfile.name||"me"}.
${constraints?`My extra constraints: ${constraints}\n`:""}Group: ${group.map(c=>`${c.name} (${c.type})`).join(", ")}
${availSection}
Shared interests (both people enjoy these — prioritise for activity ideas): ${shared.length>0?shared.map(hobbyLabel).join(", "):"none identified"}
All interests in the group: ${allH.map(hobbyLabel).join(", ")||"general"}
${hasLead?"Mix professional and social.":""}
Base activity ideas on shared interests first. Only draw from individual interests if there's no overlap. Suggest 3 specific, concrete time windows with a tailored activity. Always give real dates and times. If schedules are busy, suggest short meetups: coffee, a walk, a quick lunch. Be optimistic.
Activities must be accessible to everyone — walks, parks, home hangouts, coffee, picnics, game nights, cooking together. Avoid anything that needs a membership, special access, or high cost (no wineries, no country clubs, no expensive restaurants).
Return ONLY a JSON array, no other text: [{"time":"...","activity":"...","why":"one sentence","vibe":"one word","type":"casual|professional|mixed"}]`,
    "You are a scheduling assistant. Respond with ONLY a valid JSON array — no explanation, no markdown, no preamble. Always return exactly 3 items.");
    setResult(res||[]);setLoading(false);setShareSent(false);
    track("plan_generated",{group_size:group.length,has_calendar:busyBlocks.length>0,look_ahead:lookAhead});
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <Card>
        <Label>Who's coming?</Label>
        {groups.length===0?(
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <p style={{fontSize:14,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:6}}>No groups yet</p>
              <p style={{fontSize:13,color:C.ink3,fontFamily:C.body,lineHeight:1.6,marginBottom:14}}>Groups let you plan hangouts for your whole crew at once — family, friends, whoever.</p>
              <button onClick={()=>window.dispatchEvent(new CustomEvent("gather:goto",{detail:"contacts"}))}
                style={{background:C.blue,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",cursor:"pointer",fontFamily:C.body,fontSize:13,fontWeight:600}}>
                Create your first group →
              </button>
            </div>
          ):(
            <div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:10}}>
                {groups.map(g=>(
                  <div key={g.id} className="tap" onClick={()=>setSelGroup(selGroup===g.id?null:g.id)} style={{display:"flex",alignItems:"center",gap:9,padding:"9px 14px",borderRadius:10,cursor:"pointer",background:selGroup===g.id?C.blueSoft:C.tint,border:`1.5px solid ${selGroup===g.id?C.blueBdr:C.line}`,transition:"all .15s"}}>
                    <span style={{fontSize:16}}>{g.emoji}</span>
                    <p style={{fontSize:13,fontWeight:selGroup===g.id?600:400,color:selGroup===g.id?C.blue:C.ink,fontFamily:C.body}}>{g.name}</p>
                  </div>
                ))}
              </div>
              <button onClick={()=>window.dispatchEvent(new CustomEvent("gather:goto",{detail:"contacts"}))}
                style={{background:"none",border:`1.5px dashed ${C.lineM}`,borderRadius:9,padding:"7px 14px",cursor:"pointer",fontFamily:C.body,fontSize:12,color:C.ink3,display:"inline-flex",alignItems:"center",gap:5}}>
                + Create new group
              </button>
            </div>
          )
        }
      </Card>
      {/* Calendar status for selected contacts */}
      {activeIds.length>0&&(
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {contacts.filter(c=>activeIds.includes(c.id)).map(c=>(
            <div key={c.id} style={{display:"flex",alignItems:"center",gap:5,background:calStatus[c.id]==="found"?C.greenSoft:C.tint,border:`1px solid ${calStatus[c.id]==="found"?"rgba(22,163,74,.2)":C.line}`,borderRadius:20,padding:"3px 10px"}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:calStatus[c.id]==="found"?C.green:C.ink3,flexShrink:0}}/>
              <span style={{fontSize:13,color:calStatus[c.id]==="found"?C.green:C.ink3,fontFamily:C.body,fontWeight:500}}>{c.name}</span>
            </div>
          ))}
          {!myProfile.calendarConnected&&(
            <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,width:"100%",marginTop:4}}>💡 Connect your calendar in Profile for smarter suggestions · 🔒 Free/busy only, never event details</p>
          )}
        </div>
      )}
      <Card>
        <Label note="How far ahead should Gather look for free time?">Planning window</Label>
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {[[7,"This week"],[14,"Next 2 weeks"],[30,"Next month"],[60,"2 months"],[90,"3 months"]].map(([days,label])=>(
            <Chip key={days} color={C.blue} soft={C.blueSoft} small active={lookAhead===days} onClick={()=>setLookAhead(days)}>
              {label}
            </Chip>
          ))}
        </div>
      </Card>
      <Card>
        <Label note="Anything not on your calendar">Extra constraints</Label>
        <Field value={constraints} onChange={setConstraints} multi placeholder="e.g. never Thursday evenings, avoid school holidays…"/>
      </Card>
      {activeIds.length===0&&(
        <p style={{fontSize:13,color:C.ink3,fontFamily:C.body,textAlign:"center",padding:"4px 0 8px",fontStyle:"italic"}}>
          👆 Pick at least one person above to find a time
        </p>
      )}
      <Btn onClick={find} disabled={activeIds.length===0} loading={loading} full>Find the best times</Btn>
      {result&&(
        <div className="u">
          {result.length===0
            ? <div style={{textAlign:"center",padding:"8px 0"}}>
                <p style={{fontSize:13,color:C.ink3,fontFamily:C.body,marginBottom:12}}>Couldn't generate suggestions — try tapping the button again, or extend your planning window above.</p>
                <Btn onClick={find} variant="ghost" small>Try again →</Btn>
              </div>
            : <>
                <div style={{display:"flex",flexDirection:"column",gap:9}}>
                  {result.map((r,i)=>{
                    const group=contacts.filter(c=>activeIds.includes(c.id));
                    const names=group.map(c=>c.name.split(" ")[0]).join(" & ");
                    const phones=group.map(c=>c.phone).filter(Boolean);
                    function shareOption(){
                      const msg=`Hey ${names}! 👋 How does this work for you?\n\n📅 ${r.time}\n✨ ${r.activity}\n\nLet me know!`;
                      const to=phones.length===1?phones[0].replace(/\s/g,""):"";
                      window.open(`sms:${to}?&body=${encodeURIComponent(msg)}`,"_self");
                    }
                    return (
                      <Card key={i} style={{gap:0}}>
                        <div style={{display:"flex",gap:14,alignItems:"flex-start",marginBottom:12}}>
                          <div style={{width:42,height:42,borderRadius:10,background:C.blueSoft,border:`1.5px solid ${C.blueBdr}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>
                            {["☀️","🌿","🌙"][i%3]}
                          </div>
                          <div style={{flex:1}}>
                            <p style={{fontFamily:C.display,fontSize:16,fontWeight:700,color:C.ink,marginBottom:3}}>{r.time}</p>
                            <p style={{fontSize:13,fontWeight:600,color:C.blue,marginBottom:4,fontFamily:C.body}}>{r.activity}</p>
                            <p style={{fontSize:12,color:C.ink3,fontFamily:C.body}}>{r.why} · <em>{r.vibe}</em></p>
                          </div>
                        </div>
                        <Btn onClick={shareOption} variant="soft" small full>📱 Text this to {names||"them"}</Btn>
                      </Card>
                    );
                  })}
                </div>
                {/* Share all options at once */}
                {(()=>{
                  const group=contacts.filter(c=>activeIds.includes(c.id));
                  const names=group.map(c=>c.name.split(" ")[0]).join(" & ");
                  const phones=group.map(c=>c.phone).filter(Boolean);
                  function shareAll(){
                    const options=result.map((r,i)=>`${["1️⃣","2️⃣","3️⃣"][i]} ${r.time} — ${r.activity}`).join("\n");
                    const msg=`Hey ${names}! 👋 Trying to find a time to get together. Which of these works for you?\n\n${options}\n\nLet me know!`;
                    const to=phones.length===1?phones[0].replace(/\s/g,""):"";
                    window.open(`sms:${to}?&body=${encodeURIComponent(msg)}`,"_self");
                    setShareSent(true);
                  }
                  return shareSent
                    ? <div style={{background:C.greenSoft,border:`1.5px solid ${C.greenBdr||"#86efac"}`,borderRadius:12,padding:"14px 16px",textAlign:"center"}}>
                        <p style={{fontSize:15,fontWeight:600,color:"#166534",fontFamily:C.body,marginBottom:4}}>✓ Sent to {names||"everyone"}!</p>
                        <p style={{fontSize:12,color:"#166534",fontFamily:C.body,opacity:.8}}>Waiting to hear back? We'll remind you to follow up.</p>
                        <button onClick={()=>setShareSent(false)} style={{marginTop:10,background:"none",border:`1px solid #86efac`,borderRadius:8,padding:"6px 14px",fontSize:12,color:"#166534",fontFamily:C.body,cursor:"pointer"}}>Send again</button>
                      </div>
                    : <Btn onClick={shareAll} variant="primary" full icon="📱">Send all options to {names||"everyone"}</Btn>;
                })()}
              </>
          }
        </div>
      )}
    </div>
  );
}

/* ── Playdates ─────────────────────────────────────────────────────────────── */

function kidScheduleToBusyBlocks(kid, days=14){
  if(!kid) return [];
  const blocks=[];
  const now=new Date();
  const DAY_KEYS=['sun','mon','tue','wed','thu','fri','sat'];
  for(let d=0;d<days;d++){
    const date=new Date(now);
    date.setDate(date.getDate()+d);
    const dow=date.getDay();
    const dk=DAY_KEYS[dow];
    // School hours Mon–Fri
    if(kid.schoolHours&&dow>=1&&dow<=5){
      const s=new Date(date); s.setHours(8,0,0,0);
      const e=new Date(date); e.setHours(15,0,0,0);
      if(e>now) blocks.push({start:s.toISOString(),end:e.toISOString()});
    }
    // Nap window
    if(kid.napStart&&kid.napEnd&&kid.napStart<kid.napEnd){
      const s=new Date(date); s.setHours(kid.napStart,0,0,0);
      const e=new Date(date); e.setHours(kid.napEnd,0,0,0);
      if(e>now) blocks.push({start:s.toISOString(),end:e.toISOString()});
    }
    // Activity blocks
    for(const ab of (kid.activityBlocks?.[dk]||[])){
      if(ab.start!=null&&ab.end!=null&&ab.start<ab.end){
        const s=new Date(date); s.setHours(ab.start,0,0,0);
        const e=new Date(date); e.setHours(ab.end,0,0,0);
        if(e>now) blocks.push({start:s.toISOString(),end:e.toISOString()});
      }
    }
  }
  return blocks;
}

const SCHED_HOURS=[7,8,9,10,11,12,13,14,15,16,17,18,19,20].map(h=>({v:h,l:h<12?`${h}am`:h===12?"12pm":`${h-12}pm`}));
const SCHED_DAYS=[{k:"mon",l:"Mon"},{k:"tue",l:"Tue"},{k:"wed",l:"Wed"},{k:"thu",l:"Thu"},{k:"fri",l:"Fri"},{k:"sat",l:"Sat"},{k:"sun",l:"Sun"}];
// Keep for nap picker (narrower range)
const NAP_HOURS=[9,10,11,12,13,14,15,16,17].map(h=>({v:h,l:h<12?`${h}am`:h===12?"12pm":`${h-12}pm`}));

const EFFORT=[
  {id:"low",  label:"Easy",   emoji:"🌳", desc:"Park, playground, quick hangout",
   ideas:[
     {label:"Park",           emoji:"🌳",msg:"Quick park run?"},
     {label:"Playground",     emoji:"🛝",msg:"Playground after school?"},
     {label:"Backyard",       emoji:"🏡",msg:"Backyard hang at ours?"},
     {label:"Coffee + kids",  emoji:"☕",msg:"Coffee at ours while the kids play?"},
     {label:"School pickup",  emoji:"🎒",msg:"Walk home from school together?"},
   ]},
  {id:"medium",label:"Day out",emoji:"🏖️",desc:"Beach, museum, brunch outing",
   ideas:[
     {label:"Beach",          emoji:"🏖️",msg:"Beach morning together?"},
     {label:"Zoo",            emoji:"🦁",msg:"Zoo day?"},
     {label:"Splash pad",     emoji:"💦",msg:"Splash pad before it gets too hot?"},
     {label:"Museum",         emoji:"🏛️",msg:"Kids museum?"},
     {label:"Brunch",         emoji:"🥞",msg:"Brunch all together?"},
   ]},
  {id:"high",  label:"Big one",emoji:"🎉",desc:"Birthday, camping, day trip",
   ideas:[
     {label:"Day trip",       emoji:"🚗",msg:"Day trip together?"},
     {label:"Birthday party", emoji:"🎂",msg:"Birthday party — save the date!"},
     {label:"Camping",        emoji:"⛺",msg:"Weekend camping trip?"},
     {label:"Picnic",         emoji:"🧺",msg:"Big family picnic?"},
   ]},
];

function PlaydatesTab({myProfile,myKids,partnerKids,onAddKid,onUpdateKid,onDeleteKid,pdc,onAddFamily,onUpdateFamily,onDeleteFamily,myUid,busyBlocks,contacts,onSaveProfile}){
  const [view,setView]=useState("home"); // "home"|"families"|"kids"
  const [effortFilter,setEffortFilter]=useState("low");
  const [gatherFamilies,setGatherFamilies]=useState([]);
  const [planningFamily,setPlanningFamily]=useState(null);
  const [planResult,setPlanResult]=useState(null);
  const [planLoading,setPlanLoading]=useState(false);
  const [copied,setCopied]=useState(null);
  const [outreachId,setOutreachId]=useState(null);

  // Circles state
  const circles=myProfile.familyCircles||[];
  const [circleForm,setCircleForm]=useState(null); // null | {name:"",emoji:"🌳",familyIds:[]}
  const CIRCLE_EMOJIS=["🌳","🏖️","⚽","🎒","🍕","🎉","🏊","🎨","🎭","🌿"];
  function saveCircle(c){
    const updated=c.id?circles.map(x=>x.id===c.id?c:x):[...circles,{...c,id:Date.now().toString()}];
    onSaveProfile?.({...myProfile,familyCircles:updated});
    setCircleForm(null);
  }
  function deleteCircle(id){
    onSaveProfile?.({...myProfile,familyCircles:circles.filter(c=>c.id!==id)});
  }

  // Kid / family form state
  const bk={name:"",age:"",interests:[],energy:"medium",allergies:"",notes:"",schoolHours:false,napStart:null,napEnd:null,activityBlocks:{mon:[],tue:[],wed:[],thu:[],fri:[],sat:[],sun:[]}};
  const [kidMode,setKidMode]=useState(null);
  const [kf,setKf]=useState(bk);
  const [pdcMode,setPdcMode]=useState(null);
  const [pf,setPf]=useState({parentName:"",parentPhone:"",kids:[]});
  const [pkf,setPkf]=useState(bk);

  function saveKid(){const k={...kf,age:parseInt(kf.age)||0};if(kidMode==="add")onAddKid(k);else onUpdateKid(kidMode,k);setKidMode(null);}
  function savePdc(){const r={...pf};if(pdcMode==="add")onAddFamily(r);else onUpdateFamily(pdcMode,r);setPdcMode(null);}
  function addKidToPf(){if(!pkf.name)return;setPf(f=>({...f,kids:[...f.kids,{...pkf,age:parseInt(pkf.age)||0,id:Date.now()}]}));setPkf(bk);}

  const allMyKids=[...myKids,...partnerKids];

  // Fetch families for Gather-connected contacts who have kids in their profile
  useEffect(()=>{
    const withGather=(contacts||[]).filter(c=>c.gatherId);
    if(withGather.length===0){setGatherFamilies([]);return;}
    Promise.all(withGather.map(async c=>{
      try{
        const [kidsSnap,userSnap]=await Promise.all([
          getDocs(collection(db,"users",c.gatherId,"kids")),
          getDoc(doc(db,"users",c.gatherId)),
        ]);
        const kids=kidsSnap.docs.map(d=>({...d.data(),id:d.id}));
        const userData=userSnap.exists()?userSnap.data():{};
        return {id:c.gatherId,parentName:c.name,parentPhone:c.phone||"",gatherId:c.gatherId,kids,busyBlocks:userData.busyBlocks||[],isGatherFamily:true};
      }catch{return null;}
    })).then(fams=>setGatherFamilies(fams.filter(f=>f&&f.kids.length>0)));
  },[contacts]); // eslint-disable-line

  // All families combined — Gather-connected first, then manual
  const allFamilies=useMemo(()=>{
    const manualIds=new Set(pdc.map(p=>p.id));
    const gatherOnly=gatherFamilies.filter(f=>!manualIds.has(f.id));
    return [...gatherOnly,...pdc];
  },[pdc,gatherFamilies]);

  // Families sorted most-overdue first
  const sortedFamilies=useMemo(()=>
    [...allFamilies].sort((a,b)=>{
      if(!a.lastPlaydate&&!b.lastPlaydate) return 0;
      if(!a.lastPlaydate) return -1;
      if(!b.lastPlaydate) return 1;
      return new Date(a.lastPlaydate)-new Date(b.lastPlaydate);
    })
  ,[allFamilies]);

  // My kids' schedule blocks (school hours + nap + activities)
  const myKidBlocks=useMemo(()=>allMyKids.flatMap(k=>kidScheduleToBusyBlocks(k)),[allMyKids]);

  // My combined busy (calendar + kid schedules)
  const myBusyFull=useMemo(()=>[...(busyBlocks||[]),...myKidBlocks],[busyBlocks,myKidBlocks]);

  // Structured free slots with ISO times for overlap detection
  const myFreeSlotsFull=useMemo(()=>freeWindowSlots(myBusyFull,[],14),[myBusyFull]);

  // Simple string list for display / AI prompts
  const myFreeSlots=useMemo(()=>myFreeSlotsFull.map(s=>s.str),[myFreeSlotsFull]);

  // Windows where ≥1 Gather family overlaps (accounting for their kids' naps too)
  const overlapGroups=useMemo(()=>{
    if(!myFreeSlotsFull.length||!allFamilies.length) return [];
    return myFreeSlotsFull.map(slot=>{
      const free=allFamilies.filter(family=>{
        if(!family.isGatherFamily) return false;
        const famKidBlocks=(family.kids||[]).flatMap(k=>kidScheduleToBusyBlocks(k));
        const famBusy=[...(family.busyBlocks||[]),...famKidBlocks];
        if(!famBusy.length) return false;
        return memberAvail(famBusy,slot.startISO,slot.endISO)==="free";
      });
      return {slot,families:free};
    }).filter(g=>g.families.length>0).slice(0,4);
  },[myFreeSlotsFull,allFamilies]);

  // Quick outreach message builder
  function quickMsg(family,effortItem,slotStr){
    const kidNames=family.kids.map(k=>k.name).join(" and ");
    const myKidNames=allMyKids.slice(0,2).map(k=>k.name).join(" and ")||"the kids";
    const rawFirst=family.parentName.split(" ")[0];
    const parentFirst=rawFirst.includes("@")?"there":rawFirst;
    const windowShort=slotStr?slotStr.replace(/ \(.*?\)/,"").replace(/,.*$/,""):"this week";
    return `Hey ${parentFirst}! ${effortItem.msg.replace("?","")} — ${windowShort} with ${myKidNames} and ${kidNames}?`;
  }

  async function generatePlan(family,effortId){
    setPlanningFamily({family,effortId});
    setPlanResult(null);
    setPlanLoading(true);
    const effort=EFFORT.find(e=>e.id===effortId);
    const myKidDesc=allMyKids.slice(0,3).map(k=>`${k.name} (age ${k.age||"?"}, ${(k.interests||[]).join(",")||"general play"})`).join("; ");
    const theirDesc=family.kids.map(k=>`${k.name} (age ${k.age||"?"},${(k.interests||[]).join(",")||"general play"})`).join("; ");
    const freeWindow=myFreeSlots[0]||"this weekend";
    const res=await callAIJSON(
      `Write a casual parent message and suggest 2 activity ideas for a ${effort.label.toLowerCase()} playdate.
My kids: ${myKidDesc||"young kids"}
Their kids: ${theirDesc||"young kids"}
Effort level: ${effort.label} (${effort.desc})
Free window: ${freeWindow}
Parent: ${family.parentName}
My name: ${myProfile.name||"me"}
Return ONLY JSON: {"msg":"2-3 sentence casual message to their parent, names kids, mentions a time","ideas":[{"activity":"...","emoji":"...","why":"short reason"}]}`,
      "You are a friendly playdate planner. Return ONLY valid JSON — no explanation, no markdown."
    );
    setPlanResult(res);
    setPlanLoading(false);
    onUpdateFamily(family.id,{...family,lastPlaydate:new Date().toISOString().split("T")[0]});
  }

  const SUBS=[{id:"home",l:"Reach out"},{id:"kids",l:"My kids"}];
  const activeEffort=EFFORT.find(e=>e.id===effortFilter)||EFFORT[0];

  // ─── Shared outreach panel (reused in overlap cards + other families) ────────
  function OutreachPanel({family,slotStr}){
    const isPlanning=planningFamily?.family?.id===family.id;
    const msg=quickMsg(family,activeEffort.ideas[0],slotStr||myFreeSlots[0]);
    return(
      <div style={{borderTop:`1px solid ${C.line}`,padding:"13px 15px",background:C.tint}}>
        <div style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:10,padding:"11px 13px",marginBottom:10}}>
          <p style={{fontSize:13,color:C.ink,fontFamily:C.body,lineHeight:1.6,marginBottom:10}}>{msg}</p>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {family.parentPhone&&(
              <button onClick={()=>window.open(`sms:${family.parentPhone.replace(/\s/g,"")}?&body=${encodeURIComponent(msg)}`,"_self")}
                style={{background:C.green,color:"#fff",border:"none",borderRadius:7,padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:C.body}}>
                Text →
              </button>
            )}
            <button onClick={()=>{navigator.clipboard?.writeText(msg);setCopied(family.id);setTimeout(()=>setCopied(null),2000);}}
              style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:7,padding:"7px 12px",cursor:"pointer",fontSize:12,color:C.ink3,fontFamily:C.body}}>
              {copied===family.id?"Copied ✓":"Copy"}
            </button>
            <button onClick={()=>generatePlan(family,effortFilter)}
              style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.blue,fontFamily:C.body,padding:"7px 4px",fontWeight:500}}>
              {planLoading&&isPlanning?"Writing…":"✨ Personalise"}
            </button>
          </div>
        </div>
        {isPlanning&&planResult&&(
          <div style={{background:"#fff",border:`1.5px solid ${C.blueBdr}`,borderRadius:10,padding:"12px 14px"}}>
            <p style={{fontSize:13,lineHeight:1.75,color:C.ink,fontFamily:C.body,marginBottom:10,whiteSpace:"pre-wrap"}}>{planResult.msg}</p>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {family.parentPhone&&(
                <button onClick={()=>window.open(`sms:${family.parentPhone.replace(/\s/g,"")}?&body=${encodeURIComponent(planResult.msg)}`,"_self")}
                  style={{background:C.green,color:"#fff",border:"none",borderRadius:7,padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:C.body}}>
                  Text →
                </button>
              )}
              <button onClick={()=>{navigator.clipboard?.writeText(planResult.msg);setCopied("ai_"+family.id);setTimeout(()=>setCopied(null),2000);}}
                style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:7,padding:"7px 12px",cursor:"pointer",fontSize:12,color:C.ink3,fontFamily:C.body}}>
                {copied==="ai_"+family.id?"Copied ✓":"Copy"}
              </button>
              <button onClick={()=>generatePlan(family,effortFilter)}
                style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.ink3,fontFamily:C.body,padding:"7px 4px"}}>
                Try again
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ─── Home view ───────────────────────────────────────────────────────────────
  function HomeView(){
    const hasKids=allMyKids.length>0;
    const hasFamilies=allFamilies.length>0;

    // Families not covered by any overlap window
    const overlapFamilyIds=new Set(overlapGroups.flatMap(g=>g.families.map(f=>f.id)));
    const otherFamilies=sortedFamilies.filter(f=>!overlapFamilyIds.has(f.id));

    // Empty state
    if(!hasKids||!hasFamilies){
      return(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div style={{background:"linear-gradient(135deg,#eff6ff,#f0fdf4)",border:`1.5px solid ${C.line}`,borderRadius:16,padding:"20px 18px"}}>
            <p style={{fontFamily:C.display,fontSize:18,fontWeight:700,color:C.ink,marginBottom:6}}>Get the kids together</p>
            <p style={{fontSize:13,color:C.ink3,fontFamily:C.body,lineHeight:1.7,marginBottom:16}}>Add your kids and a few families — gather spots when everyone's free and makes it easy to reach out.</p>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
              {[
                {emoji:"👶",text:"Add your kids (and nap times if relevant)"},
                {emoji:"👨‍👩‍👧",text:"Add families you hang out with"},
                {emoji:"📅",text:"Gather finds overlapping free windows"},
                {emoji:"💬",text:"One tap to reach out"},
              ].map((s,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:18,flexShrink:0}}>{s.emoji}</span>
                  <p style={{fontSize:13,color:C.ink2,fontFamily:C.body}}>{s.text}</p>
                </div>
              ))}
            </div>
          </div>
          <div style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:14,padding:"14px 16px",opacity:.7}}>
            <p style={{fontSize:11,fontWeight:600,color:C.ink4,textTransform:"uppercase",letterSpacing:".05em",marginBottom:10}}>Example</p>
            <p style={{fontSize:13,fontWeight:600,color:C.ink,marginBottom:4}}>Saturday morning · 2 families free</p>
            <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginBottom:10}}>Rachel (Maya 6) · Tom (Jake 4)</p>
            <div style={{background:C.tint,borderRadius:10,padding:"10px 12px",fontSize:13,color:C.ink2,fontFamily:C.body,lineHeight:1.65,fontStyle:"italic"}}>
              "Hey Rachel! Quick park run Saturday morning with the kids?"
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            {!hasKids&&<Btn onClick={()=>setView("kids")} full variant="primary">Add my kids</Btn>}
            {!hasFamilies&&<Btn onClick={()=>setView("families")} full variant={hasKids?"primary":"ghost"}>Add a family</Btn>}
          </div>
        </div>
      );
    }

    return(
      <div style={{display:"flex",flexDirection:"column",gap:14}}>

        {/* Global effort picker */}
        <div style={{display:"flex",gap:6}}>
          {EFFORT.map(e=>(
            <button key={e.id} onClick={()=>setEffortFilter(e.id)}
              style={{flex:1,padding:"9px 4px",borderRadius:10,border:`1.5px solid ${effortFilter===e.id?C.blueBdr:C.line}`,background:effortFilter===e.id?C.blueSoft:"#fff",cursor:"pointer",fontFamily:C.body,transition:"all .15s",textAlign:"center"}}>
              <div style={{fontSize:16,marginBottom:2}}>{e.emoji}</div>
              <div style={{fontSize:11,fontWeight:600,color:effortFilter===e.id?C.blue:C.ink3}}>{e.label}</div>
              <div style={{fontSize:10,color:C.ink4,lineHeight:1.3,marginTop:1}}>{e.desc}</div>
            </button>
          ))}
        </div>

        {/* Family circles */}
        {(circles.length>0||circleForm)&&(
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <p style={{fontSize:12,fontWeight:600,color:C.ink2,fontFamily:C.body,margin:0}}>Your crews</p>
              {!circleForm&&<button onClick={()=>setCircleForm({name:"",emoji:"🌳",familyIds:[]})} style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.blue,fontFamily:C.body,padding:0,fontWeight:500}}>+ New crew</button>}
            </div>
            {circleForm&&(
              <div style={{background:"#fff",border:`1.5px solid ${C.blueBdr}`,borderRadius:12,padding:"14px 15px"}}>
                <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"center"}}>
                  <div style={{display:"flex",flexWrap:"wrap",gap:5,flex:1}}>
                    {CIRCLE_EMOJIS.map(e=>(
                      <button key={e} onClick={()=>setCircleForm(f=>({...f,emoji:e}))}
                        style={{width:30,height:30,borderRadius:8,border:`1.5px solid ${circleForm.emoji===e?C.blueBdr:C.line}`,background:circleForm.emoji===e?C.blueSoft:"#fff",cursor:"pointer",fontSize:15,display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {e}
                      </button>
                    ))}
                  </div>
                </div>
                <Field value={circleForm.name} onChange={v=>setCircleForm(f=>({...f,name:v}))} placeholder="Crew name (e.g. Friday park crew)"/>
                <div style={{marginTop:10}}>
                  <p style={{fontSize:12,fontWeight:600,color:C.ink2,fontFamily:C.body,marginBottom:7}}>Who's in it?</p>
                  <div style={{display:"flex",flexDirection:"column",gap:5}}>
                    {allFamilies.map(fam=>{
                      const checked=circleForm.familyIds.includes(fam.id);
                      return(
                        <label key={fam.id} style={{display:"flex",alignItems:"center",gap:9,cursor:"pointer",padding:"7px 10px",borderRadius:8,background:checked?C.blueSoft:C.tint,border:`1.5px solid ${checked?C.blueBdr:C.line}`}}>
                          <input type="checkbox" checked={checked} onChange={()=>setCircleForm(f=>({...f,familyIds:checked?f.familyIds.filter(x=>x!==fam.id):[...f.familyIds,fam.id]}))} style={{accentColor:C.blue}}/>
                          <span style={{fontSize:13,fontWeight:500,color:C.ink,fontFamily:C.body}}>{fam.parentName}</span>
                          <span style={{fontSize:11,color:C.ink3,fontFamily:C.body}}>{fam.kids.map(k=>k.name).join(", ")}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div style={{display:"flex",gap:8,marginTop:12}}>
                  <Btn onClick={()=>saveCircle(circleForm)} disabled={!circleForm.name.trim()||circleForm.familyIds.length<2} small>Save crew</Btn>
                  <Btn onClick={()=>setCircleForm(null)} variant="ghost" small>Cancel</Btn>
                </div>
              </div>
            )}
            {circles.map(circle=>{
              const circleFams=allFamilies.filter(f=>circle.familyIds.includes(f.id));
              const isOpen=outreachId==="circle_"+circle.id;
              const circleMsg=`Hey everyone! ${activeEffort.ideas[0].msg.replace("?","")} — ${myFreeSlots[0]?.replace(/ \(.*?\)/,"").replace(/,.*$/,"")||"this week"} with the kids? ${circle.emoji}`;
              return(
                <div key={circle.id} style={{background:"#fff",border:`1.5px solid ${isOpen?C.blueBdr:C.line}`,borderRadius:12,overflow:"hidden",transition:"border-color .2s"}}>
                  <div onClick={()=>setOutreachId(isOpen?null:"circle_"+circle.id)}
                    style={{padding:"12px 14px",cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:20,flexShrink:0}}>{circle.emoji}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,margin:0}}>{circle.name}</p>
                      <p style={{fontSize:11,color:C.ink3,fontFamily:C.body,marginTop:1}}>{circleFams.map(f=>f.parentName.split(" ")[0]).join(" · ")} · {circleFams.length} famil{circleFams.length===1?"y":"ies"}</p>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{transition:"transform .25s",transform:isOpen?"rotate(180deg)":"none",flexShrink:0}}><path d="M6 9l6 6 6-6" stroke={C.ink3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  {isOpen&&(
                    <div style={{borderTop:`1px solid ${C.line}`,padding:"12px 14px",background:C.tint}}>
                      <div style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:10,padding:"11px 13px",marginBottom:10}}>
                        <p style={{fontSize:13,color:C.ink,fontFamily:C.body,lineHeight:1.6,marginBottom:10}}>{circleMsg}</p>
                        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                          <button onClick={()=>{navigator.clipboard?.writeText(circleMsg);setCopied("circle_"+circle.id);setTimeout(()=>setCopied(null),2000);}}
                            style={{background:C.blue,color:"#fff",border:"none",borderRadius:7,padding:"7px 14px",cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:C.body}}>
                            {copied==="circle_"+circle.id?"Copied ✓":"Copy message"}
                          </button>
                          <button onClick={()=>setCircleForm({...circle})}
                            style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:7,padding:"7px 12px",cursor:"pointer",fontSize:12,color:C.ink3,fontFamily:C.body}}>
                            Edit
                          </button>
                          <button onClick={()=>deleteCircle(circle.id)}
                            style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.ink4,fontFamily:C.body,padding:"7px 4px"}}>
                            Remove
                          </button>
                        </div>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",gap:6}}>
                        {circleFams.map(fam=>(
                          <div key={fam.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:"#fff",borderRadius:8,border:`1px solid ${C.line}`}}>
                            <div style={{width:28,height:28,borderRadius:"50%",background:avBg(fam.parentName),display:"flex",alignItems:"center",justifyContent:"center",color:"rgba(255,255,255,.9)",fontWeight:700,fontFamily:C.display,fontSize:10,flexShrink:0}}>{initials(fam.parentName)}</div>
                            <span style={{flex:1,fontSize:12,fontWeight:500,color:C.ink,fontFamily:C.body}}>{fam.parentName}</span>
                            {fam.parentPhone&&(
                              <button onClick={()=>window.open(`sms:${fam.parentPhone.replace(/\s/g,"")}?&body=${encodeURIComponent(circleMsg)}`,"_self")}
                                style={{background:C.green,color:"#fff",border:"none",borderRadius:6,padding:"5px 10px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:C.body}}>
                                Text
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {circles.length===0&&!circleForm&&allFamilies.length>=2&&(
          <button onClick={()=>setCircleForm({name:"",emoji:"🌳",familyIds:[]})}
            style={{background:"none",border:`1.5px dashed ${C.lineM}`,borderRadius:10,padding:"10px 14px",cursor:"pointer",color:C.ink3,fontFamily:C.body,fontSize:12,width:"100%",textAlign:"left"}}>
            + Create a crew — group families you hang out with regularly
          </button>
        )}

        {/* Overlap window cards */}
        {overlapGroups.length>0&&(
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <p style={{fontSize:12,fontWeight:600,color:C.ink2,fontFamily:C.body,margin:0}}>Good times this week</p>
            {overlapGroups.map(({slot,families})=>{
              const parts=slot.str.match(/^(.+?), .+? (morning|afternoon|evening)/);
              const dayLabel=parts?`${parts[1]} ${parts[2]}`:slot.str.replace(/ \(.*?\)/,"");
              const timeLabel=slot.str.match(/\((.+?)\)/)?.[1]||"";
              const isOpen=outreachId===slot.str;
              return(
                <div key={slot.str} style={{background:"#fff",border:`1.5px solid ${isOpen?C.blueBdr:C.line}`,borderRadius:14,overflow:"hidden",transition:"border-color .2s"}}>
                  <div onClick={()=>setOutreachId(isOpen?null:slot.str)}
                    style={{padding:"13px 15px",cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:42,height:42,borderRadius:10,background:"linear-gradient(135deg,#eff6ff,#dbeafe)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}>📅</div>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontSize:14,fontWeight:600,color:C.ink,fontFamily:C.body,margin:0,textTransform:"capitalize"}}>{dayLabel}</p>
                      <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginTop:2}}>
                        {timeLabel&&<span style={{marginRight:6}}>{timeLabel}</span>}
                        <span style={{color:C.green,fontWeight:500}}>
                          {families.length===1?`${families[0].parentName} free`:`${families.length} families free`}
                        </span>
                      </p>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{transition:"transform .25s",transform:isOpen?"rotate(180deg)":"none",flexShrink:0}}><path d="M6 9l6 6 6-6" stroke={C.ink3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  {isOpen&&(
                    <div style={{borderTop:`1px solid ${C.line}`,padding:"12px 15px",background:C.tint,display:"flex",flexDirection:"column",gap:10}}>
                      {families.map(family=>(
                        <div key={family.id} style={{background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:10,overflow:"hidden"}}>
                          <div onClick={()=>setOutreachId(outreachId===family.id?slot.str:family.id)}
                            style={{padding:"10px 13px",cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
                            <div style={{width:32,height:32,borderRadius:"50%",background:avBg(family.parentName),display:"flex",alignItems:"center",justifyContent:"center",color:"rgba(255,255,255,.9)",fontWeight:700,fontFamily:C.display,fontSize:11,flexShrink:0}}>{initials(family.parentName)}</div>
                            <div style={{flex:1,minWidth:0}}>
                              <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,margin:0}}>{family.parentName}</p>
                              <p style={{fontSize:11,color:C.ink3,fontFamily:C.body}}>{family.kids.map(k=>`${k.name}${k.age?` (${k.age})`:""}` ).join(" · ")}</p>
                            </div>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{transition:"transform .2s",transform:outreachId===family.id?"rotate(180deg)":"none",flexShrink:0}}><path d="M6 9l6 6 6-6" stroke={C.ink3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </div>
                          {outreachId===family.id&&<OutreachPanel family={family} slotStr={slot.str}/>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Other families — no calendar overlap detected */}
        {otherFamilies.length>0&&(
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <p style={{fontSize:12,fontWeight:600,color:C.ink3,fontFamily:C.body,margin:0}}>
              {overlapGroups.length>0?"Other families":"All families"}
            </p>
            {otherFamilies.map(family=>{
              const isOpen=outreachId===family.id;
              const lastSeen=family.lastPlaydate?Math.round((Date.now()-new Date(family.lastPlaydate))/(1000*60*60*24)):null;
              return(
                <div key={family.id} style={{background:"#fff",border:`1.5px solid ${isOpen?C.blueBdr:C.line}`,borderRadius:14,overflow:"hidden",transition:"border-color .2s"}}>
                  <div onClick={()=>setOutreachId(isOpen?null:family.id)}
                    style={{padding:"13px 15px",cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
                    <div style={{position:"relative",flexShrink:0}}>
                      <div style={{width:40,height:40,borderRadius:"50%",background:avBg(family.parentName),display:"flex",alignItems:"center",justifyContent:"center",color:"rgba(255,255,255,.9)",fontWeight:700,fontFamily:C.display,fontSize:13}}>{initials(family.parentName)}</div>
                      {family.isGatherFamily&&(
                        <div style={{position:"absolute",bottom:-2,right:-2,width:14,height:14,borderRadius:"50%",background:C.blue,border:"2px solid #fff"}}/>
                      )}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,margin:0}}>{family.parentName}</p>
                      <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginTop:1}}>{family.kids.map(k=>k.name).join(" · ")}</p>
                    </div>
                    {lastSeen!==null&&(
                      <span style={{fontSize:11,color:lastSeen>21?C.amber:C.ink4,fontFamily:C.body,flexShrink:0,fontWeight:lastSeen>21?600:400}}>
                        {lastSeen===0?"Today":lastSeen===1?"Yesterday":lastSeen<7?`${lastSeen}d ago`:`${Math.round(lastSeen/7)}w ago`}
                      </span>
                    )}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{transition:"transform .25s",transform:isOpen?"rotate(180deg)":"none",flexShrink:0}}><path d="M6 9l6 6 6-6" stroke={C.ink3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  {isOpen&&<OutreachPanel family={family}/>}
                </div>
              );
            })}
          </div>
        )}

        {/* Add family prompt */}
        <button onClick={()=>setView("families")}
          style={{background:"none",border:`1.5px dashed ${C.lineM}`,borderRadius:12,padding:"13px",cursor:"pointer",color:C.ink3,fontFamily:C.body,fontSize:13,width:"100%"}}>
          + Add a family
        </button>
      </div>
    );
  }

  // ─── Kids management view ─────────────────────────────────────────────────
  function KidsView(){
    return(
      <div>
        <div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}>
          <Btn onClick={()=>{setKf(bk);setKidMode("add");}} variant="primary" small>+ Add child</Btn>
        </div>
        {kidMode&&(
          <Card className="p" style={{marginBottom:14,border:`1.5px solid ${C.blueBdr}`}}>
            <p style={{fontFamily:C.display,fontSize:18,fontWeight:700,color:C.ink,marginBottom:14}}>{kidMode==="add"?"Add a child":"Edit child"}</p>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 80px",gap:10}}>
                <Field value={kf.name} onChange={v=>setKf(f=>({...f,name:v}))} placeholder="Child's name"/>
                <Field value={kf.age}  onChange={v=>setKf(f=>({...f,age:v}))}  type="number" min="0" max="18" placeholder="Age"/>
              </div>
              <div>
                <Label>Energy level</Label>
                <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>{Object.entries(ENERGY).map(([k,v])=><Chip key={k} color={C.blue} soft={C.blueSoft} active={kf.energy===k} onClick={()=>setKf(f=>({...f,energy:k}))}>{v}</Chip>)}</div>
              </div>
              <div>
                <Label>Interests</Label>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>{Object.keys(KID_INT).map(i=><Chip key={i} color={C.blue} soft={C.blueSoft} small active={kf.interests.includes(i)} onClick={()=>setKf(f=>({...f,interests:f.interests.includes(i)?f.interests.filter(x=>x!==i):[...f.interests,i]}))}>{KID_INT[i]} {i}</Chip>)}</div>
              </div>
              <Field value={kf.allergies} onChange={v=>setKf(f=>({...f,allergies:v}))} placeholder="Allergies / dietary needs (optional)"/>

              {/* Schedule — used to find free windows accurately */}
              <div style={{borderTop:`1px solid ${C.line}`,paddingTop:12}}>
                <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:10}}>Schedule <span style={{fontSize:11,fontWeight:400,color:C.ink3}}>(helps find good times)</span></p>

                {/* School hours shortcut */}
                <label style={{display:"flex",alignItems:"center",gap:9,cursor:"pointer",marginBottom:12,padding:"9px 11px",background:kf.schoolHours?C.blueSoft:C.tint,border:`1.5px solid ${kf.schoolHours?C.blueBdr:C.line}`,borderRadius:9}}>
                  <input type="checkbox" checked={!!kf.schoolHours} onChange={e=>setKf(f=>({...f,schoolHours:e.target.checked}))} style={{accentColor:C.blue,width:15,height:15}}/>
                  <div>
                    <p style={{fontSize:13,fontWeight:500,color:C.ink,fontFamily:C.body,margin:0}}>School hours Mon–Fri</p>
                    <p style={{fontSize:11,color:C.ink3,fontFamily:C.body,margin:0}}>Blocks 8am–3pm weekdays</p>
                  </div>
                </label>

                {/* Nap time */}
                <div style={{marginBottom:12}}>
                  <p style={{fontSize:12,fontWeight:500,color:C.ink2,fontFamily:C.body,marginBottom:6}}>Nap time <span style={{fontWeight:400,color:C.ink4}}>(optional)</span></p>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <select value={kf.napStart||""} onChange={e=>setKf(f=>({...f,napStart:e.target.value?parseInt(e.target.value):null}))}
                      style={{flex:1,border:`1.5px solid ${C.line}`,borderRadius:8,padding:"7px 9px",fontSize:12,fontFamily:C.body,color:C.ink,background:"#fff",outline:"none"}}>
                      <option value="">No nap</option>
                      {NAP_HOURS.map(h=><option key={h.v} value={h.v}>{h.l}</option>)}
                    </select>
                    <span style={{fontSize:11,color:C.ink3,fontFamily:C.body,flexShrink:0}}>to</span>
                    <select value={kf.napEnd||""} onChange={e=>setKf(f=>({...f,napEnd:e.target.value?parseInt(e.target.value):null}))}
                      style={{flex:1,border:`1.5px solid ${C.line}`,borderRadius:8,padding:"7px 9px",fontSize:12,fontFamily:C.body,color:C.ink,background:"#fff",outline:"none"}}
                      disabled={!kf.napStart}>
                      <option value="">—</option>
                      {NAP_HOURS.filter(h=>h.v>(kf.napStart||0)).map(h=><option key={h.v} value={h.v}>{h.l}</option>)}
                    </select>
                  </div>
                </div>

                {/* Per-day activity blocks */}
                <p style={{fontSize:12,fontWeight:500,color:C.ink2,fontFamily:C.body,marginBottom:8}}>Recurring activities <span style={{fontWeight:400,color:C.ink4}}>(optional)</span></p>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {SCHED_DAYS.map(({k:dk,l:dl})=>{
                    const isWeekend=dk==="sat"||dk==="sun";
                    const maxBlocks=isWeekend?5:2;
                    const dayBlocks=(kf.activityBlocks||{})[dk]||[];
                    return(
                      <div key={dk}>
                        <div style={{display:"flex",alignItems:"center",gap:8,minHeight:30}}>
                          <span style={{fontSize:12,fontWeight:500,color:C.ink2,fontFamily:C.body,width:32,flexShrink:0}}>{dl}</span>
                          {dayBlocks.length===0?(
                            <button onClick={()=>setKf(f=>({...f,activityBlocks:{...(f.activityBlocks||{}),[dk]:[...(f.activityBlocks?.[dk]||[]),{start:15,end:17}]}}))}
                              style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:C.blue,fontFamily:C.body,padding:0}}>
                              + Add
                            </button>
                          ):(
                            <div style={{display:"flex",flexDirection:"column",gap:4,flex:1}}>
                              {dayBlocks.map((block,idx)=>(
                                <div key={idx} style={{display:"flex",alignItems:"center",gap:5}}>
                                  <select value={block.start} onChange={e=>setKf(f=>({...f,activityBlocks:{...(f.activityBlocks||{}),[dk]:(f.activityBlocks?.[dk]||[]).map((b,i)=>i===idx?{...b,start:parseInt(e.target.value)}:b)}}))}
                                    style={{flex:1,border:`1.5px solid ${C.line}`,borderRadius:7,padding:"5px 6px",fontSize:11,fontFamily:C.body,color:C.ink,background:"#fff",outline:"none"}}>
                                    {SCHED_HOURS.map(h=><option key={h.v} value={h.v}>{h.l}</option>)}
                                  </select>
                                  <span style={{fontSize:10,color:C.ink3,flexShrink:0}}>–</span>
                                  <select value={block.end} onChange={e=>setKf(f=>({...f,activityBlocks:{...(f.activityBlocks||{}),[dk]:(f.activityBlocks?.[dk]||[]).map((b,i)=>i===idx?{...b,end:parseInt(e.target.value)}:b)}}))}
                                    style={{flex:1,border:`1.5px solid ${C.line}`,borderRadius:7,padding:"5px 6px",fontSize:11,fontFamily:C.body,color:C.ink,background:"#fff",outline:"none"}}>
                                    {SCHED_HOURS.filter(h=>h.v>block.start).map(h=><option key={h.v} value={h.v}>{h.l}</option>)}
                                  </select>
                                  <button onClick={()=>setKf(f=>({...f,activityBlocks:{...(f.activityBlocks||{}),[dk]:(f.activityBlocks?.[dk]||[]).filter((_,i)=>i!==idx)}}))}
                                    style={{background:"none",border:"none",cursor:"pointer",color:C.ink4,fontSize:16,padding:"0 2px",lineHeight:1,flexShrink:0}}>×</button>
                                </div>
                              ))}
                              {dayBlocks.length<maxBlocks&&(
                                <button onClick={()=>setKf(f=>({...f,activityBlocks:{...(f.activityBlocks||{}),[dk]:[...(f.activityBlocks?.[dk]||[]),{start:15,end:17}]}}))}
                                  style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:C.blue,fontFamily:C.body,padding:"1px 0",textAlign:"left"}}>
                                  + {isWeekend?"Another":"Another activity"}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Field value={kf.notes} onChange={v=>setKf(f=>({...f,notes:v}))} multi placeholder="Other notes (optional)"/>
            </div>
            <div style={{display:"flex",gap:8,marginTop:14}}>
              <Btn onClick={saveKid} disabled={!kf.name.trim()}>Save</Btn>
              <Btn onClick={()=>setKidMode(null)} variant="ghost">Cancel</Btn>
            </div>
          </Card>
        )}

        {myProfile.partnerUid?(
          <Card style={{marginBottom:10,background:C.blueSoft,border:`1.5px solid ${C.blueBdr}`}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:18}}>💑</span>
              <div>
                <p style={{fontSize:13,fontWeight:600,color:C.blue,fontFamily:C.body}}>Partner linked</p>
                <p style={{fontSize:12,color:C.ink3,fontFamily:C.body}}>Kids are shared with your partner automatically.</p>
              </div>
            </div>
          </Card>
        ):(
          <Card style={{marginBottom:10,border:`1.5px dashed ${C.lineM}`}}>
            <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:4}}>👫 Invite your partner or co-parent</p>
            <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginBottom:10,lineHeight:1.6}}>Link accounts and kids are shared automatically — no double entry.</p>
            <InvitePartnerBtn myUid={myUid}/>
          </Card>
        )}

        {allMyKids.length===0
          ?<EmptyState icon="○" title="No children added" note="Add your kids to start finding playdate times."/>
          :<div style={{display:"flex",flexDirection:"column",gap:7}}>
            {myKids.map(k=>(
              <Card key={k.id} className="lift" style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px"}}>
                <div style={{width:42,height:42,borderRadius:"50%",background:avBg(k.name),display:"flex",alignItems:"center",justifyContent:"center",color:"rgba(255,255,255,.9)",fontFamily:C.display,fontWeight:700,fontSize:14,flexShrink:0}}>{initials(k.name)}</div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",marginBottom:4}}>
                    <span style={{fontSize:14,fontWeight:600,color:C.ink,fontFamily:C.body}}>{k.name}</span>
                    <Pill color={C.blue} soft={C.blueSoft}>Age {k.age}</Pill>
                    <Pill color={C.green} soft={C.greenSoft}>{ENERGY[k.energy]||"Active"}</Pill>
                    {k.allergies&&<Pill color={C.amber} soft={C.amberSoft}>⚠ {k.allergies}</Pill>}
                  </div>
                  <p style={{fontSize:12,color:C.ink3,fontFamily:C.body}}>{(k.interests||[]).map(i=>KID_INT[i]).join(" ")}</p>
                </div>
                <div style={{display:"flex",gap:5}}>
                  <Btn onClick={()=>{setKf(k);setKidMode(k.id);}} variant="ghost" small>Edit</Btn>
                  <Btn onClick={()=>onDeleteKid(k.id)} variant="red" small>✕</Btn>
                </div>
              </Card>
            ))}
            {partnerKids.map(k=>(
              <Card key={k.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",opacity:.8}}>
                <div style={{width:42,height:42,borderRadius:"50%",background:avBg(k.name),display:"flex",alignItems:"center",justifyContent:"center",color:"rgba(255,255,255,.9)",fontFamily:C.display,fontWeight:700,fontSize:14,flexShrink:0}}>{initials(k.name)}</div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap",marginBottom:4}}>
                    <span style={{fontSize:14,fontWeight:600,color:C.ink,fontFamily:C.body}}>{k.name}</span>
                    <Pill color={C.blue} soft={C.blueSoft}>Age {k.age}</Pill>
                    <Pill color={C.violet} soft={C.violetSoft}>partner's</Pill>
                  </div>
                  <p style={{fontSize:12,color:C.ink3,fontFamily:C.body}}>{(k.interests||[]).map(i=>KID_INT[i]).join(" ")}</p>
                </div>
              </Card>
            ))}
          </div>
        }
      </div>
    );
  }

  // ─── Families management view ─────────────────────────────────────────────
  function FamiliesView(){
    return(
      <div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <button onClick={()=>setView("home")} style={{background:"none",border:"none",cursor:"pointer",color:C.blue,fontFamily:C.body,fontSize:13,fontWeight:500,padding:0,display:"flex",alignItems:"center",gap:4}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M19 12H5M12 19l-7-7 7-7" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back
          </button>
          <Btn onClick={()=>{setPf({parentName:"",parentPhone:"",kids:[]});setPkf(bk);setPdcMode("add");}} variant="primary" small>+ Add family</Btn>
        </div>

        {pdcMode&&(
          <Card className="p" style={{marginBottom:14,border:`1.5px solid ${C.blueBdr}`}}>
            <p style={{fontFamily:C.display,fontSize:17,fontWeight:700,color:C.ink,marginBottom:12}}>{pdcMode==="add"?"Add a family":"Edit family"}</p>
            <Field value={pf.parentName} onChange={v=>setPf(f=>({...f,parentName:v}))} placeholder="Parent name (e.g. Rachel)"/>
            <Field value={pf.parentPhone||""} onChange={v=>setPf(f=>({...f,parentPhone:v}))} placeholder="Their phone (optional — for one-tap texting)" type="tel"/>
            <div style={{marginTop:12}}>
              <Label>Their kids</Label>
              {pf.kids.map(k=>(
                <div key={k.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 11px",background:C.tint,borderRadius:8,marginBottom:6,border:`1.5px solid ${C.line}`}}>
                  <span style={{fontSize:12,color:C.ink3,fontFamily:C.body,flex:1}}>{k.name}, age {k.age}</span>
                  <Btn onClick={()=>setPf(f=>({...f,kids:f.kids.filter(x=>x.id!==k.id)}))} variant="red" small>✕</Btn>
                </div>
              ))}
              <div style={{display:"grid",gridTemplateColumns:"1fr 70px 80px",gap:8,marginTop:8}}>
                <Field value={pkf.name} onChange={v=>setPkf(f=>({...f,name:v}))} placeholder="Child's name"/>
                <Field value={pkf.age}  onChange={v=>setPkf(f=>({...f,age:v}))}  type="number" placeholder="Age"/>
                <Btn onClick={addKidToPf} variant="teal" small disabled={!pkf.name.trim()}>+ Add</Btn>
              </div>
            </div>
            <div style={{display:"flex",gap:8,marginTop:14}}>
              <Btn onClick={savePdc} disabled={!pf.parentName.trim()}>Save</Btn>
              <Btn onClick={()=>setPdcMode(null)} variant="ghost">Cancel</Btn>
            </div>
          </Card>
        )}

        {allFamilies.length===0
          ?<EmptyState icon="○" title="No families yet" note="Add the families you hang out with."/>
          :<div style={{display:"flex",flexDirection:"column",gap:8}}>
            {gatherFamilies.length>0&&(
              <p style={{fontSize:11,fontWeight:600,color:C.ink4,textTransform:"uppercase",letterSpacing:".05em",margin:"0 0 2px"}}>On Gather — calendar synced</p>
            )}
            {gatherFamilies.map(p=>(
              <Card key={p.id} style={{border:`1.5px solid ${C.blueBdr}`}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:p.kids.length>0?8:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,margin:0}}>{p.parentName}</p>
                    <span style={{fontSize:10,fontWeight:600,color:C.blue,background:C.blueSoft,border:`1px solid ${C.blueBdr}`,borderRadius:20,padding:"2px 7px"}}>on gather</span>
                  </div>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {p.kids.map(k=>(
                    <span key={k.id} style={{fontSize:12,color:C.ink2,fontFamily:C.body,background:C.tint,border:`1px solid ${C.line}`,borderRadius:20,padding:"3px 9px"}}>
                      {k.name}{k.age?` · ${k.age}`:""}
                    </span>
                  ))}
                </div>
              </Card>
            ))}
            {pdc.length>0&&gatherFamilies.length>0&&(
              <p style={{fontSize:11,fontWeight:600,color:C.ink4,textTransform:"uppercase",letterSpacing:".05em",margin:"8px 0 2px"}}>Added manually</p>
            )}
            {pdc.map(p=>(
              <Card key={p.id}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:p.kids.length>0?8:0}}>
                  <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,margin:0}}>{p.parentName}</p>
                  <div style={{display:"flex",gap:5}}>
                    <Btn onClick={()=>{setPf(p);setPkf(bk);setPdcMode(p.id);}} variant="ghost" small>Edit</Btn>
                    <Btn onClick={()=>onDeleteFamily(p.id)} variant="red" small>✕</Btn>
                  </div>
                </div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                  {p.kids.map(k=>(
                    <span key={k.id} style={{fontSize:12,color:C.ink2,fontFamily:C.body,background:C.tint,border:`1px solid ${C.line}`,borderRadius:20,padding:"3px 9px"}}>
                      {k.name}{k.age?` · ${k.age}`:""}
                    </span>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        }
      </div>
    );
  }

  return(
    <div>
      {/* Sub-nav */}
      <div style={{display:"flex",gap:14,marginBottom:20,borderBottom:`1.5px solid ${C.line}`,paddingBottom:0}}>
        {SUBS.map(t=>(
          <button key={t.id} onClick={()=>setView(t.id)}
            style={{background:"none",border:"none",borderBottom:`2px solid ${view===t.id?C.blue:"transparent"}`,marginBottom:-1.5,color:view===t.id?C.blue:C.ink3,cursor:"pointer",fontFamily:C.body,fontWeight:view===t.id?600:400,fontSize:13,padding:"0 0 12px",transition:"color .15s,border-color .15s"}}>
            {t.l}
          </button>
        ))}
      </div>
      {view==="home"&&<HomeView/>}
      {view==="kids"&&<KidsView/>}
      {view==="families"&&<FamiliesView/>}
    </div>
  );
}

/* ── iCal inline connect widget ───────────────────────────────────────────── */
function IcalConnectInline({onConnect, calSyncing}) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [err, setErr] = useState("");
  async function tryConnect() {
    const trimmed = url.trim();
    if (!trimmed) return;
    if (!/^(https?|webcal):\/\//i.test(trimmed)) {
      setErr("Paste a full URL starting with https:// or webcal://");
      return;
    }
    setErr("");
    await onConnect(trimmed);
  }
  if (!open) return (
    <button onClick={()=>setOpen(true)} style={{background:"none",border:`1.5px solid ${C.line}`,borderRadius:10,padding:"9px 14px",cursor:"pointer",fontSize:13,color:C.ink2,fontFamily:C.body,fontWeight:500,width:"100%",textAlign:"left",display:"flex",alignItems:"center",gap:8}}>
      <span style={{fontSize:16}}>📅</span> Use Apple, Outlook, or any calendar link
    </button>
  );
  return (
    <div style={{border:`1.5px solid ${C.blueBdr}`,borderRadius:10,padding:"12px 14px",background:C.tint}}>
      <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:6}}>Paste your calendar subscription link</p>
      <div style={{background:"#fff",borderRadius:8,padding:"8px 11px",marginBottom:8,border:`1px solid ${C.line}`}}>
        <p style={{fontSize:11,fontWeight:600,color:C.ink3,fontFamily:C.body,marginBottom:4}}>How to get your link:</p>
        <p style={{fontSize:11,color:C.ink3,fontFamily:C.body,lineHeight:1.7}}>
          <b>Apple/iCloud:</b> System Settings → Apple ID → iCloud → Passwords &amp; Accounts → or use iCloud.com Calendar → share link<br/>
          <b>Outlook:</b> outlook.com → Settings → Calendar → Shared calendars → Publish → ICS link<br/>
          <b>Google:</b> calendar.google.com → Settings → [calendar] → "Secret address in iCal format"
        </p>
      </div>
      <Field value={url} onChange={v=>{setUrl(v);setErr("");}} placeholder="https://… or webcal://…"/>
      {err&&<p style={{fontSize:12,color:"#dc2626",marginTop:4,fontFamily:C.body}}>{err}</p>}
      <div style={{display:"flex",gap:8,marginTop:10}}>
        <Btn onClick={tryConnect} loading={calSyncing} disabled={!url.trim()}>Connect</Btn>
        <Btn onClick={()=>{setOpen(false);setUrl("");setErr("");}} variant="ghost">Cancel</Btn>
      </div>
    </div>
  );
}

/* ── Profile ───────────────────────────────────────────────────────────────── */
function ProfileTab({myProfile,setMyProfile,onSave,groups,onConnectCalendar,onSyncCalendar,onConnectIcal,onSyncIcal,calConnecting,calSyncing,calError,user,onDeleteAccount}) {
  const [saved,setSaved]=useState(false);
  const [saving,setSaving]=useState(false);
  const [deleteStep,setDeleteStep]=useState(0); // 0=hidden 1=confirm 2=deleting
  const [deleteConfirm,setDeleteConfirm]=useState("");

  const DAYS=[{k:"mon",l:"Monday"},{k:"tue",l:"Tuesday"},{k:"wed",l:"Wednesday"},{k:"thu",l:"Thursday"},{k:"fri",l:"Friday"},{k:"sat",l:"Saturday"},{k:"sun",l:"Sunday"}];
  const PRIVACY_FIELDS=[{k:"phone",l:"Phone number"},{k:"email",l:"Email address"},{k:"schedule",l:"Your schedule"},{k:"hobbies",l:"Interests"},{k:"birthday",l:"Birthday"}];
  const ALL_TABS=["overview","reach","contacts","playdates","profile"];
  const TAB_LABELS={overview:"Overview",reach:"Gather",contacts:"People",playdates:"Playdates",profile:"Profile"};

  const privacy=myProfile.privacy||{};
  const schedule=myProfile.schedule||{mon:"",tue:"",wed:"",thu:"",fri:"",sat:"",sun:""};
  const enabledTabs=myProfile.enabledTabs||ALL_TABS;

  const VISIBILITY_OPTIONS=[
    {id:"everyone",label:"Everyone"},
    ...(groups||[]).map(g=>({id:g.id,label:g.name})),
    {id:"none",label:"Only me"},
  ];

  function togglePrivacy(field,optId){
    const cur=privacy[field]||["everyone"];
    const next=cur.includes(optId)?cur.filter(x=>x!==optId):[...cur,optId];
    setMyProfile(p=>({...p,privacy:{...(p.privacy||{}),[field]:next}}));
  }

  function toggleTab(tabId){
    if(tabId==="profile") return;
    const cur=enabledTabs;
    const next=cur.includes(tabId)?cur.filter(x=>x!==tabId):[...cur,tabId];
    setMyProfile(p=>({...p,enabledTabs:next.includes("profile")?next:[...next,"profile"]}));
  }

  async function handleSave(){
    setSaving(true);
    await onSave(myProfile);
    setSaving(false); setSaved(true); setTimeout(()=>setSaved(false),2200);
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>

      {/* AI personalisation — surfaced first, highlighted */}
      <div style={{background:`linear-gradient(135deg,${C.blue}10,${C.blue}05)`,border:`1.5px solid ${C.blueBdr}`,borderRadius:16,padding:"18px 20px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
          <div style={{width:32,height:32,borderRadius:8,background:C.blue,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <span style={{fontSize:16}}>✨</span>
          </div>
          <div>
            <p style={{fontFamily:C.display,fontSize:16,fontWeight:700,color:C.ink,marginBottom:2}}>Make the AI sound like you</p>
            <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,lineHeight:1.5}}>These three settings directly improve every message gather writes for you.</p>
          </div>
        </div>

        <Label note="gather matches your tone to your gender — it matters more than you think">How do you identify?</Label>
        <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:16}}>
          {[["man","Man"],["woman","Woman"],["other","Non-binary / other"]].map(([v,l])=>(
            <Chip key={v} color={C.blue} soft={C.blueSoft} active={myProfile.gender===v} onClick={()=>setMyProfile(p=>({...p,gender:v}))}>{l}</Chip>
          ))}
        </div>

        <Label note="Older users get a warmer, more classic tone. Younger users get something more relaxed.">Your age range</Label>
        <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:16}}>
          {[["under30","Under 30"],["30s40s","30s–40s"],["50s60s","50s–60s"],["70plus","70+"]].map(([v,l])=>(
            <Chip key={v} color={C.blue} soft={C.blueSoft} active={myProfile.ageRange===v} onClick={()=>setMyProfile(p=>({...p,ageRange:v}))}>{l}</Chip>
          ))}
        </div>

        <Label note="Helps every message sound like you, not a robot">Your texting personality</Label>

        <p style={{fontSize:13,fontWeight:600,color:C.ink2,marginBottom:8,fontFamily:C.body}}>Emojis?</p>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
          {[["none","Never"],["some","Sometimes"],["lots","Always 😊"]].map(([v,l])=>(
            <Chip key={v} color={C.blue} soft={C.blueSoft} active={myProfile.emojiStyle===v}
              onClick={()=>setMyProfile(p=>({...p,emojiStyle:v}))}>{l}</Chip>
          ))}
        </div>

        <p style={{fontSize:13,fontWeight:600,color:C.ink2,marginBottom:8,fontFamily:C.body}}>Message length?</p>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
          {[["short","Short & punchy"],["medium","A few sentences"],["long","Proper messages"]].map(([v,l])=>(
            <Chip key={v} color={C.blue} soft={C.blueSoft} active={myProfile.msgLength===v}
              onClick={()=>setMyProfile(p=>({...p,msgLength:v}))}>{l}</Chip>
          ))}
        </div>

        <p style={{fontSize:13,fontWeight:600,color:C.ink2,marginBottom:8,fontFamily:C.body}}>Which sounds most like you?</p>
        <div style={{display:"flex",flexDirection:"column",gap:7,marginBottom:14}}>
          {[
            ["direct",  '"Hey. Been a while — coffee soon?"'],
            ["warm",    '"Hey! Was just thinking about you, how\'s everything going?"'],
            ["casual",  '"Alright, how\'s life treating you? Been too long!"'],
          ].map(([v,example])=>(
            <button key={v} onClick={()=>setMyProfile(p=>({...p,toneVibe:p.toneVibe===v?"":v}))}
              style={{padding:"10px 13px",borderRadius:10,border:`1.5px solid ${myProfile.toneVibe===v?C.blue:C.line}`,
                background:myProfile.toneVibe===v?C.blueSoft:"#fff",color:myProfile.toneVibe===v?C.blue:C.ink2,
                fontFamily:C.body,fontSize:13,cursor:"pointer",textAlign:"left",transition:"all .15s",
                fontStyle:"italic",fontWeight:myProfile.toneVibe===v?600:400,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span>{example}</span>
              {myProfile.toneVibe===v&&<span style={{fontStyle:"normal",fontWeight:700,flexShrink:0,marginLeft:8}}>✓</span>}
            </button>
          ))}
        </div>

        <Label note="Optional — add more detail for even better results">Anything else about how you write?</Label>
        <Field value={myProfile.voiceNote||""} onChange={v=>setMyProfile(p=>({...p,voiceNote:v}))} multi placeholder={"e.g. I always use their name, I keep it short, I never say Hope you're well..."} style={{marginBottom:14}}/>

        <Label note="Used in suggested messages when planning a catch-up">Your go-to catch-up activity</Label>
        <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
          {[
            ["coffee","☕ Coffee"],
            ["beer","🍺 Drinks"],
            ["dinner","🍽️ Dinner"],
            ["tennis","🎾 Tennis"],
            ["walk","🚶 A walk"],
            ["lunch","🥗 Lunch"],
            ["golf","⛳ Golf"],
            ["gym","🏋️ The gym"],
          ].map(([v,l])=>(
            <Chip key={v} color={C.blue} soft={C.blueSoft}
              active={(myProfile.catchupActivity||[]).includes(v)}
              onClick={()=>setMyProfile(p=>({
                ...p,
                catchupActivity:(p.catchupActivity||[]).includes(v)
                  ?(p.catchupActivity||[]).filter(x=>x!==v)
                  :[...(p.catchupActivity||[]),v]
              }))}>
              {l}
            </Chip>
          ))}
        </div>
      </div>

      <Card>
        <Label>Your full name</Label>
        <Field value={myProfile.name||""} onChange={v=>setMyProfile(p=>({...p,name:v}))} placeholder="Your full name" style={{maxWidth:240,marginBottom:14}}/>
        <Label note="Lets friends find you on gather when they add you by phone — never shared publicly">Your phone number</Label>
        <Field value={myProfile.phone||""} onChange={v=>setMyProfile(p=>({...p,phone:v}))} placeholder="+1 555 000 0000" type="tel" style={{maxWidth:240,marginBottom:6}}/>
        {user?.email&&(
          <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginTop:8}}>
            Signed in as <span style={{fontWeight:600,color:C.ink2}}>{user.email}</span>
          </p>
        )}
      </Card>


      <Card>
        <Label>Your interests</Label>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
          {[...Object.keys(HOBBIES), ...(myProfile.hobbies||[]).filter(h=>!HOBBIES[h])].map(h=>(
            <Chip key={h} color={C.blue} soft={C.blueSoft} small active={myProfile.hobbies?.includes(h)}
              onClick={()=>setMyProfile(p=>({...p,hobbies:p.hobbies?.includes(h)?p.hobbies.filter(x=>x!==h):[...(p.hobbies||[]),h]}))}>
              {hobbyEmoji(h)} {hobbyLabel(h)}
            </Chip>
          ))}
        </div>
        <ProfileCustomHobbyInput
          hobbies={myProfile.hobbies||[]}
          onChange={hobbies=>setMyProfile(p=>({...p,hobbies}))}
        />
      </Card>

      {groups&&groups.length>0&&(
        <Card>
          <Label note="Choose which groups can see each part of your profile when you share with contacts">Privacy settings</Label>
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {PRIVACY_FIELDS.map(({k,l})=>(
              <div key={k}>
                <p style={{fontSize:12,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:8}}>{l}</p>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {VISIBILITY_OPTIONS.map(opt=>{
                    const cur=privacy[k]||["everyone"];
                    return (
                      <Chip key={opt.id} color={C.blue} soft={C.blueSoft} small active={cur.includes(opt.id)} onClick={()=>togglePrivacy(k,opt.id)}>
                        {opt.label}
                      </Chip>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <Label note="Gather uses this to suggest times — you always confirm before anything is sent">When are you free?</Label>

        {/* Onboarding nudge — shown if no calendar and no schedule filled in */}
        {!myProfile.googleCalendarConnected&&!myProfile.icalCalendarConnected&&!Object.values(schedule).some(v=>v?.trim())&&(
          <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:10,padding:"11px 13px",marginBottom:14,display:"flex",gap:10,alignItems:"flex-start"}}>
            <span style={{fontSize:15,flexShrink:0}}>💡</span>
            <p style={{fontSize:12,color:"#92400e",fontFamily:C.body,lineHeight:1.55}}>Gather doesn't know when you're free yet. Add your rough availability below — even just a few days — and it'll suggest much better times for you.</p>
          </div>
        )}

        {/* Manual schedule — lead with this */}
        <div style={{marginBottom:16}}>
          <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:2}}>Type in your rough patterns</p>
          <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginBottom:10,lineHeight:1.6}}>Most people don't schedule everything — that's fine. Describe when you're generally free or busy and gather works around it. As vague or specific as you like.</p>
          {/* Quick-fill shortcuts */}
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
            {[
              {label:"Weekdays 9–5 busy",fn:s=>({...s,mon:"busy 9am–5pm",tue:"busy 9am–5pm",wed:"busy 9am–5pm",thu:"busy 9am–5pm",fri:"busy 9am–5pm"})},
              {label:"Evenings free",fn:s=>({...s,mon:s.mon||"free after 6pm",tue:s.tue||"free after 6pm",wed:s.wed||"free after 6pm",thu:s.thu||"free after 6pm",fri:s.fri||"free after 6pm"})},
              {label:"Weekends free",fn:s=>({...s,sat:"free all day",sun:"free all day"})},
              {label:"Clear all",fn:()=>({mon:"",tue:"",wed:"",thu:"",fri:"",sat:"",sun:""})},
            ].map(({label,fn})=>(
              <button key={label} onClick={()=>setMyProfile(p=>({...p,schedule:fn(p.schedule||{})}))}
                style={{background:C.tint,border:`1px solid ${C.line}`,borderRadius:20,padding:"5px 12px",cursor:"pointer",fontSize:12,fontFamily:C.body,color:C.ink2,fontWeight:500}}>
                {label}
              </button>
            ))}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {DAYS.map(({k,l})=>(
              <div key={k} style={{display:"grid",gridTemplateColumns:"90px 1fr",gap:10,alignItems:"center"}}>
                <p style={{fontSize:12,fontWeight:500,color:C.ink2,fontFamily:C.body}}>{l}</p>
                <Field value={schedule[k]||""} onChange={v=>setMyProfile(p=>({...p,schedule:{...(p.schedule||{}),[k]:v}}))} placeholder="e.g. free after 5pm / busy all day"/>
              </div>
            ))}
          </div>
        </div>

        {/* Calendar sync — secondary option */}
        <div style={{paddingTop:16,borderTop:`1px solid ${C.line}`}}>
          <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:2}}>Or connect your calendar for automatic sync</p>
          <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginBottom:12,lineHeight:1.6}}>Gather reads only free/busy times — never event titles, locations, or attendees. Read-only, won't affect your calendar, and your friends can't see it.</p>

          {/* What we access */}
          <div style={{background:C.tint,border:`1px solid ${C.line}`,borderRadius:10,padding:"12px 14px",marginBottom:12}}>
            <p style={{fontSize:12,fontWeight:700,color:C.ink,fontFamily:C.body,marginBottom:10}}>What gather accesses</p>
            <div style={{display:"flex",flexDirection:"column",gap:7}}>
              {[
                {icon:"✅",label:"When you're free or busy",note:"Start and end times only — nothing else"},
                {icon:"🚫",label:"Never reads event titles",note:"We can't see what your events are called"},
                {icon:"🚫",label:"Never reads locations or attendees",note:"Who you're meeting and where stays private"},
                {icon:"🚫",label:"Never writes to your calendar",note:"Gather is read-only — it can't create, edit, or delete events"},
                {icon:"🚫",label:"Your friends can't see your calendar",note:"Only gather uses your free/busy to suggest times"},
              ].map(({icon,label,note},i)=>(
                <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                  <span style={{fontSize:13,flexShrink:0,marginTop:1}}>{icon}</span>
                  <div>
                    <p style={{fontSize:12,fontWeight:600,color:C.ink,fontFamily:C.body}}>{label}</p>
                    <p style={{fontSize:11,color:C.ink3,fontFamily:C.body}}>{note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Google Calendar row */}
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"11px 13px",background:C.tint,borderRadius:10,border:`1.5px solid ${myProfile.googleCalendarConnected?C.green+"40":C.line}`,marginBottom:8}}>
            <span style={{fontSize:18,flexShrink:0}}>📅</span>
            <div style={{flex:1}}>
              <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body}}>Google Calendar</p>
              {myProfile.googleCalendarConnected&&myProfile.googleBusyBlocksUpdatedAt?(
                <p style={{fontSize:11,color:C.ink3,fontFamily:C.body}}>Last refreshed {new Date(myProfile.googleBusyBlocksUpdatedAt).toLocaleDateString("en-US",{month:"short",day:"numeric"})} · {(myProfile.googleBusyBlocks||[]).length} blocks loaded</p>
              ):(
                <p style={{fontSize:11,color:C.ink3,fontFamily:C.body}}>Read-only · won't affect your calendar</p>
              )}
            </div>
            {myProfile.googleCalendarConnected?(
              <div style={{display:"flex",gap:6}}>
                <Btn onClick={onSyncCalendar} loading={calSyncing} variant="soft" small>Refresh</Btn>
                <Btn onClick={()=>setMyProfile(p=>({...p,googleCalendarConnected:false,googleBusyBlocks:[],calendarConnected:p.icalCalendarConnected||false,busyBlocks:p.icalBusyBlocks||[]}))} variant="ghost" small>Remove</Btn>
              </div>
            ):(
              <Btn onClick={onConnectCalendar} loading={calConnecting} variant="soft" small>Connect</Btn>
            )}
          </div>

          {/* iCal / other calendar row */}
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"11px 13px",background:C.tint,borderRadius:10,border:`1.5px solid ${myProfile.icalCalendarConnected?C.green+"40":C.line}`,marginBottom:4}}>
            <span style={{fontSize:18,flexShrink:0}}>🗓</span>
            <div style={{flex:1}}>
              <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body}}>Apple / Outlook / other</p>
              {myProfile.icalCalendarConnected&&myProfile.icalBusyBlocksUpdatedAt?(
                <p style={{fontSize:11,color:C.ink3,fontFamily:C.body}}>Last refreshed {new Date(myProfile.icalBusyBlocksUpdatedAt).toLocaleDateString("en-US",{month:"short",day:"numeric"})} · {(myProfile.icalBusyBlocks||[]).length} blocks loaded</p>
              ):(
                <p style={{fontSize:11,color:C.ink3,fontFamily:C.body}}>Read-only · won't affect your calendar</p>
              )}
            </div>
            {myProfile.icalCalendarConnected?(
              <div style={{display:"flex",gap:6}}>
                <Btn onClick={onSyncIcal} loading={calSyncing} variant="soft" small>Refresh</Btn>
                <Btn onClick={()=>setMyProfile(p=>({...p,icalCalendarConnected:false,icalBusyBlocks:[],icalUrl:"",calendarConnected:p.googleCalendarConnected||false,busyBlocks:p.googleBusyBlocks||[]}))} variant="ghost" small>Remove</Btn>
              </div>
            ):(
              <IcalConnectInline onConnect={onConnectIcal} calSyncing={calSyncing}/>
            )}
          </div>

          {calError&&<p style={{fontSize:13,color:"#dc2626",marginTop:8,fontFamily:C.body,wordBreak:"break-all"}}>{calError}</p>}
          {(myProfile.googleCalendarConnected||myProfile.icalCalendarConnected)&&(
            <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginTop:8}}>
              {(myProfile.busyBlocks||[]).length} free/busy blocks loaded from {[myProfile.googleCalendarConnected&&"Google",myProfile.icalCalendarConnected&&"your other calendar"].filter(Boolean).join(" + ")}
            </p>
          )}
        </div>
      </Card>

      <Card>
        <Label note="Show only the tabs you actually use">Features</Label>
        <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
          {ALL_TABS.map(tabId=>(
            <Chip key={tabId} color={C.blue} soft={C.blueSoft} active={enabledTabs.includes(tabId)} onClick={()=>toggleTab(tabId)} small>
              {TAB_LABELS[tabId]}
            </Chip>
          ))}
        </div>
      </Card>

      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <Btn onClick={handleSave} loading={saving}>Save profile</Btn>
        {saved&&<span className="i" style={{fontSize:13,color:C.green,fontWeight:600,fontFamily:C.body}}>Saved ✓</span>}
      </div>

      {/* ── Danger zone ── */}
      <div style={{borderTop:`1.5px solid ${C.line}`,paddingTop:20,marginTop:8}}>
        {deleteStep===0&&(
          <button onClick={()=>setDeleteStep(1)} style={{background:"none",border:"none",padding:0,cursor:"pointer",fontSize:13,color:C.ink4,fontFamily:C.body,textDecoration:"underline",textUnderlineOffset:3}}>
            Delete my account
          </button>
        )}

        {deleteStep===1&&(
          <div className="p" style={{background:C.redSoft,border:`1.5px solid ${C.red}28`,borderRadius:12,padding:"16px 18px"}}>
            <p style={{fontFamily:C.display,fontSize:16,fontWeight:700,color:C.red,marginBottom:6}}>Delete account</p>
            <p style={{fontSize:13,color:C.ink2,fontFamily:C.body,lineHeight:1.6,marginBottom:14}}>
              This will permanently delete your account and all your data — contacts, logs, groups, and kids. This cannot be undone.
            </p>
            <p style={{fontSize:13,fontWeight:600,color:C.ink,fontFamily:C.body,marginBottom:8}}>
              Type <span style={{color:C.red,fontFamily:"monospace"}}>DELETE</span> to confirm
            </p>
            <input
              value={deleteConfirm}
              onChange={e=>setDeleteConfirm(e.target.value)}
              placeholder="Type DELETE"
              style={{width:"100%",background:"#fff",border:`1.5px solid ${deleteConfirm==="DELETE"?C.red:C.lineM}`,borderRadius:8,color:C.ink,fontFamily:"monospace",fontSize:14,padding:"10px 13px",outline:"none",marginBottom:14,boxSizing:"border-box",transition:"border-color .15s"}}
            />
            <div style={{display:"flex",gap:8}}>
              <button
                onClick={()=>onDeleteAccount&&onDeleteAccount()}
                disabled={deleteConfirm!=="DELETE"||deleteStep===2}
                style={{background:deleteConfirm==="DELETE"?C.red:"rgba(220,38,38,.25)",color:"#fff",border:"none",borderRadius:8,padding:"9px 18px",cursor:deleteConfirm==="DELETE"?"pointer":"not-allowed",fontFamily:C.body,fontSize:13,fontWeight:600,transition:"background .15s"}}>
                {deleteStep===2?"Deleting…":"Delete everything"}
              </button>
              <Btn onClick={()=>{setDeleteStep(0);setDeleteConfirm("");}} variant="ghost" small>Cancel</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Onboarding ────────────────────────────────────────────────────────────── */
const LIFE_STAGES=[
  {id:"parent_young", emoji:"👶", label:"Parent with young kids",   sub:"Under 10"},
  {id:"parent_older", emoji:"🧒", label:"Parent with older kids",   sub:"10 and up"},
  {id:"no_kids",      emoji:"🙋", label:"No kids",                  sub:"Just me"},
  {id:"mix",          emoji:"👨‍👩‍👧", label:"It's complicated",         sub:"Mix of the above"},
];
const MATTERS=[
  {id:"friends",    emoji:"❤️",  label:"Old friends",       sub:"Reconnect with people I've drifted from"},
  {id:"family",     emoji:"🏠",  label:"Family",            sub:"Stay close with family no matter the distance"},
  {id:"network",    emoji:"💼",  label:"Professional",      sub:"Maintain and grow my work relationships"},
  {id:"playdates",  emoji:"🧒",  label:"Kids & playdates",  sub:"Keep my kids socially connected too"},
  {id:"new",        emoji:"✨",  label:"New friendships",   sub:"Build new connections where I live"},
];
const CHALLENGES=[
  {id:"forget",     emoji:"⏰", label:"I forget to reach out",         sub:"Until too much time has passed"},
  {id:"words",      emoji:"💬", label:"I don't know what to say",      sub:"Especially after a long gap"},
  {id:"schedule",   emoji:"📅", label:"Scheduling is exhausting",      sub:"Coordinating diaries with people is hard"},
  {id:"guilt",      emoji:"😔", label:"I feel guilty",                 sub:"I know I should but don't know where to start"},
];
/* ── Gather Agent ───────────────────────────────────────────────────────────── */
const AGENT_SYSTEM=`You are the Gather guide — a warm, practical coach helping people get real value from the Gather app. Your job is to help users discover and use features, not to handle support tickets or complaints. Keep replies short: 2–3 sentences unless walking through steps.

YOUR ROLE:
You help users understand what Gather can do for them and how to use it well. Think of yourself as a knowledgeable friend showing someone around a new tool — enthusiastic, practical, non-preachy.

WHAT GATHER DOES:
Gather helps people stay connected in real life — reach out to friends and family, find times to meet, coordinate playdates, plan hangouts. It is not a social network. It never posts, scores relationships, or judges how often someone is in touch.

KEY FEATURES TO GUIDE USERS TOWARDS:
Weekend Spark (Home tab): Shows your free windows this week and suggests who to invite. Tap a person to float an idea — Gather drafts the message for you.
Find a time (Gather tab): Tap any person → "Find a time". Compares both calendars and suggests real slots. Great for people with synced calendars.
Group scheduling: Create a group, add people, and Gather compares all members' calendars to find times everyone is free. Perfect for recurring crew hangouts.
Chat: Message anyone in Gather directly. Tap "📅 Suggest a time" to propose a specific slot — they can accept and it goes straight to calendar.
Playdates: Add your kids, connect with other parents, and Gather coordinates kids' schedules alongside adult ones.
Calendar sync: Connect Google Calendar or Apple/Outlook via iCal. Only free/busy is used — event titles are never shared.
Daily nudge: Opt in to an 8am nudge suggesting one person to reach out to. Turn on in Profile.

FEATURE COACHING:
When a user wants to do something, walk them to the exact feature. Be specific:
- "Want to find a time with someone? Go to the Gather tab, tap their name, then hit 'Find a time'."
- "For a group hangout, go to People tab, tap your group, and use 'Schedule' — it'll show slots that work for everyone."
- "In chat, hit the 📅 button to suggest a specific time — they'll see Accept/Decline buttons."

PLANNING HELP:
When helping plan something, ask naturally (one question at a time):
1. Who do they want to see?
2. What kind of thing — coffee, walk, dinner, playdate, hangout?
3. Rough timing — this week, weekend, next week?
Then direct them to the right feature.

SAFE BOUNDARIES:
If someone asks for therapy, relationship diagnosis, crisis support, or emotional advice: respond briefly with warmth, do not roleplay as therapist — "Gather is best at practical planning. For deeper support, talking to someone you trust can really help."

GUARDRAILS — never say or imply:
- Any relationship is overdue, neglected, or failing
- The user should feel guilty about not being in touch
- Event titles or calendar details are visible to anyone
- Gather has access to anything beyond free/busy availability

TONE: warm, concise, practical, non-judgmental. Prefer "Here's a great way to do that…" over "You should…"`;

const QUICK_PROMPTS=[
  "How do I find a time to meet?",
  "How does group scheduling work?",
  "Help me plan something this week",
  "How do playdates work?",
  "How do I sync my calendar?",
];

function GatherAgent({contacts,groups,myProfile,myUid,busyBlocks,onTabChange}){
  const [open,setOpen]=useState(false);
  const [msgs,setMsgs]=useState([]);
  const [input,setInput]=useState("");
  const [loading,setLoading]=useState(false);
  const endRef=useRef(null);

  useEffect(()=>{ endRef.current?.scrollIntoView({behavior:"smooth"}); },[msgs,loading]);

  function buildCtx(){
    const names=contacts.slice(0,15).map(c=>`${c.name.split(" ")[0]} (${c.type||"friend"})`).join(", ")||"none yet";
    const grps=groups.map(g=>`${g.emoji} ${g.name}`).join(", ")||"none";
    const windows=busyBlocks.length>0?findFreeWindows(busyBlocks,[],14).slice(0,4).join("; "):"not connected";
    return `User: ${myProfile.name||"the user"} | Calendar: ${myProfile.calendarConnected?"connected":"not connected"} | Free windows: ${windows} | People: ${names} | Groups: ${grps}`;
  }

  async function send(text){
    const msg=(text||input).trim();
    if(!msg||loading) return;
    setInput("");
    const next=[...msgs,{role:"user",content:msg}];
    setMsgs(next);
    setLoading(true);
    const history=next.slice(-8).map(m=>`${m.role==="user"?"User":"Gather"}:${m.content}`).join("\n");
    const t=await callAI(`${history}\nGather:`,`${AGENT_SYSTEM}\n\nAPP CONTEXT: ${buildCtx()}`);
    setMsgs(m=>[...m,{role:"assistant",content:t||"Sorry, something went wrong — try again in a moment."}]);
    setLoading(false);
  }

  if(!open) return(
    <button onClick={()=>setOpen(true)} aria-label="Gather assistant"
      style={{position:"fixed",bottom:`calc(80px + env(safe-area-inset-bottom))`,right:18,zIndex:200,
        width:42,height:42,borderRadius:"50%",background:C.blue,border:"none",cursor:"pointer",
        display:"flex",alignItems:"center",justifyContent:"center",
        boxShadow:`0 4px 18px rgba(37,99,235,.38)`}}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="1.9"/>
        <path d="M12 8v1M12 11.5v4.5" stroke="#fff" strokeWidth="1.9" strokeLinecap="round"/>
      </svg>
    </button>
  );

  return(
    <div style={{position:"fixed",inset:0,zIndex:400,display:"flex",flexDirection:"column"}}>
      <div onClick={()=>setOpen(false)} style={{flex:1,background:"rgba(0,0,0,.45)"}}/>
      <div style={{background:"#fff",borderRadius:"20px 20px 0 0",maxHeight:"82vh",display:"flex",flexDirection:"column",maxWidth:720,margin:"0 auto",width:"100%",boxShadow:"0 -8px 40px rgba(0,0,0,.18)"}}>

        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px 12px",borderBottom:`1px solid ${C.line}`,flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:32,height:32,borderRadius:8,background:C.blue,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2"/>
                <path d="M12 8v1M12 11.5v4.5" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <p style={{fontFamily:C.display,fontSize:15,fontWeight:700,color:C.ink,lineHeight:1.2}}>Gather guide</p>
              <p style={{fontSize:11,color:C.ink4,fontFamily:C.body}}>Get more from the app</p>
            </div>
          </div>
          <button onClick={()=>setOpen(false)} style={{background:"none",border:`1.5px solid ${C.line}`,borderRadius:8,cursor:"pointer",color:C.ink3,width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>×</button>
        </div>

        {/* Messages */}
        <div style={{flex:1,overflowY:"auto",padding:"16px 18px",display:"flex",flexDirection:"column",gap:10}}>
          {/* Welcome + quick prompts */}
          {msgs.length===0&&(
            <>
              <div style={{background:C.blueSoft,border:`1.5px solid ${C.blueBdr}`,borderRadius:12,padding:"12px 14px"}}>
                <p style={{fontSize:13,color:C.ink,fontFamily:C.body,lineHeight:1.65}}>
                  Hey {myProfile.name?.split(" ")[0]||"there"} 👋 I'm your Gather guide. Ask me how to use any feature, or tell me what you're trying to do and I'll point you to the right tool.
                </p>
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:7,marginTop:2}}>
                {QUICK_PROMPTS.map(p=>(
                  <button key={p} onClick={()=>send(p)}
                    style={{background:C.tint,border:`1.5px solid ${C.line}`,borderRadius:20,padding:"6px 13px",cursor:"pointer",fontFamily:C.body,fontSize:12,color:C.ink2,whiteSpace:"nowrap"}}>
                    {p}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Message bubbles */}
          {msgs.map((m,i)=>(
            <div key={i} style={{display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start"}}>
              <div style={{
                maxWidth:"82%",background:m.role==="user"?C.blue:C.tint,
                color:m.role==="user"?"#fff":C.ink,
                borderRadius:m.role==="user"?"16px 16px 4px 16px":"16px 16px 16px 4px",
                padding:"10px 14px",fontFamily:C.body,fontSize:13,lineHeight:1.6,
              }}>
                {m.content}
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {loading&&(
            <div style={{display:"flex",justifyContent:"flex-start"}}>
              <div style={{background:C.tint,borderRadius:"16px 16px 16px 4px",padding:"10px 16px",display:"flex",gap:4,alignItems:"center"}}>
                {[0,1,2].map(i=>(
                  <span key={i} style={{width:6,height:6,borderRadius:"50%",background:C.ink3,display:"inline-block",animation:`bounce 1.2s ease-in-out ${i*.2}s infinite`}}/>
                ))}
              </div>
            </div>
          )}

          <div ref={endRef}/>
        </div>

        {/* Input */}
        <div style={{padding:"10px 14px calc(10px + env(safe-area-inset-bottom))",borderTop:`1px solid ${C.line}`,display:"flex",gap:8,alignItems:"flex-end",flexShrink:0,background:"#fff"}}>
            <textarea
              value={input}
              onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
              placeholder="Ask anything…"
              rows={1}
              style={{flex:1,background:C.tint,border:`1.5px solid ${C.line}`,borderRadius:12,padding:"9px 12px",fontFamily:C.body,fontSize:13,color:C.ink,resize:"none",outline:"none",lineHeight:1.5,maxHeight:80,overflowY:"auto"}}
            />
            <button onClick={()=>send()} disabled={!input.trim()||loading}
              style={{width:38,height:38,borderRadius:10,background:input.trim()&&!loading?C.blue:"#e2e8f0",border:"none",cursor:input.trim()&&!loading?"pointer":"default",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"background .15s"}}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" stroke={input.trim()&&!loading?"#fff":"#94a3b8"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
      </div>
    </div>
  );
}

const TAB_BENEFITS={
  overview: {emoji:"📊", label:"Home",       benefit:"Your people and your plans — everything in one glance"},
  reach:    {emoji:"✨", label:"Gather",     benefit:"Reach out, find a time, make something happen"},
  contacts: {emoji:"👥", label:"People",     benefit:"Everyone who matters, right when you need them"},
  playdates:{emoji:"🧒", label:"Playdates",  benefit:"Kids' playdates, sorted — parents included"},
  profile:  {emoji:"⚙️", label:"Profile",    benefit:"Personalise gather so every message sounds like you"},
};

function computeTabs(lifeStage, matters){
  const tabs=["overview","reach","contacts","profile"];
  const wantsPlaydates=lifeStage==="parent_young"||lifeStage==="parent_older"||lifeStage==="mix"||matters.includes("playdates");
  if(wantsPlaydates) tabs.splice(3,0,"playdates");
  return tabs;
}

function OnboardingFlow({user, onComplete, connectCalendar}){
  const [step,setStep]=useState(1);
  const [name,setName]=useState(user.displayName||"");
  const [lifeStage,setLifeStage]=useState("");
  const [matters,setMatters]=useState([]);
  const [challenge,setChallenge]=useState("");
  const [gender,setGender]=useState("");
  const [ageRange,setAgeRange]=useState("");
  const [emojiStyle,setEmojiStyle]=useState("");
  const [msgLength,setMsgLength]=useState("");
  const [toneVibe,setToneVibe]=useState("");
  const [saving,setSaving]=useState(false);
  const contactPickerSupported=typeof navigator!=="undefined"&&"contacts" in navigator&&"ContactsManager" in window;

  async function finish(){
    setSaving(true);
    const enabledTabs=computeTabs(lifeStage,matters);
    const profile={
      name:name.trim()||user.displayName||"",
      hobbies:[],voiceNote:"",
      schedule:{mon:"",tue:"",wed:"",thu:"",fri:"",sat:"",sun:""},
      privacy:{},
      enabledTabs,
      onboarded:true,
      lifeStage,
      gender,
      ageRange,
      emojiStyle,
      msgLength,
      toneVibe,
      matters,
      challenge,
    };
    await setDoc(doc(db,"users",user.uid),profile,{merge:true});
    setSaving(false);
    track("onboarding_complete",{life_stage:lifeStage,matters,challenge,enabled_tabs:enabledTabs});
    onComplete(profile);
  }

  const inp={width:"100%",background:"#fff",border:"1.5px solid rgba(37,99,235,.14)",borderRadius:8,
    color:"#0f172a",fontFamily:"'DM Sans',system-ui,sans-serif",fontSize:14,padding:"12px 14px",
    outline:"none",boxSizing:"border-box"};

  const enabledTabs=computeTabs(lifeStage,matters);
  const firstName=name.trim().split(" ")[0]||"";

  // Shared choice card style
  function choiceCard(selected){
    return {
      display:"flex",alignItems:"flex-start",gap:12,padding:"13px 15px",borderRadius:12,cursor:"pointer",
      background:selected?C.blueSoft:"#fff",
      border:`1.5px solid ${selected?C.blue:C.line}`,
      transition:"all .15s",marginBottom:8,
    };
  }

  return(
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-start",padding:"24px 20px 48px",fontFamily:C.body}}>
      {/* Logo */}
      <div style={{display:"flex",alignItems:"center",gap:10,margin:"32px 0 28px"}}>
        <div style={{width:32,height:32,borderRadius:9,background:C.blue,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 10px rgba(37,99,235,.35)"}}>
          <div style={{width:8,height:8,borderRadius:"50%",background:"rgba(255,255,255,.9)"}}/>
        </div>
        <span style={{fontFamily:"'DM Serif Display',serif",fontSize:24,fontWeight:400,fontStyle:"italic",color:C.ink}}>gather</span>
      </div>

      {/* Progress bar */}
      <div style={{display:"flex",gap:5,width:"100%",maxWidth:440,marginBottom:28}}>
        {[1,2,3,4].map(s=>(
          <div key={s} style={{height:3,flex:1,borderRadius:3,background:step>=s?C.blue:"rgba(37,99,235,.12)",transition:"background .35s"}}/>
        ))}
      </div>

      <div style={{background:"#fff",borderRadius:20,border:`1.5px solid ${C.line}`,boxShadow:"0 4px 32px rgba(37,99,235,.08)",padding:"28px 24px",width:"100%",maxWidth:440}}>

        {/* ── Step 1: Who are you ── */}
        {step===1&&(
          <div className="u">
            {/* Hero copy */}
            <div style={{background:`linear-gradient(135deg,${C.blue},${C.blueDk})`,borderRadius:12,padding:"20px 22px",marginBottom:24,position:"relative",overflow:"hidden"}}>
              <div style={{position:"absolute",top:-15,right:-15,width:80,height:80,borderRadius:"50%",background:"rgba(255,255,255,.06)"}}/>
              <p style={{fontFamily:"'DM Serif Display',serif",fontSize:20,color:"#fff",lineHeight:1.35,position:"relative",marginBottom:10}}>
                Less time coordinating.<br/>More time together.
              </p>
              <p style={{fontSize:13,color:"rgba(255,255,255,.75)",fontFamily:C.body,lineHeight:1.6,position:"relative"}}>
                The people you care about are busy too. gather helps you find your way back together.
              </p>
            </div>

            <h2 style={{fontFamily:"'DM Serif Display',serif",fontSize:22,fontWeight:400,fontStyle:"italic",color:C.ink,marginBottom:5}}>
              Let's make gather yours
            </h2>
            <p style={{fontSize:13,color:C.ink3,marginBottom:20,lineHeight:1.6}}>A few quick questions and we'll set it up around your life.</p>

            <div style={{marginBottom:18}}>
              <p style={{fontSize:13,fontWeight:600,color:C.ink2,marginBottom:4}}>What's your name?</p>
              <p style={{fontSize:12,color:C.ink3,marginBottom:8}}>Used so your friends know it's you when you reach out.</p>
              <input style={inp} value={name} onChange={e=>setName(e.target.value)} placeholder="Your full name" autoFocus/>
            </div>

            <p style={{fontSize:13,fontWeight:600,color:C.ink2,marginBottom:10}}>Which best describes you?</p>
            {LIFE_STAGES.map(ls=>(
              <div key={ls.id} className="tap" onClick={()=>setLifeStage(ls.id)} style={choiceCard(lifeStage===ls.id)}>
                <span style={{fontSize:22,lineHeight:1,flexShrink:0}}>{ls.emoji}</span>
                <div>
                  <p style={{fontSize:14,fontWeight:600,color:lifeStage===ls.id?C.blue:C.ink,fontFamily:C.body}}>{ls.label}</p>
                  <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginTop:1}}>{ls.sub}</p>
                </div>
                {lifeStage===ls.id&&<span style={{marginLeft:"auto",color:C.blue,fontSize:16,flexShrink:0}}>✓</span>}
              </div>
            ))}

            <div style={{marginTop:20}}>
              <p style={{fontSize:13,fontWeight:600,color:C.ink2,marginBottom:10}}>How do you identify? <span style={{fontWeight:400,color:C.ink3}}>(optional)</span></p>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {["Man","Woman","Non-binary"].map(g=>(
                  <button key={g} onClick={()=>setGender(gender===g?"":g)}
                    style={{padding:"8px 16px",borderRadius:20,border:`1.5px solid ${gender===g?C.blue:C.line}`,
                      background:gender===g?C.blueSoft:"#fff",color:gender===g?C.blue:C.ink2,
                      fontFamily:C.body,fontSize:13,fontWeight:gender===g?600:400,cursor:"pointer",transition:"all .15s"}}>
                    {g}
                  </button>
                ))}
              </div>
            </div>

            <div style={{marginTop:16}}>
              <p style={{fontSize:13,fontWeight:600,color:C.ink2,marginBottom:10}}>Age range <span style={{fontWeight:400,color:C.ink3}}>(optional)</span></p>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {["Under 30","30s–40s","50s–60s","70+"].map(a=>(
                  <button key={a} onClick={()=>setAgeRange(ageRange===a?"":a)}
                    style={{padding:"8px 16px",borderRadius:20,border:`1.5px solid ${ageRange===a?C.blue:C.line}`,
                      background:ageRange===a?C.blueSoft:"#fff",color:ageRange===a?C.blue:C.ink2,
                      fontFamily:C.body,fontSize:13,fontWeight:ageRange===a?600:400,cursor:"pointer",transition:"all .15s"}}>
                    {a}
                  </button>
                ))}
              </div>
            </div>

            {/* Texting personality — 3 quick questions */}
            <div style={{marginTop:22,paddingTop:18,borderTop:`1.5px solid ${C.line}`}}>
              <p style={{fontSize:14,fontWeight:600,color:C.ink,marginBottom:4,fontFamily:"'DM Sans',system-ui,sans-serif"}}>How do you text? <span style={{fontWeight:400,color:C.ink3,fontSize:13}}>(takes 10 seconds)</span></p>
              <p style={{fontSize:12,color:C.ink3,marginBottom:14,lineHeight:1.5,fontFamily:"'DM Sans',system-ui,sans-serif"}}>gather uses this so every message sounds like <em>you</em>, not a template.</p>

              <p style={{fontSize:13,fontWeight:600,color:C.ink2,marginBottom:8,fontFamily:"'DM Sans',system-ui,sans-serif"}}>Emojis?</p>
              <div style={{display:"flex",gap:8,marginBottom:14}}>
                {[["none","Never"],["some","Sometimes"],["lots","Always 😊"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setEmojiStyle(emojiStyle===v?"":v)}
                    style={{padding:"8px 14px",borderRadius:20,border:`1.5px solid ${emojiStyle===v?C.blue:C.line}`,
                      background:emojiStyle===v?C.blueSoft:"#fff",color:emojiStyle===v?C.blue:C.ink2,
                      fontFamily:"'DM Sans',system-ui,sans-serif",fontSize:13,fontWeight:emojiStyle===v?600:400,cursor:"pointer",transition:"all .15s"}}>
                    {l}
                  </button>
                ))}
              </div>

              <p style={{fontSize:13,fontWeight:600,color:C.ink2,marginBottom:8,fontFamily:"'DM Sans',system-ui,sans-serif"}}>Message length?</p>
              <div style={{display:"flex",gap:8,marginBottom:14}}>
                {[["short","Short & punchy"],["medium","A few sentences"],["long","Proper messages"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setMsgLength(msgLength===v?"":v)}
                    style={{padding:"8px 14px",borderRadius:20,border:`1.5px solid ${msgLength===v?C.blue:C.line}`,
                      background:msgLength===v?C.blueSoft:"#fff",color:msgLength===v?C.blue:C.ink2,
                      fontFamily:"'DM Sans',system-ui,sans-serif",fontSize:13,fontWeight:msgLength===v?600:400,cursor:"pointer",transition:"all .15s"}}>
                    {l}
                  </button>
                ))}
              </div>

              <p style={{fontSize:13,fontWeight:600,color:C.ink2,marginBottom:8,fontFamily:"'DM Sans',system-ui,sans-serif"}}>Which sounds most like you?</p>
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {[
                  ["direct",  '"Hey. Been a while — coffee soon?"'],
                  ["warm",    '"Hey! Was just thinking about you, how\'s everything going?"'],
                  ["casual",  '"Alright, how\'s life treating you? Been too long!"'],
                ].map(([v,example])=>(
                  <button key={v} onClick={()=>setToneVibe(toneVibe===v?"":v)}
                    style={{padding:"10px 13px",borderRadius:10,border:`1.5px solid ${toneVibe===v?C.blue:C.line}`,
                      background:toneVibe===v?C.blueSoft:"#fff",color:toneVibe===v?C.blue:C.ink2,
                      fontFamily:"'DM Sans',system-ui,sans-serif",fontSize:13,cursor:"pointer",
                      textAlign:"left",transition:"all .15s",fontStyle:"italic",fontWeight:toneVibe===v?600:400}}>
                    {example}
                    {toneVibe===v&&<span style={{float:"right",fontStyle:"normal",fontWeight:700}}>✓</span>}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={()=>setStep(2)} disabled={!name.trim()||!lifeStage}
              style={{width:"100%",marginTop:24,background:(name.trim()&&lifeStage)?C.blue:"rgba(37,99,235,.25)",color:"#fff",
                border:"none",borderRadius:10,padding:"13px",cursor:(name.trim()&&lifeStage)?"pointer":"not-allowed",
                fontFamily:C.body,fontSize:14,fontWeight:600,transition:"background .2s"}}>
              Next →
            </button>
          </div>
        )}

        {/* ── Step 2: What matters most ── */}
        {step===2&&(
          <div className="u">
            <h2 style={{fontFamily:"'DM Serif Display',serif",fontSize:22,fontWeight:400,fontStyle:"italic",color:C.ink,marginBottom:5}}>
              {firstName?`What matters most to you, ${firstName}?`:"What matters most to you?"}
            </h2>
            <p style={{fontSize:13,color:C.ink3,marginBottom:20,lineHeight:1.6}}>Select everything that applies — gather will be built around it.</p>

            {MATTERS.map(m=>(
              <div key={m.id} className="tap" onClick={()=>setMatters(prev=>prev.includes(m.id)?prev.filter(x=>x!==m.id):[...prev,m.id])} style={choiceCard(matters.includes(m.id))}>
                <span style={{fontSize:22,lineHeight:1,flexShrink:0}}>{m.emoji}</span>
                <div style={{flex:1}}>
                  <p style={{fontSize:14,fontWeight:600,color:matters.includes(m.id)?C.blue:C.ink,fontFamily:C.body}}>{m.label}</p>
                  <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginTop:1}}>{m.sub}</p>
                </div>
                <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${matters.includes(m.id)?C.blue:C.line}`,background:matters.includes(m.id)?C.blue:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
                  {matters.includes(m.id)&&<span style={{color:"#fff",fontSize:11,fontWeight:700,lineHeight:1}}>✓</span>}
                </div>
              </div>
            ))}

            <div style={{display:"flex",gap:10,marginTop:20}}>
              <button onClick={()=>setStep(1)} style={{flex:1,background:"none",border:`1.5px solid ${C.line}`,borderRadius:10,padding:"12px",cursor:"pointer",fontFamily:C.body,fontSize:13,color:C.ink2}}>← Back</button>
              <button onClick={()=>setStep(3)} disabled={matters.length===0}
                style={{flex:2,background:matters.length>0?C.blue:"rgba(37,99,235,.25)",color:"#fff",border:"none",borderRadius:10,padding:"12px",
                  cursor:matters.length>0?"pointer":"not-allowed",fontFamily:C.body,fontSize:14,fontWeight:600,transition:"background .2s"}}>
                Next →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Biggest challenge ── */}
        {step===3&&(
          <div className="u">
            <h2 style={{fontFamily:"'DM Serif Display',serif",fontSize:22,fontWeight:400,fontStyle:"italic",color:C.ink,marginBottom:5}}>
              What's your biggest challenge?
            </h2>
            <p style={{fontSize:13,color:C.ink3,marginBottom:20,lineHeight:1.6}}>Be honest — gather works best when it knows what's actually getting in the way.</p>

            {CHALLENGES.map(ch=>(
              <div key={ch.id} className="tap" onClick={()=>setChallenge(ch.id)} style={choiceCard(challenge===ch.id)}>
                <span style={{fontSize:22,lineHeight:1,flexShrink:0}}>{ch.emoji}</span>
                <div style={{flex:1}}>
                  <p style={{fontSize:14,fontWeight:600,color:challenge===ch.id?C.blue:C.ink,fontFamily:C.body}}>{ch.label}</p>
                  <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,marginTop:1}}>{ch.sub}</p>
                </div>
                {challenge===ch.id&&<span style={{marginLeft:"auto",color:C.blue,fontSize:16,flexShrink:0}}>✓</span>}
              </div>
            ))}

            <div style={{display:"flex",gap:10,marginTop:20}}>
              <button onClick={()=>setStep(2)} style={{flex:1,background:"none",border:`1.5px solid ${C.line}`,borderRadius:10,padding:"12px",cursor:"pointer",fontFamily:C.body,fontSize:13,color:C.ink2}}>← Back</button>
              <button onClick={()=>setStep(4)} disabled={!challenge}
                style={{flex:2,background:challenge?C.blue:"rgba(37,99,235,.25)",color:"#fff",border:"none",borderRadius:10,padding:"12px",
                  cursor:challenge?"pointer":"not-allowed",fontFamily:C.body,fontSize:14,fontWeight:600,transition:"background .2s"}}>
                See my gather →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Your gather is ready ── */}
        {step===4&&(
          <div className="u">
            <div style={{textAlign:"center",marginBottom:22}}>
              <div style={{fontSize:36,marginBottom:8}}>🎉</div>
              <h2 style={{fontFamily:"'DM Serif Display',serif",fontSize:24,fontWeight:400,fontStyle:"italic",color:C.ink,marginBottom:6}}>
                {firstName?`${firstName}'s gather is ready`:"Your gather is ready"}
              </h2>
              <p style={{fontSize:13,color:C.ink3,lineHeight:1.6,fontFamily:C.body}}>
                We've set it up based on your answers. Here's what's enabled for you:
              </p>
            </div>

            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:22}}>
              {enabledTabs.filter(t=>TAB_BENEFITS[t]).map(t=>{
                const tb=TAB_BENEFITS[t];
                return(
                  <div key={t} style={{display:"flex",gap:12,padding:"12px 14px",background:C.tint,borderRadius:12,border:`1px solid ${C.line}`,alignItems:"flex-start"}}>
                    <span style={{fontSize:20,flexShrink:0,marginTop:1}}>{tb.emoji}</span>
                    <div style={{flex:1}}>
                      <p style={{fontSize:13,fontWeight:700,color:C.ink,fontFamily:C.body,marginBottom:2}}>{tb.label}</p>
                      <p style={{fontSize:12,color:C.ink3,fontFamily:C.body,lineHeight:1.5}}>{tb.benefit}</p>
                    </div>
                    <span style={{fontSize:11,color:C.green,fontWeight:700,fontFamily:C.body,flexShrink:0,marginTop:2}}>ON</span>
                  </div>
                );
              })}
            </div>

            <p style={{fontSize:12,color:C.ink4,fontFamily:C.body,textAlign:"center",marginBottom:20,lineHeight:1.6}}>
              You can turn any tab on or off any time in Profile.
            </p>

            <button onClick={finish} disabled={saving}
              style={{width:"100%",background:C.blue,color:"#fff",border:"none",borderRadius:10,padding:"14px",
                cursor:saving?"not-allowed":"pointer",fontFamily:C.body,fontSize:14,fontWeight:600,
                display:"flex",alignItems:"center",justifyContent:"center",gap:8,opacity:saving?.7:1}}>
              {saving?<><span className="sp" style={{width:14,height:14,borderTopColor:"#fff"}}/> Setting up…</>
                :"Let's go 🎉"}
            </button>
          </div>
        )}

      </div>

      <p style={{fontSize:11,color:C.ink4,marginTop:20,textAlign:"center",lineHeight:1.8}}>
        Less scrolling. More showing up.<br/>
        <a href="/privacy" style={{color:C.ink4,textDecoration:"underline"}}>Privacy Policy</a>
        {" · "}
        <a href="mailto:hello@heygather.app" style={{color:C.ink4,textDecoration:"underline"}}>Contact</a>
      </p>
    </div>
  );
}

/* ── App ───────────────────────────────────────────────────────────────────── */
export default function App() {
  const {user,loading:authLoading,logOut,connectCalendar}=useAuth();
  const [loaded,setLoaded]=useState(false);
  const [tab,setTab]=useState("overview");
  const [menuOpen,setMenuOpen]=useState(false);
  const [partnerKids,setPartnerKids]=useState([]);
  const [reachOpenId,setReachOpenId]=useState(null);
  const [chatView,setChatView]=useState(null); // {chatId,chatName,participants}

  function handleReachOut(contactId){
    setReachOpenId(contactId);
    setTab("reach");
  }

  function openChat(chatId,chatName,participants,initialText,offPlatformContacts){
    setChatView({chatId,chatName,participants:participants||[],initialText:initialText||"",offPlatformContacts:offPlatformContacts||[]});
  }

  /* ── Capture ?ref param immediately (survives OAuth redirect) ── */
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const ref=params.get("ref");
    const partner=params.get("partner");
    if(ref){ localStorage.setItem("gather_ref",ref); }
    if(partner==="1"){ localStorage.setItem("gather_ref_partner","1"); }
    if(ref||partner) window.history.replaceState({},"",window.location.pathname);
  },[]);

  /* ── Native app setup (Capacitor) ── */
  useEffect(()=>{
    setupStatusBar();
    hideSplash();
    // Deep link handler: fires when app is already open and a Universal Link arrives
    const cleanupDeepLink=addDeepLinkListener((url)=>{
      try{
        const u=new URL(url);
        const ref=u.searchParams.get("ref");
        const partner=u.searchParams.get("partner");
        if(ref) localStorage.setItem("gather_ref",ref);
        if(partner==="1") localStorage.setItem("gather_ref_partner","1");
      }catch{}
    });
    return cleanupDeepLink;
  },[]);

  useEffect(()=>{
    if(!menuOpen) return;
    const close=()=>setMenuOpen(false);
    document.addEventListener("click",close);
    return ()=>document.removeEventListener("click",close);
  },[menuOpen]);

  // Cross-component navigation via custom event (used by empty states)
  useEffect(()=>{
    const handler=(e)=>setTab(e.detail);
    window.addEventListener("gather:goto",handler);
    return ()=>window.removeEventListener("gather:goto",handler);
  },[]);
  const [contacts,setContacts]=useState([]);
  const [groups,  setGroups]  =useState([]);
  const [myKids,  setMyKids]  =useState([]);
  const [plans,   setPlans]   =useState([]);
  const [pdc,     setPdc]     =useState([]);
  const [myProfile,setMyProfile]=useState({
    name:"",hobbies:[],voiceNote:"",
    schedule:{mon:"",tue:"",wed:"",thu:"",fri:"",sat:"",sun:""},
    privacy:{},enabledTabs:["overview","reach","contacts","playdates","profile"],
  });

  /* ── Load from Firestore ── */
  useEffect(()=>{
    if(!user){
      setLoaded(false);
      setContacts([]); setGroups([]); setMyKids([]); setPdc([]);
      setMyProfile({name:"",hobbies:[],voiceNote:"",schedule:{mon:"",tue:"",wed:"",thu:"",fri:"",sat:"",sun:""},privacy:{},enabledTabs:["overview","reach","contacts","playdates","profile"]});
      return;
    }
    const uid=user.uid;
    const unsubs=[];
    unsubs.push(onSnapshot(collection(db,"users",uid,"contacts"),s=>setContacts(s.docs.map(d=>({...d.data(),id:d.id})))));
    unsubs.push(onSnapshot(query(collection(db,"groups"),where("memberUids","array-contains",uid)),s=>setGroups(s.docs.map(d=>({...d.data(),id:d.id})))));
    unsubs.push(onSnapshot(collection(db,"users",uid,"kids"),    s=>setMyKids(  s.docs.map(d=>({...d.data(),id:d.id})))));
    unsubs.push(onSnapshot(collection(db,"users",uid,"families"),s=>setPdc(     s.docs.map(d=>({...d.data(),id:d.id})))));
    unsubs.push(onSnapshot(query(collection(db,"plans"),where("participants","array-contains",uid)),
      s=>setPlans(s.docs.map(d=>({...d.data(),id:d.id})).filter(p=>p.status!=="cancelled"||p.participants.includes(uid)).sort((a,b)=>new Date(a.startISO)-new Date(b.startISO)))
    ));
    getDoc(doc(db,"users",uid)).then(snap=>{
      if(snap.exists()){
        const data=snap.data();
        // Migrate: old accounts have calendarConnected but not googleCalendarConnected
        if(data.calendarConnected&&!data.googleCalendarConnected){
          data.googleCalendarConnected=true;
          setDoc(doc(db,"users",uid),{googleCalendarConnected:true},{merge:true});
        }
        setMyProfile(p=>({...p,...data}));
      } else if(user.displayName) setMyProfile(p=>({...p,name:user.displayName}));
      setLoaded(true);
    });
    return ()=>unsubs.forEach(u=>u());
  },[user]);

  /* ── Process invite ref link after data loads ── */
  useEffect(()=>{
    if(!user||!loaded) return;
    const refUid=localStorage.getItem("gather_ref");
    if(!refUid||refUid===user.uid){ localStorage.removeItem("gather_ref"); localStorage.removeItem("gather_ref_partner"); return; }
    const isPartner=localStorage.getItem("gather_ref_partner")==="1";
    localStorage.removeItem("gather_ref"); localStorage.removeItem("gather_ref_partner");

    (async()=>{
      const inviterSnap=await getDoc(doc(db,"users",refUid));
      if(!inviterSnap.exists()) return;
      const inv=inviterSnap.data();

      // 1. Add inviter to this user's contacts (Friends section)
      const alreadyContact=contacts.some(c=>c.gatherId===refUid||(c.email&&c.email.toLowerCase()===(inv.email||"").toLowerCase()));
      if(!alreadyContact&&inv.name){
        const newContact={
          name:inv.name,
          email:inv.email||"",
          type:"friend",
          stage:"close",
          metAt:"gather invite",
          notes:"Connected via gather invite",
          hobbies:inv.hobbies||[],
          lastSeen:"",
          birthday:"",
          followUpDays:14,
          followUpSet:todayISO(),
          phone:"",
          calType:"none",
          calUrl:"",
          manualSlots:"",
          log:[],
          snoozedUntil:null,
          touchCount:0,
          touchLog:[],
          lifeEvents:[],
          gatherId:refUid,
        };
        await addDoc(collection(db,"users",user.uid,"contacts"),newContact);
        showToast(`${inv.name.split(" ")[0]} joined gather — they're in your people now 👋`);
      }

      // 2. Create a connections doc so the inviter's app auto-receives this user as a contact.
      // Writing directly to users/{refUid}/contacts would require cross-user Firestore write
      // permissions and fails silently when rules are strict. The connections listener on
      // both sides handles the contact creation reliably without cross-user writes.
      const myName=myProfile.name||user.displayName||user.email||"";
      if(myName){
        const connId=[user.uid,refUid].sort().join("_");
        await setDoc(doc(db,"connections",connId),{
          uids:[user.uid,refUid],
          [user.uid]:{name:myName,email:user.email||"",uid:user.uid},
          [refUid]:{name:inv.name||"",email:inv.email||"",uid:refUid},
          isPartner:isPartner,
          createdAt:Date.now(),
        },{merge:true});
      }
    })();
  },[user,loaded]); // eslint-disable-line

  /* ── Keep phones index up to date so others can find this user by phone ── */
  useEffect(()=>{
    if(!user||!myProfile.phone) return;
    const norm=myProfile.phone.replace(/\D/g,"");
    if(norm.length<7) return;
    setDoc(doc(db,"phones",norm),{uid:user.uid},{merge:true}).catch(()=>{});
  },[user,myProfile.phone]); // eslint-disable-line

  /* ── Auto-link contacts who are already on gather (by email or phone) ── */
  useEffect(()=>{
    if(!user||!loaded||contacts.length===0) return;
    const unlinked=contacts.filter(c=>(c.email||c.phone)&&!c.gatherId);
    if(unlinked.length===0) return;
    (async()=>{
      for(const c of unlinked){
        try{
          let otherUid=null;
          if(c.email){
            const emailDoc=await getDoc(doc(db,"emails",c.email.toLowerCase()));
            if(emailDoc.exists()) otherUid=emailDoc.data().uid||null;
          }
          if(!otherUid&&c.phone){
            const norm=c.phone.replace(/\D/g,"");
            if(norm.length>=7){
              const phoneDoc=await getDoc(doc(db,"phones",norm));
              if(phoneDoc.exists()) otherUid=phoneDoc.data().uid||null;
            }
          }
          if(!otherUid||otherUid===user.uid) continue;
          // Update contact record with their gatherId
          await setDoc(doc(db,"users",user.uid,"contacts",c.id),{gatherId:otherUid},{merge:true});
          // Create a connection record so both sides know they're linked
          const connId=[user.uid,otherUid].sort().join("_");
          const otherSnap=await getDoc(doc(db,"users",otherUid));
          const other=otherSnap.exists()?otherSnap.data():{};
          await setDoc(doc(db,"connections",connId),{
            uids:[user.uid,otherUid],
            [user.uid]:{name:myProfile.name||user.email||"",email:user.email||"",uid:user.uid},
            [otherUid]:{name:other.name||c.name||"",email:c.email||"",uid:otherUid},
            isPartner:false,
            createdAt:Date.now(),
          },{merge:true});
        }catch(e){ console.warn("Auto-link failed for",c.name,e); }
      }
    })();
  },[user,loaded,contacts]); // eslint-disable-line

  /* ── Listen to connections — auto-add contacts + partner link ── */
  useEffect(()=>{
    if(!user||!loaded) return;
    const q=query(collection(db,"connections"),where("uids","array-contains",user.uid));
    const unsub=onSnapshot(q,async snap=>{
      for(const change of snap.docChanges()){
        if(change.type!=="added"&&change.type!=="modified") continue;
        const conn=change.doc.data();
        const otherUid=conn.uids?.find(u=>u!==user.uid);
        if(!otherUid) continue;
        const other=conn[otherUid];
        if(!other?.name) continue;

        // Auto-add as contact — query Firestore directly to avoid React state race conditions
        const coll=collection(db,"users",user.uid,"contacts");
        const byGatherIdSnap=await getDocs(query(coll,where("gatherId","==",otherUid)));
        if(!byGatherIdSnap.empty) continue; // already linked

        if(other.email){
          const byEmailSnap=await getDocs(query(coll,where("email","==",other.email.toLowerCase())));
          if(!byEmailSnap.empty){
            await setDoc(doc(db,"users",user.uid,"contacts",byEmailSnap.docs[0].id),{gatherId:otherUid},{merge:true}).catch(()=>{});
            continue;
          }
        }
        // Check by name as final fallback
        const byNameSnap=await getDocs(query(coll,where("name","==",other.name)));
        if(!byNameSnap.empty){
          await setDoc(doc(db,"users",user.uid,"contacts",byNameSnap.docs[0].id),{gatherId:otherUid},{merge:true}).catch(()=>{});
          continue;
        }
        if(other.name){
          await addDoc(coll,{
            name:other.name, email:other.email||"", phone:"",
            type:conn.isPartner?"family":"friend", stage:"close",
            metAt:"Joined via gather invite",
            notes:conn.isPartner?"Partner / Co-parent on gather":"Joined gather via your invite",
            gatherId:otherUid,
            followUpDays:conn.isPartner?1:14,
            followUpSet:todayISO(),
            hobbies:[], log:[], lastSeen:todayISO(), snoozedUntil:null,
            touchCount:0, touchLog:[], lifeEvents:[],
          }).catch(()=>{});
        }

        // If partner connection, store partnerUid + subscribe to their kids
        if(conn.isPartner){
          setMyProfile(p=>{
            if(p.partnerUid===otherUid) return p;
            setDoc(doc(db,"users",user.uid),{partnerUid:otherUid},{merge:true}).catch(()=>{});
            return {...p,partnerUid:otherUid};
          });
        }
      }
    });
    return unsub;
  },[user,loaded]); // eslint-disable-line

  /* ── Subscribe to partner's kids when partnerUid is known ── */
  useEffect(()=>{
    if(!myProfile.partnerUid) return;
    const unsub=onSnapshot(
      collection(db,"users",myProfile.partnerUid,"kids"),
      s=>setPartnerKids(s.docs.map(d=>({...d.data(),id:d.id,fromPartner:true})))
    );
    return unsub;
  },[myProfile.partnerUid]);


  /* ── Notifications ── */
  // Listen for service worker telling us to switch tabs (from notification tap)
  useEffect(()=>{
    if(!("serviceWorker" in navigator)) return;
    const handler=e=>{
      if(e.data?.type==="OPEN_TAB"&&e.data.tab) setTab(e.data.tab);
    };
    navigator.serviceWorker.addEventListener("message",handler);
    return ()=>navigator.serviceWorker.removeEventListener("message",handler);
  },[]);

  // Fire a notification when the app opens or returns to focus
  useEffect(()=>{
    if(!loaded||!user) return;
    async function checkAndNotify(){
      if(Notification?.permission!=="granted") return;
      // Throttle: only notify once per 4 hours
      const lastKey="gather_last_notif";
      const last=Number(localStorage.getItem(lastKey)||0);
      if(Date.now()-last < 4*60*60*1000) return;
      const overdue=urgentList(contacts).filter(c=>fupStatus(c).urgent);
      if(overdue.length===0) return;
      localStorage.setItem(lastKey,String(Date.now()));
      const firstName=overdue[0].name.split(" ")[0];
      const title=overdue.length===1
        ? `Good time to reach out to ${firstName}`
        : `${firstName} and ${overdue.length-1} other${overdue.length>2?"s":""} — good time to reconnect`;
      const body=overdue.length===1
        ? `A quick message goes a long way.`
        : overdue.slice(0,3).map(c=>c.name.split(" ")[0]).join(", ")+" — good time to check in.";
      try{
        const reg=await navigator.serviceWorker.ready;
        reg.showNotification(title,{
          body,
          icon:"/icon.svg",
          badge:"/icon.svg",
          tag:"gather-reach-out",   // replaces any existing gather notification
          renotify:false,
          data:{url:"/?tab=reach"},
        });
      }catch(err){ console.warn("Notification failed:",err); }
    }
    checkAndNotify();
    // Also check when the user returns to the tab
    function onVisible(){ if(document.visibilityState==="visible") checkAndNotify(); }
    document.addEventListener("visibilitychange",onVisible);
    return ()=>document.removeEventListener("visibilitychange",onVisible);
  },[loaded,user,contacts]); // eslint-disable-line

  /* ── CRUD ── */
  async function addContact(d){
    const{id:_,...r}=d;
    const norm=r.name?.trim().toLowerCase();
    if(norm){
      const existing=contacts.find(c=>c.name?.trim().toLowerCase()===norm);
      if(existing){ showToast(`${r.name} is already in your contacts`); return existing.id; }
    }
    const ref=await addDoc(collection(db,"users",user.uid,"contacts"),r);
    track("contact_added",{contact_type:r.type});
    return ref.id;
  }
  async function updateContact(id,d)   {const{id:_,...r}=d; await setDoc(doc(db,"users",user.uid,"contacts",id),r);}
  async function deleteContact(id)     {await deleteDoc(doc(db,"users",user.uid,"contacts",id));}
  async function addGroup(d){
    const{id:_,...r}=d;
    const memberUids=[user.uid];
    for(const cId of (r.contactIds||[])){const c=contacts.find(x=>x.id===cId);if(c?.gatherId&&c.gatherId!==user.uid)memberUids.push(c.gatherId);}
    await addDoc(collection(db,"groups"),{...r,createdBy:user.uid,memberUids,createdAt:new Date().toISOString()});
  }
  async function updateGroup(id,d){
    const{id:_,...r}=d;
    const memberUids=[user.uid];
    for(const cId of (r.contactIds||[])){const c=contacts.find(x=>x.id===cId);if(c?.gatherId&&c.gatherId!==user.uid)memberUids.push(c.gatherId);}
    await setDoc(doc(db,"groups",id),{...r,memberUids},{merge:true});
  }
  async function deleteGroup(id){await deleteDoc(doc(db,"groups",id));}
  async function addKid(d)             {const{id:_,...r}=d; await addDoc(collection(db,"users",user.uid,"kids"),r);}
  async function updateKid(id,d)       {const{id:_,...r}=d; await setDoc(doc(db,"users",user.uid,"kids",id),r);}
  async function deleteKid(id)         {await deleteDoc(doc(db,"users",user.uid,"kids",id));}
  async function addFamily(d)          {const{id:_,...r}=d; await addDoc(collection(db,"users",user.uid,"families"),r);}
  async function updateFamily(id,d)    {const{id:_,...r}=d; await setDoc(doc(db,"users",user.uid,"families",id),r);}
  async function deleteFamily(id)      {await deleteDoc(doc(db,"users",user.uid,"families",id));}
  async function saveProfile(profile)  {await setDoc(doc(db,"users",user.uid),profile,{merge:true});}
  async function createPlan(planData)  {
    const ref=await addDoc(collection(db,"plans"),{...planData,createdBy:user.uid,createdAt:Date.now(),status:"tentative",rsvps:{[user.uid]:"in"}});
    // Post a plan card message into the chat so everyone sees it
    if(planData.chatId){
      await addDoc(collection(db,"chats",planData.chatId,"messages"),{from:user.uid,createdAt:new Date().toISOString(),read:false,type:"plan_created",planId:ref.id,slotStr:planData.startISO});
      await setDoc(doc(db,"chats",planData.chatId),{lastMessage:`📅 Plan: ${planData.title||planData.activity||"Hangout"}`,lastMessageAt:new Date().toISOString(),lastMessageFrom:user.uid},{merge:true});
    }
    return ref.id;
  }
  async function rsvpPlan(planId,rsvp) {
    const planSnap=await getDoc(doc(db,"plans",planId));
    const plan=planSnap.data();
    const prevRsvps=plan?.rsvps||{};
    const prevInCount=Object.values(prevRsvps).filter(v=>v==="in").length;
    await setDoc(doc(db,"plans",planId),{rsvps:{[user.uid]:rsvp}},{merge:true});
    if(plan?.chatId){
      const firstName=myProfile.name?.split(" ")[0]||"Someone";
      const groupEmoji=plan.groupEmoji||"🎉";
      const msgText=rsvp==="in"?`${firstName} is in ${groupEmoji}`:`${firstName} can't make it 😔`;
      await addDoc(collection(db,"chats",plan.chatId,"messages"),{
        from:user.uid,createdAt:new Date().toISOString(),read:false,
        type:"rsvp_update",rsvp,planId,text:msgText,
      });
      await setDoc(doc(db,"chats",plan.chatId),{lastMessage:msgText,lastMessageAt:new Date().toISOString(),lastMessageFrom:user.uid},{merge:true});
      // Celebration when plan tips to 2+ confirmed
      if(rsvp==="in"&&prevInCount===1){
        const newInUids=Object.entries({...prevRsvps,[user.uid]:"in"}).filter(([,v])=>v==="in").map(([k])=>k);
        const nameSnaps=await Promise.all(newInUids.map(uid=>getDoc(doc(db,"users",uid))));
        const names=nameSnaps.map(s=>s.data()?.name?.split(" ")[0]||"Someone");
        const activity=plan.title||plan.activity||"Hangout";
        const celebration=`🎉 It's on! ${activity} — ${names.join(" & ")} are in!`;
        await addDoc(collection(db,"chats",plan.chatId,"messages"),{
          from:"system",createdAt:new Date().toISOString(),read:false,
          type:"plan_confirmed",planId,text:celebration,
        });
        await setDoc(doc(db,"chats",plan.chatId),{lastMessage:celebration,lastMessageAt:new Date().toISOString(),lastMessageFrom:"system"},{merge:true});
      }
      (plan.participants||[]).filter(uid=>uid!==user.uid).forEach(uid=>{
        getDoc(doc(db,"users",uid)).then(s=>{
          const token=s.data()?.fcmToken;
          if(token) fetch("/api/notify-chat",{method:"POST",headers:{"Content-Type":"application/json"},
            body:JSON.stringify({fcmToken:token,senderName:firstName,message:msgText})}).catch(()=>{});
        }).catch(()=>{});
      });
    }
  }
  async function cancelPlan(planId)    {await setDoc(doc(db,"plans",planId),{status:"cancelled"},{merge:true});}

  function handleUpdate(updated){updateContact(updated.id,updated);}

  /* ── Account deletion ── */
  async function handleDeleteAccount() {
    if(!user) return;
    const uid=user.uid;
    try {
      // Delete all subcollections
      const colls=["contacts","groups","kids","families"];
      for(const col of colls){
        const snap=await getDocs(collection(db,"users",uid,col));
        await Promise.all(snap.docs.map(d=>deleteDoc(d.ref)));
      }
      // Delete user document
      await deleteDoc(doc(db,"users",uid));
      // Remove email index
      if(user.email){
        await deleteDoc(doc(db,"emails",user.email.toLowerCase())).catch(()=>{});
      }
      // Clear local storage
      localStorage.removeItem("gather_streak");
      localStorage.removeItem("gather_streak_date");
      localStorage.removeItem("gather_last_notif");
      // Delete Firebase Auth user (signs out automatically)
      await deleteUser(auth.currentUser);
    } catch(e) {
      console.error("Delete account error:",e);
      // If the error is auth/requires-recent-login, Firebase needs a recent sign-in
      if(e.code==="auth/requires-recent-login"){
        alert("For security, please sign out and sign back in before deleting your account.");
      } else {
        alert("Something went wrong. Please try again or contact hello@heygather.app");
      }
    }
  }

  /* ── Calendar ── */
  const [calConnecting,setCalConnecting]=useState(false);
  const [calSyncing,setCalSyncing]=useState(false);

  async function storeCalendar(token){
    const blocks=await fetchCalendarEvents(token);
    // Merge with any existing iCal blocks so both sources stay active
    const icalBlocks=myProfile.icalBusyBlocks||[];
    const merged=[...blocks,...icalBlocks];
    const update={
      calendarConnected:true,
      googleCalendarConnected:true,
      calendarToken:token,
      calendarTokenExpiry:Date.now()+3600000,
      googleBusyBlocks:blocks,
      googleBusyBlocksUpdatedAt:Date.now(),
      busyBlocks:merged,
      busyBlocksUpdatedAt:Date.now(),
    };
    await setDoc(doc(db,"users",user.uid),update,{merge:true});
    setMyProfile(p=>({...p,...update}));
  }

  const [calError,setCalError]=useState("");

  async function handleConnectCalendar(){
    setCalConnecting(true); setCalError("");
    try{
      const token=await connectCalendar();
      if(token) await storeCalendar(token);
    }catch(e){
      console.error("Calendar connect error:",e);
      setCalError(e?.code||e?.message||"Unknown error — check console");
    }
    setCalConnecting(false);
  }

  async function handleSyncCalendar(){
    setCalSyncing(true); setCalError("");
    try{
      const token=await connectCalendar();
      if(token) await storeCalendar(token);
    }catch(e){
      console.error("Calendar sync error:",e);
      setCalError(e?.code||e?.message||"Unknown error — check console");
    }
    setCalSyncing(false);
  }

  async function handleConnectIcal(icalUrl){
    setCalSyncing(true); setCalError("");
    try{
      const r=await fetch("/api/ical-proxy",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({url:icalUrl}),
      });
      if(!r.ok){const t=await r.text();throw new Error(t);}
      const data=await r.json();
      const icalBlocks=data.events||[];
      // Merge with any existing Google Calendar blocks
      const googleBlocks=myProfile.googleBusyBlocks||[];
      const merged=[...googleBlocks,...icalBlocks];
      const update={
        calendarConnected:true,
        icalCalendarConnected:true,
        icalUrl,
        icalBusyBlocks:icalBlocks,
        icalBusyBlocksUpdatedAt:Date.now(),
        busyBlocks:merged,
        busyBlocksUpdatedAt:Date.now(),
      };
      await setDoc(doc(db,"users",user.uid),update,{merge:true});
      setMyProfile(p=>({...p,...update}));
    }catch(e){
      console.error("iCal connect error:",e);
      setCalError("Couldn't read that calendar link. Make sure it's public and starts with https:// or webcal://");
    }
    setCalSyncing(false);
  }

  async function handleSyncIcal(){
    if(!myProfile.icalUrl) return;
    await handleConnectIcal(myProfile.icalUrl);
  }

  function handleOnboardingComplete(profile){
    setMyProfile(p=>({...p,...profile}));
    setTab("overview"); // Land on Home — Weekend Spark + nudge give immediate value
  }

  const enabledTabs=(myProfile.enabledTabs||["overview","reach","contacts","playdates","profile"]).filter(t=>t!=="plan");
  const urgentCount=urgentList(contacts).length;
  const bdayCount=bdaySoon(contacts).filter(({bs})=>bs.days<=7).length;
  const alerts=urgentCount+bdayCount;

  const TABS=[
    {id:"overview", label:"Home"},
    {id:"reach",    label:"Gather"},
    {id:"contacts", label:"People"},
    {id:"playdates",label:"Playdates"},
    {id:"profile",  label:"Profile"},
  ].filter(t=>enabledTabs.includes(t.id));

  const PAGE={
    overview: {h:"Your social life",   sub:"A clear view of who you're staying close with and what needs attention."},
    reach:    {h:"Gather",             sub:"Your groups, plans, and conversations — everything for getting people together."},
    contacts: {h:"Your people",        sub:"Everyone you want to stay close with — plus anyone who needs a nudge."},
    playdates:{h:"Playdates",          sub:"Coordinate kids' plans and draft the parent message — without the back-and-forth."},
    profile:  {h:"Your profile",       sub:"Your name, schedule, interests, and privacy settings."},
  };

  if(authLoading) return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <span className="sp" style={{width:24,height:24}}/>
    </div>
  );

  if(!user) return <AuthScreen/>;

  if(!loaded) return (
    <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16}}>
      <span className="sp" style={{width:24,height:24}}/>
      <p style={{fontFamily:C.body,fontSize:13,color:C.ink3}}>Loading your Gather…</p>
    </div>
  );

  if(!myProfile.onboarded) return (
    <OnboardingFlow user={user} onComplete={handleOnboardingComplete} connectCalendar={connectCalendar}/>
  );

  return (
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:C.body}}>

      {/* ── Header ── */}
      <header className="fr" style={{background:"rgba(240,242,248,.94)",borderBottom:`1.5px solid ${C.line}`,position:"sticky",top:0,zIndex:100,paddingTop:"env(safe-area-inset-top)"}}>
        <div style={{maxWidth:720,margin:"0 auto",padding:"0 max(24px, env(safe-area-inset-right)) 0 max(24px, env(safe-area-inset-left))"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 0 16px"}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{width:30,height:30,borderRadius:8,background:C.blue,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 2px 10px rgba(37,99,235,.35)`}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:"rgba(255,255,255,.9)"}}/>
              </div>
              <span style={{fontFamily:"'DM Serif Display', serif",fontSize:22,fontWeight:400,fontStyle:"italic",color:C.ink,letterSpacing:".01em"}}>gather</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              {alerts>0&&(
                <div style={{display:"flex",alignItems:"center",gap:6,background:C.blueSoft,border:`1.5px solid ${C.blueBdr}`,borderRadius:6,padding:"4px 11px"}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:C.blue}}/>
                  <span style={{fontSize:11,fontWeight:600,color:C.blue,fontFamily:C.body,letterSpacing:".02em"}}>{alerts} to reach out</span>
                </div>
              )}
              {/* Sign-out menu */}
              <div style={{position:"relative"}}>
                <button onClick={e=>{e.stopPropagation();setMenuOpen(o=>!o);}} style={{background:"none",border:`1.5px solid ${C.line}`,borderRadius:8,color:C.ink,cursor:"pointer",padding:"6px 10px",display:"flex",flexDirection:"column",gap:4,alignItems:"center",justifyContent:"center",width:38,height:36}}>
                  <span style={{display:"block",width:16,height:1.5,background:C.ink,borderRadius:2}}/>
                  <span style={{display:"block",width:16,height:1.5,background:C.ink,borderRadius:2}}/>
                  <span style={{display:"block",width:16,height:1.5,background:C.ink,borderRadius:2}}/>
                </button>
                {menuOpen&&(
                  <div className="p" onClick={e=>e.stopPropagation()} style={{position:"absolute",right:0,top:"calc(100% + 8px)",background:"#fff",border:`1.5px solid ${C.line}`,borderRadius:12,boxShadow:"0 8px 32px rgba(37,99,235,.12)",zIndex:200,minWidth:160,overflow:"hidden"}}>
                    <button onClick={logOut} style={{width:"100%",background:"transparent",border:"none",padding:"13px 16px",cursor:"pointer",fontFamily:C.body,fontSize:13,color:C.red,textAlign:"left",fontWeight:500}}>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── Email verification banner ── */}
      {user&&!user.emailVerified&&user.providerData?.[0]?.providerId==="password"&&(
        <div style={{background:C.amberSoft,borderBottom:`1.5px solid ${C.amber}30`,padding:"10px max(24px, env(safe-area-inset-right))",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
          <p style={{fontSize:12,color:C.ink2,fontFamily:C.body,lineHeight:1.5}}>📧 Please verify your email address — check your inbox.</p>
          <button onClick={()=>{sendEmailVerification(auth.currentUser).then(()=>showToast("Verification email sent!")).catch(()=>showToast("Couldn't send — try again soon","error"));}}
            style={{background:"none",border:`1.5px solid ${C.amber}50`,borderRadius:6,padding:"4px 12px",cursor:"pointer",fontFamily:C.body,fontSize:12,fontWeight:600,color:C.amber,whiteSpace:"nowrap"}}>
            Resend email
          </button>
        </div>
      )}

      {/* ── Content ── */}
      <main style={{maxWidth:720,margin:"0 auto",padding:"36px max(24px, env(safe-area-inset-right)) calc(100px + env(safe-area-inset-bottom)) max(24px, env(safe-area-inset-left))"}} key={tab}>
        <div className="u" style={{marginBottom:28}}>
          <h1 style={{fontFamily:"'DM Serif Display', serif",fontSize:32,fontWeight:400,fontStyle:"italic",color:C.ink,letterSpacing:"-.01em",marginBottom:7,lineHeight:1.1}}>{PAGE[tab]?.h}</h1>
          <p style={{fontSize:13,color:C.ink3,lineHeight:1.7,fontFamily:C.body}}>{PAGE[tab]?.sub}</p>
        </div>

        {tab==="overview"  &&<Overview    contacts={contacts} groups={groups} myProfile={myProfile} myUid={user?.uid} onTabChange={setTab} onOpenChat={openChat} plans={plans} onRsvpPlan={rsvpPlan}/>}
        {tab==="reach"     &&<GatherTab groups={groups} contacts={contacts} plans={plans} myUid={user?.uid} myName={myProfile.name} onOpenChat={openChat} onAddGroup={addGroup} onUpdateGroup={updateGroup} onDeleteGroup={deleteGroup} onRsvpPlan={rsvpPlan} myProfile={myProfile}/>}
        {tab==="contacts"  &&<ContactsTab contacts={contacts} onAddContact={addContact} onUpdateContact={updateContact} onDeleteContact={deleteContact} groups={groups} onAddGroup={addGroup} onUpdateGroup={updateGroup} onDeleteGroup={deleteGroup} myUid={user?.uid} myName={myProfile.name} onReachOut={handleReachOut} onOpenChat={openChat}/>}
        {tab==="plan"      &&<><button onClick={()=>setTab("overview")} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",color:C.ink3,fontFamily:C.body,fontSize:13,padding:"0 0 16px",fontWeight:500}}>← Back to home</button><PlanTab contacts={contacts} groups={groups} myProfile={myProfile} busyBlocks={myProfile.busyBlocks||[]}/></>}
        {tab==="playdates" &&<PlaydatesTab myProfile={myProfile} myKids={myKids} partnerKids={partnerKids} onAddKid={addKid} onUpdateKid={updateKid} onDeleteKid={deleteKid} pdc={pdc} onAddFamily={addFamily} onUpdateFamily={updateFamily} onDeleteFamily={deleteFamily} myUid={user?.uid} busyBlocks={myProfile.busyBlocks||[]} contacts={contacts} onSaveProfile={saveProfile}/>}
        {tab==="profile"   &&<ProfileTab  myProfile={myProfile} setMyProfile={setMyProfile} onSave={saveProfile} groups={groups} onConnectCalendar={handleConnectCalendar} onSyncCalendar={handleSyncCalendar} onConnectIcal={handleConnectIcal} onSyncIcal={handleSyncIcal} calConnecting={calConnecting} calSyncing={calSyncing} calError={calError} user={user} onDeleteAccount={handleDeleteAccount}/>}
      </main>

      {/* ── Bottom Navigation ── */}
      <nav className="fr" style={{position:"fixed",bottom:0,left:0,right:0,zIndex:100,background:"rgba(240,242,248,.96)",backdropFilter:"blur(20px) saturate(1.5)",WebkitBackdropFilter:"blur(20px) saturate(1.5)",borderTop:`1.5px solid ${C.line}`,paddingBottom:"env(safe-area-inset-bottom)"}}>
        <div style={{maxWidth:720,margin:"0 auto",display:"flex",justifyContent:"space-around",padding:"6px 4px 2px"}}>
          {TABS.map(t=>{
            const isActive=tab===t.id;
            const hasBadge=(t.id==="reach"||t.id==="overview")&&alerts>0;
            return(
              <button key={t.id} onClick={()=>setTab(t.id)} style={{background:"none",border:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"5px 8px",borderRadius:10,color:isActive?C.blue:C.ink4,position:"relative",flex:1,minWidth:0}}>
                <div style={{position:"relative"}}>
                  <NavTabIcon id={t.id} active={isActive}/>
                  {hasBadge&&<span style={{position:"absolute",top:-3,right:-5,width:7,height:7,borderRadius:"50%",background:C.blue,border:"1.5px solid rgba(240,242,248,.96)"}}/>}
                </div>
                <span style={{fontSize:10,fontWeight:isActive?600:400,fontFamily:C.body,letterSpacing:".01em",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"100%"}}>
                  {{overview:"Home",reach:"Gather",contacts:"People",playdates:"Kids",profile:"Profile"}[t.id]||t.label}
                </span>
                {isActive&&<div style={{position:"absolute",bottom:-2,left:"50%",transform:"translateX(-50%)",width:18,height:2,borderRadius:2,background:C.blue}}/>}
              </button>
            );
          })}
        </div>
      </nav>

      <Toast/>
      <AddToHomeScreen/>
      <GatherAgent contacts={contacts} groups={groups} myProfile={myProfile} myUid={user?.uid} busyBlocks={myProfile.busyBlocks||[]} onTabChange={setTab}/>

      {/* ── Full-screen chat overlay ── */}
      {chatView&&(()=>{
        const otherUid=!chatView.chatId.startsWith("group_")
          ?chatView.participants.find(p=>p!==user?.uid)
          :null;
        const otherContact=otherUid?contacts.find(c=>c.gatherId===otherUid):null;
        const otherBusy=otherContact?.busyBlocks||[];
        return(
          <ChatView
            chatId={chatView.chatId}
            chatName={chatView.chatName}
            participants={chatView.participants}
            myUid={user?.uid}
            myName={myProfile.name}
            contacts={contacts}
            busyBlocks={myProfile.busyBlocks||[]}
            othersBusyBlocks={otherBusy}
            initialText={chatView.initialText||""}
            plans={plans}
            onCreatePlan={createPlan}
            onRsvpPlan={rsvpPlan}
            onCancelPlan={cancelPlan}
            onClose={()=>setChatView(null)}
            onFindTime={otherContact?()=>{setChatView(null);setReachOpenId(otherContact.id);setTab("reach");}:null}
            offPlatformContacts={chatView.offPlatformContacts||[]}
          />
        );
      })()}
    </div>
  );
}
